/**
 * AgedCareAnalyser.js – main aged care funding analyser: strategy mode (rent/sell/downsize/equity),
 * RAD/DAP inputs, allocators (downsize, investment property, reverse mortgage, HEAS, RAD funding),
 * suggestion engine, financial impact panel, SOA, projections modal. Used by AgedCarePage; receives
 * factFind and strategy state from App.js.
 */
import { scenarioLivingExpensesOr } from '../../../../shared/src/calculation/support/livingExpenses.ts';
import { fundingPhrase } from './fundingLabels.ts';
import { withDailyPaymentOverride } from './householdCareCosts.ts';
import { currentHomeSummary, refundFirstLine, movePiece } from './facilityMoveWords.ts';
import { existingDepositBalance, applyFacilityMoveToFactFind } from '../../../../shared/src/calculation/support/facilityMove.ts';
import { villageExitEntitlement, villageSellsOnEntry, villageInterimDap, villageFeesAfterLeaving } from '../../../../shared/src/calculation/support/villageExit.ts';
import { villageSummary, villageRefundLine, villageCoveredLine, villagePiece, villageFeesAfterLeavingLine, villageWaitSensitivityLine, type VillageFacts } from './villageExitWords.ts';
import { isExistingResidentBeforeNewArrangements } from '../../../../shared/src/calculation/residentialFeeArrangements.ts';
import { quietCalc } from './quietCalc.ts';
import { secondEntersLater as secondEntersLaterRule, secondEntersEarlier as secondEntersEarlierRule, phaseFromLabel, railPhaseTwoLine, roleNames, livingPhaseTwoHelp, todayIso as todayIsoDate, isCompleteIsoDate } from './carePhases.ts';
import { allocateSecondDepositFunding } from '../../../../shared/src/calculation/support/secondDeposit.ts';
import RoomInputs from './RoomInputs.tsx';
import { describeFundingKeys } from './fundingLabels.ts';
import { withHouseholdTotals, householdDailyCost } from './householdCareCosts.ts';
import { anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';
import { runwayFactsOf } from './shortfallOutlook.ts';
import React, { useState, useMemo, useEffect, startTransition } from 'react';
import { useClampedDateField } from './careDateField.ts';
import type { Dispatch, SetStateAction } from 'react';
import { ArrowRight, BookOpen, ChevronLeft, ChevronDown, Info, AlertTriangle, Search, ShieldCheck } from 'lucide-react';
import { formatCurrency, parseCurrency, calculateAge } from '../../utils/index.ts';
import { agedCareEntrantResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import { householdCoveredByNoWorseOffPrinciple } from '../../../../shared/src/calculation/residentialFeeArrangements.ts';
import { calculateDeprivedAssets } from '../../../../shared/src/calculation/giftingDeprivation.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { GIFTING_LIMIT_ANNUAL, GIFTING_LIMIT_5_YEAR } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { markAbpFundedAcquisitionAfterLiquidation } from '../../../../shared/src/calculation/abpProjectionUtils.ts';
import { applyDownsizerContributionsToAssets } from '../../../../shared/src/calculation/downsizerContributionUtils.ts';
import { buildNetWorthSchedule, type NetWorthSchedulePoint } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import {
    formatServiceBuilderSelectionId,
    notifyHomeCareStrategiesUpdated,
    setActiveHomeCareStrategyId,
} from '../../strategy/careCompanionStrategyEvents.ts';
import { AGED_CARE_SCHEDULE, ASSET_TYPES, DEFAULT_INFLATION_ASSUMPTION_PCT, DEFAULT_PROJECTION_YEARS } from '../../constants/index.ts';
import { unallocatedSaleProceeds } from '../../../../shared/src/calculation/saleProceedsAllocation.ts';
import { Card, GradientText, CurrencyInput, FormSelect, FormCheckbox, SummaryRow, InfoIcon } from '../../components/ui/index.tsx';
import AgedCareSteps, { type AnalyserStep } from './AgedCareSteps.tsx';
import WalkInHero from './WalkInHero.tsx';
import CostOverviewDisclosure from './CostOverviewDisclosure.tsx';
import { annuityHorizonFlag, homeSaleHorizonFlag } from './strategyHorizonFlags.ts';
import { HorizonNote, moneySequenceSentence, type RunwayRow } from './moneySequence.tsx';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';

/**
 * ONE HELP DEVICE (owner, 2026-09-22: "I've never really liked the question mark — it makes the
 * screen look messy"). A field's guidance used to sit behind a grey question-mark icon; it is now a
 * dotted "what's this?" that opens the same words in place, the gesture every dotted term on the
 * page already teaches. Numbers with a breakdown open it on hover of the figure or its label; words
 * open on the dotted term. No icons.
 */
const WhatsThis = ({ text }: { text: string }) => <InfoIcon text={text} />;
import AgedCareSuggestionEnginePanel from './AgedCareSuggestionEnginePanel.tsx';
import ProjectionsModal from './ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from './projectionsModalPreferences.ts';
import DownsizeStrategyAllocator from './DownsizeStrategyAllocator.tsx';
import type { DownsizeDetails } from './DownsizeStrategyAllocator.tsx';
import InvestmentPropertyStrategyAllocator from './InvestmentPropertyStrategyAllocator.tsx';
import StrategyPlanRecap, { buildPlanRecapClauses, RecapClauses } from './StrategyPlanRecap.tsx';
import { FUNERAL_BOND_LIMIT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import {
    GiftingFieldNote,
    FuneralBondFieldNote,
    ReverseMortgageFieldNote,
    HeasFieldNote,
} from '../../components/strategy/StrategyFieldNotes.tsx';
import PlanExplanationModal from './PlanExplanationModal.tsx';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { FundingRunway } from '../../components/narrative/index.tsx';

/**
 * How far ahead "how long the money lasts" is tested — deliberately NOT the Project Legacy Over
 * period. See the runway projection effect below for why that distinction matters.
 */
const RUNWAY_HORIZON_YEARS = 25;
import { ModeChooser } from '../../components/ui/index.tsx';
import type { InvestmentPropertySaleDetails } from '../../utils/entityInvestmentPropertySale.ts';
import ReverseMortgageStrategyAllocator from './ReverseMortgageStrategyAllocator.tsx';
import type { ReverseMortgageDetails } from './ReverseMortgageStrategyAllocator.tsx';
import HEASStrategyAllocator from './HEASStrategyAllocator.tsx';
import type { HEASDetails } from './HEASStrategyAllocator.tsx';
import FinancialImpactPanel from './FinancialImpactPanel.tsx';
import PlanSummaryPanel from './PlanSummaryPanel.tsx';
import type { FinancialPosition, ProjectionYear } from './FinancialImpactPanel.tsx';
import type { AgedCareCosts } from '../../../../shared/src/calculation/agedCareCostTypes.ts';
import RadFundingStrategy from './RadFundingStrategy.tsx';
import WalkInPositionNote from './WalkInPositionNote.tsx';
import { buildAgedCareBaseline, buildDoNothingStrategyOptions, EMPTY_AGED_CARE_BASELINE, type AgedCareBaseline } from './agedCareBaseline.ts';
import { hasAssetRateOverrides, withDefaultAssetRates } from '../../../../shared/src/calculation/assetRateOverrides.ts';
import DebtPayoffStrategy from './DebtPayoffStrategy.tsx';
import { simulateManualAgedCarePlan } from './agedCarePlanFactFind.ts';
import { applyDebtPayoffToFactFind } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import { CoupleValidationModal, FacilitySearchModal } from '../../components/modals/index.tsx';
import type { Facility } from '../../components/modals/FacilitySearchModal.tsx';
import { annuityFundingAvailable, applyAnnuityWithDraw, buildAnnuityImpact, summariseAnnuityImpact } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { getInvestmentPropertyTaxSummary } from '../../../../shared/src/calculation/entityDisposalTax.ts';
import {
    computeRadFundingTotals,
    findAssetInFactFind,
    parseRadFundingAssetKey,
    radFundingInvolvesAssetSale,
    resolveRadFundingEntry,
} from '../../utils/entityFundingUtils.ts';
import {
    applyInvestmentPropertySaleToFactFind,
} from '../../utils/entityInvestmentPropertySale.ts';
import SupportAtHomeAnnuityConfigurator, { DEFAULT_NEW_ANNUITY_RESIDUAL } from '../supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import type { AnnuityDetails, AnnuityFundingSource as ExternalAnnuityFundingSource } from '../supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import { buildAgedCareDigitalSOASnapshot, openAgedCareDigitalSOAAttachment } from './AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind, FactFindAsset, FactFindGiftsHistory } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { RadFundingBag } from './getRadFundingTooltipData.tsx';
import type { EvaluatedStrategy } from './futureLifeEventUtils.tsx';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
import { stockPhoto } from '../../assets/stock/index.ts';
import { notify } from '../../components/ui/notices.tsx';

/** The second room's inputs, where both of a couple enter care — see `secondRoom` in the analyser. */
export interface SecondRoomInputs {
    roomPrice: number;
    /** YYYY-MM-DD; '' = at the same time as the first entrant. */
    careStartDate: string;
    /** The second room's optional extra services, a year. */
    extraServiceAnnual: number;
}
const EMPTY_SECOND_ROOM: SecondRoomInputs = { roomPrice: 0, careStartDate: '', extraServiceAnnual: 0 };
// DraftFactFind is structurally compatible with FactFind (see types/formDraft.ts);
// the shared calc-engine fns defensively parseFloat/coerce internally regardless (see
// factFindTypes.ts header), so this cast is safe. Used at the handful of call sites below that
// invoke shared calc-engine helpers directly (bypassing the calculationApi.ts REST boundary that
// normally does this coercion).
const asFactFind = (data: DraftFactFind): FactFind => data as unknown as FactFind;

const AGED_CARE_HOME_EXEMPTION_CAP = AGED_CARE_SCHEDULE.homeExemptionCap;

// ── Local state / prop shapes (this file constructs and owns all of these) ─────────────────────
export interface OverallStrategyState {
    rentHome: boolean;
    downsizeHome: boolean;
    accessHomeEquity: boolean;
    reverseMortgage: boolean;
    heas: boolean;
    sellInvestmentProperty?: boolean;
    [key: string]: unknown;
}

export interface StrategyInputsState {
    rentAmount: number | undefined;
    rentExpenses: number | undefined;
    partnerRent: number | undefined;
    [key: string]: unknown;
}

export interface OtherStrategiesState {
    gifting: boolean;
    giftingAmount: number | undefined;
    giftingSource: string;
    funeralBonds: boolean;
    funeralBondAmount: number | undefined;
    funeralBondSource: string;
    annuity: boolean;
    [key: string]: unknown;
}

export interface AnnuityFundingSource {
    id?: string | null;
    type?: string;
    assetId?: string | number | null;
    label?: string;
    ownership?: string;
    available?: number;
    [key: string]: unknown;
}

export interface AnnuityRecordState {
    id?: string;
    annuityType?: string;
    purchaseAmount?: number | string;
    residualCapital?: number;
    owner?: string;
    fundingSource?: AnnuityFundingSource | string;
    fundingSourceType?: string;
    fundingSourceId?: string | number | null;
    indexation?: boolean;
    reversionary?: boolean;
    age?: number | null;
    partnerAge?: number | null;
    [key: string]: unknown;
}

/** Manual-control RAD funding: `type-assetId` -> gross liquidation amount. */
export type RadFundingManualBag = Record<string, number>;
/** Per RAD-funding key -> accountant CGT override ($ total, or other override shape). */
export type CgtOverridesBag = Record<string, unknown>;

/** See {@link AgedCareStrategyInputsSnapshot.radDrawdown}. */
export type RadDrawdownMode = 'fees' | 'dap' | 'none';
/** See {@link AgedCareStrategyInputsSnapshot.residentialFeeArrangements}. */
export type ResidentialFeeArrangementsChoice = '2014' | '2025';

const RAD_DRAWDOWN_OPTIONS: { value: RadDrawdownMode; label: string }[] = [
    { value: 'fees', label: 'Yes — all fees (most homes agree to this)' },
    { value: 'dap', label: 'Only the accommodation payment (the legal minimum)' },
    { value: 'none', label: 'No' },
];

export interface AgedCareStrategyInputsSnapshot {
    radInput: number;
    extraServiceFeeInput: number;
    radFunding: RadFundingManualBag;
    /**
     * The Debt Strategy card's two allocations — how much of each debt is cleared (keyed by
     * `debtPayoffKey`), and which assets pay for it (the same `type-id` keys as {@link radFunding},
     * which is what lets the two cards net off against each other).
     *
     * Optional because strategies saved before the Debt Strategy card existed carry neither, and a
     * strategy with no debt plan carries neither either. Absent reads as "no debt was paid out".
     */
    debtPayments?: RadFundingManualBag;
    debtFunding?: RadFundingManualBag;
    /** What a PART payment does to the repayment; absent reads as the default, keeping it. */
    debtRepaymentBasis?: 'keep_repayment' | 'lower_repayment';
    cgtOverrides: CgtOverridesBag;
    overallStrategy: OverallStrategyState;
    strategyInputs: StrategyInputsState;
    downsizeDetails: DownsizeDetails;
    /** Saved from 3 Oct 2026, so the plan's position can be rebuilt off this page. */
    investmentPropertyDetails?: InvestmentPropertySaleDetails | null;
    reverseMortgageDetails: ReverseMortgageDetails;
    heasDetails: HEASDetails;
    scenarioLivingExpenses: number;
    otherStrategies: OtherStrategiesState;
    annuityRecord: AnnuityRecordState | null;
    /**
     * Nominated care start date, `YYYY-MM-DD`. Strategies saved before entry became day-precise carry a
     * `careEntryMonth` (`YYYY-MM`) here instead — see {@link careStartDateFromSavedInputs}, which reads
     * either and is why that older key needs no declaration of its own.
     */
    careStartDate?: string;
    /**
     * Nominated RAD sale date, `YYYY-MM-DD`. Only ever set alongside {@link careStartDate}, and never
     * later than it — see `clampRadFundingDate`.
     */
    radFundingDate?: string;
    /**
     * Whether the home may take its fees out of the deposit: all fees (the usual written agreement),
     * only the daily accommodation payment (the legal minimum), or nothing. Absent on strategies saved
     * before 2026-09-12, which are read as 'fees' — the arrangement the engine assumes.
     */
    radDrawdown?: RadDrawdownMode;
    /** The second room, where both of a couple enter care (7 Oct 2026); null or absent otherwise. `radPaid` is derived (the same share as the first room). */
    secondRoom?: (SecondRoomInputs & { radPaid?: number; person?: 'client' | 'partner' }) | null;
    /** Who moves in first (the engine's first entrant); 'client' unless the dates say otherwise. */
    leadPerson?: 'client' | 'partner';
    /** Living costs a year once both are in care, where the second person enters later; null = unchanged. */
    phaseTwoLivingExpenses?: number | null;
    /** A change of home (8 Oct 2026): recorded so a reopened plan, the forms and the advice document know what the move does. */
    facilityMove?: { person: 'client' | 'partner'; moveDate: string; facilityName: string; currentRoomPrice: number; paid: number; refundToday: number; retained: number; retentionDaysTaken: number; grandfathered: boolean; newRoomPrice: number; radTarget: number } | null;
    /**
     * Which residential fee arrangements the strategy was priced on. Only recorded for a resident the
     * profile puts under the no-worse-off principle: `'2014'` (the means-tested care fee — their
     * default) or `'2025'` (modelled as opting in to the 1 November 2025 rules). Absent for everyone
     * else, and on strategies saved before 2026-09-17.
     */
    residentialFeeArrangements?: ResidentialFeeArrangementsChoice;
    /**
     * The projection horizon this strategy's outputs were struck over. Absent on strategies saved
     * before 2026-08-07, which is why the comparison says "not recorded" rather than assuming today's
     * default — the default itself moved from 10 years to 5 on 2026-08-06, so guessing would be wrong
     * for exactly the strategies most likely to be sitting in a client's list.
     */
    legacyYears?: number;
    [key: string]: unknown;
}

export interface AgedCareStrategyOutputsSnapshot {
    agedCareCost: number;
    agePension: number;
    cashFlow: number;
    legacyEstimate: number;
    roomCost: number;
    /**
     * Net worth at every year end of the projection — `legacyEstimate` above is its LAST entry.
     *
     * Absent on strategies saved before it was recorded, which is why every reader treats an empty
     * schedule as "not recorded" rather than back-filling from the estate figure. See
     * shared/src/calculation/netWorthSchedule.ts.
     */
    netWorthSchedule?: NetWorthSchedulePoint[];
    [key: string]: unknown;
}

/** A saved Aged Care strategy row (Manual Control save, or a Suggestion Engine pick promoted into savedStrategies). */
export interface SavedAgedCareStrategy {
    id: string;
    name: string;
    type?: 'aged_care' | string;
    source?: 'suggestion_engine' | string;
    inputs?: Partial<AgedCareStrategyInputsSnapshot>;
    outputs?: AgedCareStrategyOutputsSnapshot;
    /** Only present for source === 'suggestion_engine' (the SE result picked by the adviser). */
    strategy?: EvaluatedStrategy;
    createdAt?: string;
    [key: string]: unknown;
}

export interface SavedCaseStudy {
    id?: string;
    name?: string;
    createdAt?: string;
    soaSource?: string;
    caseId?: string | number | null;
    soaDeliveryStatus?: string;
    isCurrentForCase?: boolean;
    outputs?: Record<string, unknown>;
    soaSnapshot?: unknown;
    [key: string]: unknown;
}

export interface CoupleValidationState {
    exploreAlternatives?: boolean;
    suggestionEngineSetupComplete?: boolean;
    [key: string]: unknown;
}

export interface AgedCareAnalyserProps {
    setActivePage: (page: string) => void;
    factFind: DraftFactFind;
    setFactFind: Dispatch<SetStateAction<DraftFactFind>>;
    onBack: () => void;
    legacyYears: number;
    setLegacyYears: Dispatch<SetStateAction<number>>;
    selectedStrategy: EvaluatedStrategy | null;
    setSelectedStrategy: Dispatch<SetStateAction<EvaluatedStrategy | null>>;
    coupleValidation: CoupleValidationState;
    setCoupleValidation: Dispatch<SetStateAction<CoupleValidationState>>;
    savedStrategies: SavedAgedCareStrategy[];
    setSavedStrategies: Dispatch<SetStateAction<SavedAgedCareStrategy[]>>;
    savedCaseStudies: SavedCaseStudy[];
    setSavedCaseStudies: Dispatch<SetStateAction<SavedCaseStudy[]>>;
    strategyOptions: Record<string, unknown>;
    setStrategyOptions: Dispatch<SetStateAction<Record<string, unknown>>>;
    isProfessionalJourney?: boolean;
    professionalCaseId?: string | number | null;
    onSoaGenerated?: ((scenario: SavedCaseStudy) => void) | null;
    /** Fired when the adviser saves a modelled strategy, so a REAL client file can persist it to the
     *  server's saved_models table. Without this, modelling lived only in sessionStorage: it was lost
     *  at sign-out and invisible to the Partner Portal, which is why a fully-modelled client still
     *  showed as "In Progress". */
    onStrategySaved?: ((strategy: SavedAgedCareStrategy) => void) | null;
}

const convertManualRadFundingForSnapshot = (radFunding: RadFundingManualBag = {}): RadFundingBag => {
    const converted: Record<string, { id: string; amount: number }[]> = {};
    Object.entries(radFunding || {}).forEach(([key, rawAmount]) => {
        const amount = parseFloat(String(rawAmount)) || 0;
        if (amount <= 0) return;
        const parsed = parseRadFundingAssetKey(key);
        if (!parsed) return;
        if (!converted[parsed.assetType]) converted[parsed.assetType] = [];
        converted[parsed.assetType]!.push({ id: parsed.assetId, amount });
    });
    return converted;
};

/** Saved outputs.cashFlow; if legacy save used wrong field (0), recover from nested SE snapshot. */
const getDisplayCashFlowForSavedStrategy = (s: SavedAgedCareStrategy | null | undefined): number => {
    const o = s?.outputs?.cashFlow;
    if (o != null && o !== 0) return Number(o);
    const strategySnapshot = s?.strategy?.snapshot as { netCashflow?: number; cashFlow?: number } | undefined;
    const fromSnap = strategySnapshot?.netCashflow ?? strategySnapshot?.cashFlow;
    if (fromSnap != null && fromSnap !== 0) return Number(fromSnap);
    return Number(o ?? fromSnap ?? 0);
};

/** `YYYY-MM` for a Date (local time — matches what an `<input type="month">` produces). */
export { formatExtraServiceFee } from './extraServiceFee.ts';

const toMonthValue = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** `YYYY-MM-DD` for a Date, in local time — the shape an `<input type="date">` reads and writes. */
const toDateValue = (d: Date): string => `${toMonthValue(d)}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Normalise a nominated care start date: a day-precise `YYYY-MM-DD` in the future, or '' for "care is
 * starting now".
 *
 * There is no upper bound any more, and that is the point of the change this belongs to. Entry used to
 * be capped at 12 months out because the entry date WAS the projection anchor — the run began at
 * admission, so the years in between would have gone silently unmodelled. Now the projection starts
 * today and entry is an event inside it, so those years are projected like any other and a date five
 * years out is as modellable as one five months out.
 *
 * Today or earlier collapses to '' rather than deferring by a negative amount: a client entering now is
 * exactly what a blank field means to the engine.
 */
const clampCareStartDate = (raw: unknown): string => {
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
    const now = new Date();
    return value <= toDateValue(now) ? '' : value;
};

/**
 * Read a care start date off a saved strategy's inputs, accepting the `careEntryMonth` (`YYYY-MM`) that
 * strategies saved before entry became day-precise carry instead.
 *
 * Those older strategies mean the 1st of the month they name, which is what the month value was
 * converted to when it was fed to the projection anchor.
 */
/**
 * Normalise a nominated RAD sale date: a future `YYYY-MM-DD` on or before the move-in date, or '' for
 * "sold now, as part of entering care".
 *
 * Bounded above by the care date because a deposit cannot be funded by a sale that has not happened —
 * the engine would bank the proceeds after it had already tried to pay out of them. Selling ON the day
 * is allowed, and is the ordinary case; selling earlier is the one worth modelling.
 */
const clampRadFundingDate = (raw: unknown, careStart: string): string => {
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
    if (value <= toDateValue(new Date())) return '';
    if (careStart && value > careStart) return careStart;
    return value;
};

const careStartDateFromSavedInputs = (inputs: Partial<AgedCareStrategyInputsSnapshot>): string => {
    if (typeof inputs.careStartDate === 'string' && inputs.careStartDate) return clampCareStartDate(inputs.careStartDate);
    const legacyMonth = typeof inputs.careEntryMonth === 'string' ? inputs.careEntryMonth.trim() : '';
    return /^\d{4}-\d{2}$/.test(legacyMonth) ? clampCareStartDate(`${legacyMonth}-01`) : '';
};


/**
 * What each saved strategy was modelled ON, and whether the set being compared agrees.
 *
 * The comparison table showed outputs alone — daily cost, pension, cashflow, legacy — with nothing to
 * say that one row assumed a $500,000 room and the next $700,000, or that one entered care today and
 * another in eighteen months, or that one legacy was struck over ten years and its neighbour over five.
 * Ranked side by side they read as alternatives to the same question. They need not be.
 *
 * The horizon is the sharpest case: it is not a modelling choice a reader can weigh from the numbers,
 * it simply scales the legacy, and the product's own default moved from 10 years to 5 on 2026-08-06 —
 * so a client's list can straddle the change without anyone having touched a setting.
 */
export function describeStrategyBasis(strategy: SavedAgedCareStrategy) {
    const inputs = strategy.inputs ?? ({} as Partial<AgedCareStrategyInputsSnapshot>);
    return {
        roomPrice: Number(inputs.radInput) || Number(strategy.outputs?.roomCost) || 0,
        careStartDate: typeof inputs.careStartDate === 'string' ? inputs.careStartDate : '',
        legacyYears: typeof inputs.legacyYears === 'number' ? inputs.legacyYears : null,
        radDrawdown: inputs.radDrawdown || 'fees',
        residentialFeeArrangements: inputs.residentialFeeArrangements ?? null,
    };
}

/** Which parts of the basis DIFFER across the strategies being compared — empty when they are like for like. */
export function basisDifferences(strategies: SavedAgedCareStrategy[]): string[] {
    if (strategies.length < 2) return [];
    const bases = strategies.map(describeStrategyBasis);
    const differs = <T,>(pick: (b: ReturnType<typeof describeStrategyBasis>) => T) =>
        new Set(bases.map(pick)).size > 1;
    const differences: string[] = [];
    if (differs((b) => b.roomPrice)) differences.push('room price');
    if (differs((b) => b.careStartDate)) differences.push('care start date');
    if (differs((b) => b.radDrawdown)) differences.push('whether fees are drawn from the deposit');
    if (differs((b) => b.residentialFeeArrangements)) differences.push('which fee arrangements apply (no worse off vs opted in)');
    // A missing horizon counts as different from a recorded one: "not recorded" is not "the same".
    if (differs((b) => b.legacyYears)) differences.push('projection length');
    return differences;
}

const AgedCareAnalyser = ({ setActivePage, factFind, setFactFind, onBack, legacyYears, selectedStrategy, setSelectedStrategy, coupleValidation, setCoupleValidation, savedStrategies, setSavedStrategies, savedCaseStudies, setSavedCaseStudies, strategyOptions, setStrategyOptions, isProfessionalJourney = false, professionalCaseId = null, onSoaGenerated = null, onStrategySaved = null }: AgedCareAnalyserProps) => {
    // State for user inputs specific to this page
    const [radInput, setRadInput] = useState<number>(0);
    /** Show the funding sliders even though a strategy already covers the whole room. */
    const [showFundingAnyway, setShowFundingAnyway] = useState(false);
    /**
     * Nominated residential care start date (`YYYY-MM-DD`), '' = care is starting now.
     *
     * Passed to the engine as `strategyOptions.careStartDate`, and deliberately NOT as the projection
     * anchor. It used to be both: setting the anchor to the entry month made the projection BEGIN at
     * admission, so nothing between today and entry was modelled and legacy estimates for different
     * entry dates covered different horizons. The projection now always starts from today, and this
     * says when care fees begin inside it.
     */
    const [careStartDate, setCareStartDate] = useState<string>('');
    /**
     * Whether the home may take its fees out of the deposit. A fact about the provider agreement, not
     * a strategy: it is asked once, here, and both the manual plan and every ranked candidate run on
     * it. Defaults to the usual arrangement — a home must allow the accommodation payment to be drawn
     * and most agree to the rest in writing — so a plan modelled before the question is asked is the
     * plan most clients will actually get.
     */
    const [radDrawdown, setRadDrawdown] = useState<RadDrawdownMode>('fees');
    /*
     * A resident the profile puts under the no-worse-off principle is on the 1 July 2014 fee
     * arrangements (the means-tested care fee) unless they opt in to the 1 November 2025 rules — an
     * election that cannot be undone, so the screen models it only when asked. Resolved from the
     * profile by the same function the fee engine uses (residentialFeeArrangements.ts).
     */
    const [optInToNovember2025Fees, setOptInToNovember2025Fees] = useState(false);
    const coveredByNoWorseOff = useMemo(() => householdCoveredByNoWorseOffPrinciple(factFind as unknown as FactFind), [factFind]);
    const residentialFeeArrangements: ResidentialFeeArrangementsChoice | undefined = coveredByNoWorseOff ? (optInToNovember2025Fees ? '2025' : '2014') : undefined;
    /**
     * When the RAD funding assets are SOLD (`YYYY-MM-DD`), '' = sold now.
     *
     * Passed to the engine as `strategyOptions.radFundingDate`, which is also what tells it to do the
     * selling. Setting it therefore changes what `simulatedFactFind` below builds: a sale that has not
     * happened yet must not appear in the client's CURRENT position, only in the projection, on its day.
     *
     * Separate from {@link careStartDate} because selling and moving in are separate acts. The gap is
     * the point — proceeds sit in the bank, deemed and means-tested, until the deposit is paid.
     */
    const [radFundingDate, setRadFundingDate] = useState<string>('');
    /**
     * THE SECOND ROOM, where both of a couple are entering care (owner, 7 Oct 2026: "both into care,
     * built the right way"). The first entrant is the client (the run's existing machinery); the
     * partner is the second resident, with their own room, entry date and deposit. Their deposit is
     * funded from what the first room's funding leaves (shared/.../secondDeposit.ts), so one dollar
     * cannot pay for two rooms, and the engine prices the pair as a couple living apart.
     */
    const bothEntering = factFind.adviceFor === 'couple' && (factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'both';
    /**
     * WHOEVER MOVES IN FIRST LEADS (owner, 7 Oct 2026: "isn't the system just smart enough to pick it
     * up based on date?"). The engine's first entrant is the person with the earlier date; the other
     * is the second resident. The two rooms' inputs swap roles when the dates say so (below), and
     * every name on the page follows the role, not the profile's client/partner slot.
     */
    const [leadPerson, setLeadPerson] = useState<'client' | 'partner'>('client');
    const secondPerson: 'client' | 'partner' = leadPerson === 'client' ? 'partner' : 'client';
    const secondKey = secondPerson === 'client' ? 'clientResidencyOverride' : 'partnerResidencyOverride';
    const secondRecorded = (secondPerson === 'client' ? factFind.client?.currentResidency : factFind.partner?.currentResidency) || 'own_home';
    /**
     * A CHANGE OF HOME (owner, 7 Oct 2026). The person is in care now (the profile's stay): the page
     * prices a move to a new home against STAYING PUT. Their deposit's refundable balance today is
     * what the new room's deposit is paid from first; its retention standing, and whether any applies
     * at all, follow from when they moved in.
     */
    const moverPerson: 'client' | 'partner' = factFind.adviceFor === 'couple' && (factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'partner' ? 'partner' : 'client';
    const moverRecord = moverPerson === 'partner' ? factFind.partner : factFind.client;
    const moverStay = moverRecord?.currentResidency === 'aged_care' && moverRecord?.careStay && (Number(moverRecord.careStay.roomPrice) || 0) > 0 && !bothEntering ? moverRecord.careStay : null;
    const isMover = !!moverStay;
    const moverGrandfathered = isMover && isExistingResidentBeforeNewArrangements(asFactFind(factFind), moverPerson);
    const moverDeposit = useMemo(
        () => (moverStay ? existingDepositBalance(Number(moverStay.radPaid) || 0, moverStay.entryDate, new Date(), moverGrandfathered) : { balance: 0, retained: 0, retentionDaysTaken: 0 }),
        [moverStay, moverGrandfathered],
    );
    const existingStay = useMemo(
        () => (moverStay ? { person: moverPerson, facilityName: String(moverStay.facilityName || ''), roomPrice: Number(moverStay.roomPrice) || 0, refundToday: moverDeposit.balance, dailyPayment: Number(moverStay.dailyPayment) || 0 } : null),
        [moverStay, moverPerson, moverDeposit.balance],
    );
    const currentHomeFacts = moverStay ? {
        name: moverRecord?.firstName || 'the client',
        facilityName: String(moverStay.facilityName || ''),
        roomPrice: Number(moverStay.roomPrice) || 0,
        paid: Number(moverStay.radPaid) || 0,
        refundToday: moverDeposit.balance,
        retained: moverDeposit.retained,
        dailyPayment: Number(moverStay.dailyPayment) || 0,
        entryLabel: moverStay.entryDate ? phaseFromLabel(moverStay.entryDate) : '',
        grandfathered: moverGrandfathered,
    } : null;
    const [secondRoom, setSecondRoom] = useState<SecondRoomInputs>(EMPTY_SECOND_ROOM);
    const secondRoomActive = bothEntering && secondRoom.roomPrice > 0;
    const { firstName, secondName } = roleNames(factFind, secondPerson, { first: 'you', second: 'your partner' });
    /** Which room the facility search fills. */
    const [facilityTarget, setFacilityTarget] = useState<'first' | 'second'>('first');
    const [selectedSecondFacility, setSelectedSecondFacility] = useState<Facility | null>(null);
    /** The second person enters after the first: the first's schedule is struck with them still at home. */
    const secondEntersLater = secondRoomActive && secondEntersLaterRule(careStartDate, secondRoom.careStartDate);
    const phaseNames = { firstName, secondName, fromLabel: phaseFromLabel(secondRoom.careStartDate) };
    useEffect(() => {
        if (!bothEntering) return;
        // The second slot's date is EARLIER than the first's (a blank first date is "now"): that
        // person moves in first, so the engine's first entrant must be them. The two rooms' inputs
        // change SLOTS; nothing on screen moves — each block is bound to its person, whichever slot
        // holds their room (owner, 7 Oct 2026: "who asked for that?"). Only a date the browser has
        // finished typing counts: a native date input reports "0002-01-01" half-way through "2029".
        if (!secondEntersEarlierRule(careStartDate, secondRoom.careStartDate)) return;
        const wasFirst = { roomPrice: radInput, careStartDate, extraServiceAnnual: extraServiceFeeInput, facility: selectedFacility };
        setRadInput(secondRoom.roomPrice);
        setCareStartDate(secondRoom.careStartDate);
        setRadFundingDate((current) => clampRadFundingDate(current, secondRoom.careStartDate));
        setExtraServiceFeeInput(secondRoom.extraServiceAnnual);
        setSelectedFacility(selectedSecondFacility);
        setSecondRoom({ roomPrice: wasFirst.roomPrice, careStartDate: wasFirst.careStartDate, extraServiceAnnual: wasFirst.extraServiceAnnual });
        setSelectedSecondFacility(wasFirst.facility);
        setLeadPerson((p) => (p === 'client' ? 'partner' : 'client'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bothEntering, careStartDate, secondRoom.careStartDate]);

    /** The aged care strategies the comparison table lists, and what they disagree about. */
    const comparedAgedCareStrategies = useMemo(() => savedStrategies.filter((s) => s.type === 'aged_care'), [savedStrategies]);
    const comparedBasisDifferences = useMemo(() => basisDifferences(comparedAgedCareStrategies), [comparedAgedCareStrategies]);

    /** Whether the optional "sell earlier" date has been asked for. Collapsed to a stated default until then. */
    const [showRadFundingDate, setShowRadFundingDate] = useState<boolean>(false);

    /** Typing-safe bindings for the two date inputs — see useClampedDateField for why they are needed. */
    const careStartDateField = useClampedDateField(careStartDate, (next) => {
        setCareStartDate(next);
        // Re-clamped against the new entry date — and cleared with it, so a sale date cannot survive
        // out of sight once its control is hidden.
        setRadFundingDate((current) => clampRadFundingDate(current, next));
    }, clampCareStartDate);
    const radFundingDateField = useClampedDateField(radFundingDate, setRadFundingDate, (raw) => clampRadFundingDate(raw, careStartDate));

    /**
     * The day the funding assets are actually sold: the nominated date, or failing that the day care
     * starts — which is what the screen has always said happens.
     *
     * Until this existed, leaving the sale date blank did NOT mean "sold on the entry date". It meant
     * the sale was baked into the fact-find before the projection began, i.e. sold TODAY, whatever
     * entry date had been entered. The care FEES correctly waited for the entry date while the funding
     * jumped the gun, so a client entering care in four months had their RAD counted as an exempt asset
     * from day one and their Age Pension lifted for four months that had not happened yet. Entering a
     * December date produced, to the dollar, the same cash as entering care tomorrow.
     *
     * With an entry date set, the engine now owns the liquidation in every case (`radPlugin` registers
     * its sale and deposit phases off this), and the "sell earlier" control simply moves the day it
     * happens. Blank entry date still means care is already under way, where selling at the start of
     * the projection IS selling on the entry date.
     */
    const effectiveRadFundingDate = radFundingDate || careStartDate;

    /**
     * Whether the ENGINE performs the asset sale and the debt payoff, rather than the fact-find
     * arriving with them already applied. True whenever there is a day to do them on.
     *
     * Hoisted out of `simulatedFactFind` because the Financial Impact panel needs it too — for the
     * opposite reason, see `factFindForImpactPanel`.
     */
    const radLiquidationDeferredToEngine = !!effectiveRadFundingDate || isMover;

    const [extraServiceFeeInput, setExtraServiceFeeInput] = useState<number>(0);
    const [radFunding, setRadFunding] = useState<RadFundingManualBag>({});
    /**
     * The Debt Strategy card's two allocations: how much of each debt to clear, and which assets pay
     * for it. Separate bags rather than one, because they are answers to different questions — and the
     * funding one shares its `type-id` key space with `radFunding` so the two cards can net off against
     * each other asset by asset.
     */
    const [debtPayments, setDebtPayments] = useState<RadFundingManualBag>({});
    const [debtFunding, setDebtFunding] = useState<RadFundingManualBag>({});
    /**
     * What a PART payment does to the repayment. Defaults to keeping it — which is what lenders do
     * unless asked otherwise, and retires the debt sooner. Only bites on a partial payoff.
     */
    const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
    /** Per RAD funding asset key — accountant CGT override ($ total). */
    const [cgtOverrides, setCgtOverrides] = useState<CgtOverridesBag>({});
    const [localLegacyYears, setLocalLegacyYears] = useState<number>(Number(legacyYears) || DEFAULT_PROJECTION_YEARS);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [overallStrategy, setOverallStrategy] = useState<OverallStrategyState>({ rentHome: false, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false });
    /** Highlights which saved aged care row was last opened or selected from the Suggestion Engine. */
    const [activeSavedStrategyId, setActiveSavedStrategyId] = useState<string | null>(null);
    /**
     * Set while a saved Suggestion Engine plan is being re-opened, so the panel opens quietly instead
     * of "thinking" (engineOpening.ts). Cleared whenever a mode is chosen afresh.
     */
    const [reopenedEngineStrategyId, setReopenedEngineStrategyId] = useState<string | null>(null);
    const [scenarioLivingExpenses, setScenarioLivingExpenses] = useState<number>(Number(factFind.expenses?.livingExpenses) || 0);
    /**
     * Living costs ONCE BOTH ARE IN CARE, where the second person enters later (owner, 7 Oct 2026).
     * Null = the same as the adjusted figure above; asked only in the staggered case, and applied by
     * the engine from the second entry (carePhases.ts).
     */
    const [phaseTwoLivingExpenses, setPhaseTwoLivingExpenses] = useState<number | null>(null);
    /** The second stage's living costs a year, where the second person enters later; null otherwise. */
    const livingOnceBoth: number | null = secondEntersLater ? (phaseTwoLivingExpenses ?? (Number(scenarioLivingExpenses) || 0)) : null;
    const [downsizeDetails, setDownsizeDetails] = useState<DownsizeDetails>({
        salePrice: factFind.home?.value || 0,
        sellingCosts: (Number(factFind.home?.value) || 0) * 0.025,
        radAllocation: 0,
        newHomePurchasePrice: 0,
        downsizerContribution: 0,
        partnerDownsizerContribution: 0,
        commenceIncomeStream: false,
        giftingAmount: 0,
        funeralBondPurchase: 0,
        purchaseInvestmentProperty: 0,
        investmentPropertyRent: 0,
        investmentPropertyExpenses: 0,
        purchaseShares: 0,
    });

    const [investmentPropertyDetails, setInvestmentPropertyDetails] = useState<InvestmentPropertySaleDetails>({
        selectedPropertyId: factFind.assets?.investmentProperty?.[0]?.id ?? undefined,
        salePrice: factFind.assets?.investmentProperty?.[0]?.value || 0,
        sellingCosts: (Number(factFind.assets?.investmentProperty?.[0]?.value) || 0) * 0.025,
        costBase: factFind.assets?.investmentProperty?.[0]?.costBase,
        radAllocation: 0,
        newHomePurchasePrice: 0,
        downsizerContribution: 0,
        partnerDownsizerContribution: 0,
        commenceIncomeStream: false,
        giftingAmount: 0,
        funeralBondPurchase: 0
    });

    const [reverseMortgageDetails, setReverseMortgageDetails] = useState<ReverseMortgageDetails>({
        loanAmount: 0,
        loanPercentage: 0,
        interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
        loanStructure: 'lump_sum', // 'lump_sum', 'regular_payments'
        regularPaymentAmount: 0,
        regularPaymentFrequency: 'fortnightly', // 'fortnightly', 'monthly'
        establishmentFees: 0,
        useForRad: false // Checkbox: Use loan amount to fund RAD
    });

    const [heasDetails, setHeasDetails] = useState<HEASDetails>({
        paymentAmount: 0,
        paymentFrequency: 'fortnightly', // 'fortnightly', 'monthly'
        interestRate: 3.95 // Default HEAS rate (will be updated periodically)
    });

    const [strategyInputs, setStrategyInputs] = useState<StrategyInputsState>({
        rentAmount: factFind.home?.likelyRent || 0,
        rentExpenses: factFind.home?.likelyExpenses || 0,
        partnerRent: 0
    });

    // Other Strategies state
    const [showOtherStrategies, setShowOtherStrategies] = useState(false); // Collapsed by default
    const [otherStrategies, setOtherStrategies] = useState<OtherStrategiesState>({
        gifting: false,
        giftingAmount: 0,
        giftingSource: 'bankAccounts',
        funeralBonds: false,
        funeralBondAmount: 0,
        funeralBondSource: 'bankAccounts',
        annuity: false
    });
    const [annuityRecord, setAnnuityRecord] = useState<AnnuityRecordState | null>(null);

    // Initialize annuity function
    const initializeAnnuityRecord = () => {
        const isCouple = factFind?.adviceFor === 'couple';
        return {
            id: 'aged-care-annuity', // Stable ID so we can replace existing annuity
            annuityType: 'lifetime',
            purchaseAmount: 0,
            // See DEFAULT_NEW_ANNUITY_RESIDUAL — capital preserved unless the client chooses otherwise.
            residualCapital: DEFAULT_NEW_ANNUITY_RESIDUAL,
            owner: isCouple ? 'joint' : 'client',
            fundingSource: {
                id: 'bank-default',
                type: 'bank',
                assetId: null,
                label: 'Bank accounts / cash',
                ownership: isCouple ? 'joint' : 'client'
            },
            indexation: false,
            reversionary: false,
            age: factFind?.client?.dob ? calculateAge(factFind.client?.dob) : null,
            partnerAge: isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null
        };
    };
    
    const [showProjections, setShowProjections] = useState(false);
    /** "Understanding your plan" — the full plain-words recap, opened from the plan summary panel. */
    const [showPlanExplanation, setShowPlanExplanation] = useState(false);
    const [strategyMode, setStrategyMode] = useState<'manual' | 'optimised'>('manual');
    /**
     * Whether a way of modelling has been CHOSEN on step 3 (owner-caught, 9 Oct 2026). Step 3 used to
     * open silently in Manual Control, whose default plan puts a village payout to the room, so the plan
     * summary read "Pay the room in full" before the adviser had chosen anything. Until a mode is chosen
     * (or a saved plan reopened) the rail is the walk-in position and neither mode is highlighted.
     */
    const [strategyModeChosen, setStrategyModeChosen] = useState(false);
    /*
     * THE THREE STEPS (owner's review, September 2026). 1: what care costs. 2: what it means, as
     * four numbers and the plain-words note. 3: the ways to close the gap — the mode chooser and
     * everything after it, exactly as before, with the plan summary rail. The rail is shown ONLY on
     * step 3: on step 2 the page IS the baseline, and a rail restating it was a second column
     * saying the same thing. Every step is reachable at any time from the strip.
     */
    // Both entering: the page is about the two of them, and says so wherever it names who is moving.
    const enteringFirstName = bothEntering
        ? `${roleNames(factFind, secondPerson).firstName} and ${roleNames(factFind, secondPerson).secondName}`
        : ((factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'partner'
            ? factFind.partner?.firstName
            : factFind.client?.firstName) || 'the client';
    const [step, setStep] = useState<AnalyserStep>(1);
    /**
     * Which position the projections modal shows. Step 2's "See the projections" opens the WALK-IN
     * projection over the legacy horizon (owner, 22 Sep 2026: "this should show the projections for
     * the default period which is 5 years"); the rail's button on step 3 opens the plan's, as before.
     */
    const [projectionsSource, setProjectionsSource] = useState<'plan' | 'walkIn'>('plan');
    const goToStep = (next: AnalyserStep) => {
        setStep(next);
        scrollAppToTop();
    };
    const [showFacilitySearch, setShowFacilitySearch] = useState(false);
    const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
    /** The second slot's three questions — the same component as the first slot's. */
    const secondRoomInputs = (
        <RoomInputs
            idSuffix="-second"
            roomPrice={secondRoom.roomPrice}
            onRoomPrice={(v) => setSecondRoom((r) => ({ ...r, roomPrice: v }))}
            onFacilitySearch={() => { setFacilityTarget('second'); setShowFacilitySearch(true); }}
            selectedFacility={selectedSecondFacility}
            extraServiceAnnual={secondRoom.extraServiceAnnual}
            onExtraServiceAnnual={(v) => setSecondRoom((r) => ({ ...r, extraServiceAnnual: v }))}
            careStartDate={secondRoom.careStartDate}
            dateInputProps={{ value: secondRoom.careStartDate, onChange: (e) => setSecondRoom((r) => ({ ...r, careStartDate: e.target.value })) }}
            minDate={toDateValue(new Date())}
            dateHelp={`Leave this blank if ${secondName} moves in at the same time as ${firstName}. With a later date, ${secondName} is modelled at home until then — the family home stays exempt and the pension is paid at the illness-separated rate — and their fees begin on the day you choose. An earlier date than ${firstName}'s makes ${secondName} the first to move in.`}
            dateHint={{ blank: careStartDate ? `Leave blank if ${secondName} moves in with ${firstName} in ${phaseFromLabel(careStartDate)}.` : `Leave blank if ${secondName} moves in with ${firstName} now.`, chosen: `${secondName}'s fees begin on this date.` }}
        />
    );
    /** The first room's three questions — rendered alone for one entrant, under a heading when both enter. */
    const firstRoomInputs = (
        <RoomInputs
            roomPrice={radInput}
            onRoomPrice={setRadInput}
            onFacilitySearch={() => { setFacilityTarget('first'); setShowFacilitySearch(true); }}
            selectedFacility={selectedFacility}
            extraServiceAnnual={extraServiceFeeInput}
            onExtraServiceAnnual={setExtraServiceFeeInput}
            careStartDate={careStartDate}
            dateInputProps={careStartDateField}
            minDate={toDateValue(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 1))}
            dateLabel={isMover ? 'When is the move?' : undefined}
            dateHelp={isMover
                ? "Leave this blank if the move is straight away. With a future date, the current home's fees and deposit run until then; on the day of the move the deposit comes back and goes to the new room, and the new home's fees begin."
                : "Leave this blank if care is starting now, or if you're not sure yet — we'll model it from today. With a future date, we show life at home as it is until then, and care costs only begin on the day you choose."}
            dateHint={isMover
                ? { blank: 'Leave blank if the move is straight away, or pick a future date.', chosen: 'The move happens on this date: the deposit comes back and the new home\'s fees begin.' }
                : { blank: 'Leave blank if care is starting now, or pick a future date.', chosen: 'Care costs begin on this date. The projection still starts today, so the lead-up is modelled too.' }}
        />
    );
    // For progressive disclosure
    const [showStrategyOptions, setShowStrategyOptions] = useState(false);
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [resetTrigger, setResetTrigger] = useState(0);

    // Mock facility data with demo pricing and Sydney suburb-based locations
    const mockFacilities = [
        {
            id: 1,
            name: "Sunrise Aged Care",
            suburb: "Croydon",
            state: "NSW",
            radPrice: 500000, // Demo price
            dailyFees: {
                basic: 70,
                dap: 68,
                nccc: 0,
                hsc: 13,
                extraServices: 0
            },
            distance: 1.2,
            rating: 4.2,
            careLevels: ["Low Care", "High Care"],
            amenities: ["Garden", "Library", "Chapel"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/sunrise-aged-care',
            images: [
                stockPhoto('1559027615-cd4628902d4a', 300, 200),
                stockPhoto('1586023492125-27b2c045efd7', 300, 200),
                stockPhoto('1560448204-e02f11c3d0e2', 300, 200)
            ]
        },
        {
            id: 2,
            name: "Garden View Retirement Village",
            suburb: "Five Dock",
            state: "NSW",
            radPrice: 850000, // Higher demo price
            dailyFees: {
                basic: 70,
                dap: 116,
                nccc: 0,
                hsc: 13,
                extraServices: 15
            },
            distance: 2.1,
            rating: 4.5,
            careLevels: ["Low Care", "High Care", "Dementia Care"],
            amenities: ["Swimming Pool", "Gym", "Cafe"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/garden-view-retirement-village',
            images: [
                stockPhoto('1571896349842-33c89424de2d', 300, 200),
                stockPhoto('1564013799919-ab600027ffc6', 300, 200),
                stockPhoto('1582407947304-fd86f028f716', 300, 200)
            ]
        },
        {
            id: 3,
            name: "Heritage Manor",
            suburb: "Campsie",
            state: "NSW",
            radPrice: 780000, // Higher demo price
            dailyFees: {
                basic: 70,
                dap: 107,
                nccc: 0,
                hsc: 13,
                extraServices: 0
            },
            distance: 3.4,
            rating: 3.8,
            careLevels: ["Low Care", "High Care"],
            amenities: ["Garden", "Library"],
            images: [
                stockPhoto('1512917774080-9991f1c4c750', 300, 200),
                stockPhoto('1560448204-603b3fc33ddc', 300, 200),
                stockPhoto('1564013799919-ab600027ffc6', 300, 200)
            ]
        },
        {
            id: 4,
            name: "Riverside Care Centre",
            suburb: "Burwood",
            state: "NSW",
            radPrice: 650000,
            dailyFees: {
                basic: 70,
                dap: 89,
                nccc: 0,
                hsc: 13,
                extraServices: 8
            },
            distance: 4.7,
            rating: 4.0,
            careLevels: ["Low Care", "High Care", "Palliative Care"],
            amenities: ["Water Views", "Garden", "Chapel"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/riverside-care-centre',
            images: [
                stockPhoto('1564013799919-ab600027ffc6', 300, 200),
                stockPhoto('1582407947304-fd86f028f716', 300, 200),
                stockPhoto('1571896349842-33c89424de2d', 300, 200)
            ]
        },
        {
            id: 5,
            name: "Mountain View Aged Care",
            suburb: "Ashfield",
            state: "NSW",
            radPrice: 420000,
            dailyFees: {
                basic: 70,
                dap: 58,
                nccc: 0,
                hsc: 13,
                extraServices: 0
            },
            distance: 6.2,
            rating: 4.3,
            careLevels: ["Low Care", "High Care"],
            amenities: ["Mountain Views", "Garden", "Library"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/mountain-view-aged-care',
            images: [
                stockPhoto('1559027615-cd4628902d4a', 300, 200),
                stockPhoto('1586023492125-27b2c045efd7', 300, 200),
                stockPhoto('1560448204-e02f11c3d0e2', 300, 200)
            ]
        },
        {
            id: 6,
            name: "Carlton Gardens Care",
            suburb: "Croydon",
            state: "NSW",
            radPrice: 720000,
            dailyFees: {
                basic: 70,
                dap: 99,
                nccc: 0,
                hsc: 13,
                extraServices: 12
            },
            distance: 1.8,
            rating: 4.1,
            careLevels: ["Low Care", "High Care", "Dementia Care"],
            amenities: ["City Views", "Garden", "Library", "Cafe"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/carlton-gardens-care',
            images: [
                stockPhoto('1512917774080-9991f1c4c750', 300, 200),
                stockPhoto('1560448204-603b3fc33ddc', 300, 200),
                stockPhoto('1564013799919-ab600027ffc6', 300, 200)
            ]
        }
    ];
    
    // Check for strategy to reopen from Dashboard
    useEffect(() => {
        const strategyToReopen = sessionStorage.getItem('careCompanionReopenStrategy');
        if (strategyToReopen) {
            try {
                const strategy = JSON.parse(strategyToReopen);
                reopenStrategy(strategy);
                // Clear the stored strategy
                sessionStorage.removeItem('careCompanionReopenStrategy');
            } catch (error) {
                console.error('Error parsing strategy to reopen:', error);
                sessionStorage.removeItem('careCompanionReopenStrategy');
            }
        } else {
            // If no strategy to reopen, clear selectedStrategy to ensure fresh start
            // This handles the case when navigating from Dashboard "Next Steps" (not reopening)
            setSelectedStrategy(null);
        }
    }, [setSelectedStrategy]);
    
    // Trigger reset when resetTrigger changes
    useEffect(() => {
        if (resetTrigger > 0) {
            resetManualControlToBaseline();
        }
    }, [resetTrigger]);
    
    // Strategy management functions
    const saveStrategy = (strategyName: string) => {
        const strategy: SavedAgedCareStrategy = {
            id: `strategy-${Date.now()}`,
            name: strategyName,
            type: 'aged_care',
            /*
             * WHO BUILT THIS PLAN. The suggestion engine already stamps its own saves with
             * source: 'suggestion_engine'; this path — an adviser setting the levers by hand — stamped
             * nothing, so on the dashboard the two were indistinguishable. That matters when an adviser
             * comes back weeks later to a list of plans and has to know which ones they reasoned their
             * way to and which the engine proposed.
             *
             * Plans saved before this carry no source at all, and the dashboard shows them no badge
             * rather than guessing one.
             */
            source: 'manual_control',
            inputs: {
                radInput,
                extraServiceFeeInput,
                radFunding,
                // The Debt Strategy card's two allocations, saved beside the RAD's so a reopened
                // strategy models the same plan it was saved as. Without these a strategy that cleared
                // a $50,000 loan would silently reopen as one that did not.
                debtPayments,
                debtFunding,
                debtRepaymentBasis,
                cgtOverrides,
                overallStrategy,
                strategyInputs,
                downsizeDetails,
                // The investment property sale, so the plan's position can be rebuilt away from this
                // page (the Action Plan's after-the-plan forms). Plans saved before 3 Oct 2026 lack it.
                investmentPropertyDetails,
                reverseMortgageDetails,
                heasDetails,
                scenarioLivingExpenses,
                otherStrategies,
                annuityRecord,
                careStartDate,
                radFundingDate,
                radDrawdown,
                // The second room, where both are entering care (null otherwise), so a reopened plan has it.
                secondRoom: bothEntering ? { ...secondRoom, person: secondPerson, radPaid: secondFunding?.funded ?? 0 } : null,
                leadPerson,
                // A change of home (8 Oct 2026): the home left, the refund, and what the move pays.
                facilityMove: isMover && currentHomeFacts ? { person: moverPerson, moveDate: careStartDate || todayIsoDate(), facilityName: currentHomeFacts.facilityName, currentRoomPrice: currentHomeFacts.roomPrice, paid: currentHomeFacts.paid, refundToday: moverDeposit.balance, retained: moverDeposit.retained, retentionDaysTaken: moverDeposit.retentionDaysTaken, grandfathered: moverGrandfathered, newRoomPrice: radInput, radTarget: radPaid } : null,
                // Leaving a village (8 Oct 2026): what the village pays, when, and what of it goes to the room.
                villageExit: villageExit ? { person: villagePerson, leaveDate: villageLeaveDate, refundDate: villageRefundDate, paidAtMove: villagePaidAtMove, refundMonths: villageExit.refundMonths, entitlement: villageExit.entitlement, departureFee: villageExit.departureFee, reinstatement: villageExit.reinstatement, fromVillage: radPaidBreakdown.fromVillage, toBank: Math.max(0, villageExit.entitlement - radPaidBreakdown.fromVillage), interim: villageInterim, feesAfterLeaving: villageFees } : null,
                phaseTwoLivingExpenses,
                ...(residentialFeeArrangements ? { residentialFeeArrangements } : {}),
                // Recorded so a saved strategy can say what horizon its legacy estimate was struck over.
                // Without it the comparison table put a 10-year legacy beside a 5-year one with nothing
                // to distinguish them — and the default horizon itself changed on 2026-08-06, so a
                // client's own saved strategies can straddle the change. Strategies saved before this
                // carry no `legacyYears`; the table says "not recorded" rather than guessing.
                legacyYears: localLegacyYears,
            },
            outputs: {
                agedCareCost: (householdAgedCareCosts.totalDailyCost || 0) * 365,
                // The second stage, where the second person enters later (carePhases.ts).
                phaseTwo: phaseTwoHousehold && phaseTwo ? { fromDate: secondRoom.careStartDate, agedCareCost: (phaseTwoHousehold.totalDailyCost || 0) * 365, householdDailyCost: phaseTwoHousehold.totalDailyCost || 0, cashFlow: (phaseTwo.financials.cashFlow || 0) - (phaseTwoHousehold.totalDailyCost || 0) * 365, agePension: phaseTwo.financials.socialSecurity || 0, ...(livingOnceBoth != null ? { livingExpenses: livingOnceBoth } : {}) } : null,
                agePension: simulatedFinancials.socialSecurity || 0,
                cashFlow: simulatedFinancials.cashFlow - ((householdAgedCareCosts.totalDailyCost || 0) * 365),
                legacyEstimate: (projections && projections.length > 0 ? projections[projections.length - 1]?.netWorth : 0) || 0,
                roomCost: radInput, // Add room cost for identification
                // The whole net worth line, not just its last row. `legacyEstimate` above is the final
                // year and answers only "what is left at the end"; a client asking what they will be
                // worth in a particular year is asking about a row in the middle, which used to be
                // discarded here. See shared/src/calculation/netWorthSchedule.ts.
                netWorthSchedule: buildNetWorthSchedule(projections),
            },
            createdAt: new Date().toISOString()
        };

        const newStrategies = [...savedStrategies, strategy];
        setSavedStrategies(newStrategies);
        setActiveSavedStrategyId(strategy.id);
        sessionStorage.setItem('careCompanionStrategies', JSON.stringify(newStrategies));
        // sessionStorage is the working copy; this is what gets it onto the server for a real client.
        onStrategySaved?.(strategy);
        setActiveHomeCareStrategyId(formatServiceBuilderSelectionId('aged_care', strategy.id));
        notifyHomeCareStrategiesUpdated();

        // Trigger reset using state change
        setResetTrigger(prev => prev + 1);
    };

    const openManualControlDigitalSOA = async () => {
        /**
         * The reader's chosen calendar is a MODELLING choice, not a display filter — the engine opens,
         * grows and settles on the period it reports — so an SOA produced while calendar years are
         * selected must be built from a calendar-year RUN, not financial-year rows wearing calendar
         * headings. Run here rather than kept in state: generating an SOA is an explicit act, and this
         * spares every page load a second engine call for a document nobody may ask for.
         *
         * Uses the identical inputs the financial-year run used, so the only difference between the two
         * documents is the calendar the reader asked for.
         */
        const soaPeriodMode = readPeriodMode();
        const soaProjections = soaPeriodMode === 'calendar_year'
            ? await calculationApi.runProjections(
                simulatedFactFind,
                { ...projectionStrategyOptions, projectionPeriodMode: 'calendar_year' },
                agedCareCosts,
                localLegacyYears,
                { debug: false },
            )
            : projections;
        /*
         * THE SAME PLAN AT OUR RATES, where the client set a rate of their own. The SOA reports a
         * client-chosen rate as their decision and shows what it is worth — this exact plan, on the
         * same applied balance sheet, with nothing changed but the rates. See assetRateOverrides.ts.
         */
        const atDefaultRatesProjections = hasAssetRateOverrides(factFind)
            ? await calculationApi.runProjections(
                withDefaultAssetRates(simulatedFactFind),
                soaPeriodMode === 'calendar_year' ? { ...projectionStrategyOptions, projectionPeriodMode: 'calendar_year' } : projectionStrategyOptions,
                agedCareCosts,
                localLegacyYears,
                { debug: false },
            )
            : null;
        const manualSavedStrategies = savedStrategies.filter(
            (s) => s.type === 'aged_care' && s.source !== 'suggestion_engine'
        );
        const describeManualStrategy = () => {
            const radPct = radInput > 0 ? Math.round((radPaid / radInput) * 100) : 0;
            const homeAction = overallStrategy.downsizeHome
                ? 'Downsize Home'
                : overallStrategy.rentHome
                    ? 'Rent Home'
                    : overallStrategy.reverseMortgage
                        ? 'Reverse Mortgage'
                        : overallStrategy.heas
                            ? 'HEAS'
                            : 'Keep Home';
            return `Pay ${radPct}% RAD, ${homeAction}`;
        };

        const selectedName = describeManualStrategy();
        const convertedRadFunding = convertManualRadFundingForSnapshot(radFunding);
        const firstYear = soaProjections?.[0] || {};
        const currentManualStrategy = {
            id: `manual-current-${Date.now()}`,
            name: selectedName,
            projections: soaProjections || [],
            radFunding: convertedRadFunding,
            strategyOptions: { ...overallStrategy, ...strategyInputs, downsizeDetails, radDrawdown },
            atDefaultRatesProjections,
            snapshot: {
                legacyValue: soaProjections && soaProjections.length > 0 ? soaProjections[soaProjections.length - 1].netWorth : 0,
                netCashflow: simulatedFinancials.cashFlow - ((householdAgedCareCosts.totalDailyCost || 0) * 365),
                pension: simulatedFinancials.socialSecurity ?? firstYear.socialSecurity ?? 0,
                totalAgedCareCosts: (householdAgedCareCosts.totalDailyCost || 0) * 365,
                livingExpenses: scenarioLivingExpenses || 0,
                taxPayable: simulatedFinancials.taxPayable ?? firstYear.taxPayable ?? 0,
            },
            context: `Selected by adviser from manual control modelling (${selectedName}).`,
        };

        const savedAlternatives = manualSavedStrategies.map((s) => ({
            id: s.id,
            name: s.name,
            projections: [],
            radFunding: convertManualRadFundingForSnapshot(s.inputs?.radFunding || {}),
            strategyOptions: s.inputs?.overallStrategy || {},
            snapshot: {
                legacyValue: s.outputs?.legacyEstimate || 0,
                netCashflow: s.outputs?.cashFlow || 0,
                pension: s.outputs?.agePension || 0,
                totalAgedCareCosts: s.outputs?.agedCareCost || 0,
                livingExpenses: scenarioLivingExpensesOr(s.inputs?.scenarioLivingExpenses, factFind.expenses?.livingExpenses),
                taxPayable: 0,
            },
            context: 'Alternative manual strategy saved during adviser modelling.',
        }));

        const normalizeName = (name: string = '') => String(name).toLowerCase().replace(/[^a-z0-9]/g, '');
        const isNearlyEqual = (a: unknown, b: unknown, tolerance: number = 1) => Math.abs((Number(a) || 0) - (Number(b) || 0)) <= tolerance;
        const dedupedAlternatives = savedAlternatives.filter((s) => {
            const sameName = normalizeName(s.name) === normalizeName(currentManualStrategy.name);
            const sameOutcome =
                isNearlyEqual(s.snapshot?.legacyValue, currentManualStrategy.snapshot?.legacyValue) &&
                isNearlyEqual(s.snapshot?.netCashflow, currentManualStrategy.snapshot?.netCashflow) &&
                isNearlyEqual(s.snapshot?.pension, currentManualStrategy.snapshot?.pension);
            return !(sameName || sameOutcome);
        });

        const allManualScenarios = [currentManualStrategy, ...dedupedAlternatives];
        /*
         * The SOA baseline, from the SAME definition the screen uses (agedCareBaseline.ts) — so the
         * document and the panel can never quote different improvement figures for one plan.
         *
         * It previously built these options inline and fell into both halves of the trap that module
         * exists to close: it carried no residency override, so the couple was assessed as two people
         * living at home while being charged full nursing-home fees, and the illness-separated
         * treatment never fired — understating the baseline pension and crediting every strategy with
         * the difference. It also ran on `simulatedFactFind`, i.e. the strategy's own balance sheet.
         * Both are fixed below: original fact-find, residency carried explicitly.
         */
        const doNothingStrategyOptions = buildDoNothingStrategyOptions(factFind, scenarioLivingExpenses, {
            ...(careStartDate ? { careStartDate } : {}),
        });
        const doNothingAgedCareCosts = await calculationApi.calculateAgedCareCosts({
            factFind,
            financials: initialFinancials,
            radInput,
            extraServiceFeeInput: 0,
            radPaid: 0,
            overallStrategy: { rentHome: false, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false },
        });
        const doNothingProjections = await calculationApi.runProjections(
            // The ORIGINAL fact-find. Running the strategy's mutated one made the "do nothing"
            // baseline inherit the home already sold and the RAD already paid — every balance-sheet
            // consequence of the strategy, with only its options removed.
            factFind,
            // Same calendar as the strategy above: comparing a calendar-year strategy against a
            // financial-year baseline would put a timing artefact into every delta in the document.
            // The second room, undecided too, where both are entering care.
            { ...doNothingStrategyOptions, projectionPeriodMode: soaPeriodMode, ...(baselineSecondCosts ? { secondResident: { person: secondPerson, ...(secondRoom.careStartDate ? { careStartDate: secondRoom.careStartDate } : {}), radFunding: {}, agedCareCosts: baselineSecondCosts } } : {}) },
            { ...doNothingAgedCareCosts, scenarioLivingExpenses },
            localLegacyYears
        );
        const doNothingFirstYear = doNothingProjections?.[0] || {};
        const doNothingStrategy = {
            id: `manual-do-nothing-${Date.now()}`,
            name: 'Do Nothing',
            projections: doNothingProjections || [],
            radFunding: {},
            strategyOptions: doNothingStrategyOptions,
            snapshot: {
                legacyValue: doNothingProjections?.length > 0 ? doNothingProjections[doNothingProjections.length - 1].netWorth : 0,
                netCashflow: (doNothingFirstYear.totalIncome || 0) - (doNothingFirstYear.totalExpenses || 0),
                pension: doNothingFirstYear.socialSecurity || 0,
                totalAgedCareCosts: householdDailyCost(doNothingAgedCareCosts, baselineSecondCosts) * 365,
                livingExpenses: scenarioLivingExpenses || 0,
                taxPayable: doNothingFirstYear.taxPayable || 0,
            },
            context: 'Do nothing baseline: no strategy actions and no RAD lump sum contribution.',
        };
        const analysisForSoa = {
            totalScenarios: allManualScenarios.length + 1,
            strategies: [doNothingStrategy, ...allManualScenarios],
            allSimulatedStrategies: [doNothingStrategy, ...allManualScenarios],
            recommendedOptimalStrategies: [],
        };

        // buildAgedCareDigitalSOASnapshot's SoaScenario/SoaAnalysis param types (owned by a concurrent
        // typing pass on AgedCareSuggestionEngineDigitalSOAAttachment.tsx) aren't exported, so the
        // locally-built snapshot bags here (structurally close but not identical) are cast at this
        // one boundary rather than duplicating their internal shape.
        const snapshot = await buildAgedCareDigitalSOASnapshot({
            selectedStrategy: currentManualStrategy as any,
            factFind,
            agedCareCosts: { ...agedCareCosts, radAmount: radInput, scenarioLivingExpenses },
            analysis: analysisForSoa as any,
            coupleValidation,
            reportAudience: 'professional',
            strategySource: 'manual_control',
            manualAlternativeStrategies: allManualScenarios as any,
            projectionPeriodMode: soaPeriodMode,
        });
        openAgedCareDigitalSOAAttachment(snapshot);

        const savedSoaScenario = {
            id: `soa-${Date.now()}`,
            name: `MC | ${currentManualStrategy.name}`,
            createdAt: new Date().toISOString(),
            soaSource: 'MC',
            caseId: professionalCaseId || null,
            soaDeliveryStatus: 'draft',
            isCurrentForCase: true,
            outputs: {
                agedCareCost: (householdAgedCareCosts.totalDailyCost || 0) * 365,
                // The second stage, where the second person enters later (carePhases.ts).
                phaseTwo: phaseTwoHousehold && phaseTwo ? { fromDate: secondRoom.careStartDate, agedCareCost: (phaseTwoHousehold.totalDailyCost || 0) * 365, householdDailyCost: phaseTwoHousehold.totalDailyCost || 0, cashFlow: (phaseTwo.financials.cashFlow || 0) - (phaseTwoHousehold.totalDailyCost || 0) * 365, agePension: phaseTwo.financials.socialSecurity || 0, ...(livingOnceBoth != null ? { livingExpenses: livingOnceBoth } : {}) } : null,
                agePension: currentManualStrategy.snapshot.pension || 0,
                cashFlow: currentManualStrategy.snapshot.netCashflow || 0,
                legacyEstimate: currentManualStrategy.snapshot.legacyValue || 0,
            },
            soaSnapshot: snapshot,
        };
        const newCaseStudies = [
            ...savedCaseStudies.map((item) =>
                professionalCaseId && item.caseId === professionalCaseId
                    ? { ...item, isCurrentForCase: false }
                    : item
            ),
            savedSoaScenario,
        ];
        setSavedCaseStudies(newCaseStudies);
        sessionStorage.setItem('careCompanionCaseStudies', JSON.stringify(newCaseStudies));
        sessionStorage.setItem('careCompanionScenarios', JSON.stringify(newCaseStudies));
        onSoaGenerated?.(savedSoaScenario);
    };
    
    const resetManualControlToBaseline = () => {
        
        // Keep RAD and living expenses - don't reset these core values
        // setRadInput(0); // Don't reset - keep current RAD value
        // setScenarioLivingExpenses(factFind.expenses?.livingExpenses || 0); // Don't reset - keep current living expenses
        
        // Reset only the strategy-specific inputs
        setExtraServiceFeeInput(0);
        // Care start date is KEPT, like the room price and living expenses above it. When someone
        // enters a date and saves a strategy, they are almost always about to model a second way of
        // funding the SAME move — clearing it made them re-enter the same date for every scenario,
        // and (worse) a strategy saved without noticing came out anchored to "care starts now",
        // which is a different projection rather than a blank field.
        //
        // The SALE date does still clear: it belongs to the funding plan being cleared below, not to
        // the move. With `radFunding` emptied there is nothing left to sell, so keeping it would
        // leave a date attached to a sale that no longer exists.
        setRadFundingDate('');
        setRadFunding({});
        setDebtPayments({});
        setDebtFunding({});
        setDebtRepaymentBasis('keep_repayment');
        setCgtOverrides({});
        setLocalLegacyYears(legacyYears || DEFAULT_PROJECTION_YEARS);
        setOverallStrategy({ rentHome: false, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false });
        setStrategyInputs({
            rentAmount: factFind.home?.likelyRent || 0,
            rentExpenses: factFind.home?.likelyExpenses || 0,
            partnerRent: 0
        });
        setDownsizeDetails({
            salePrice: factFind.home?.value || 0,
            sellingCosts: (Number(factFind.home?.value) || 0) * 0.025,
            radAllocation: 0,
            newHomePurchasePrice: 0,
            downsizerContribution: 0,
            giftingAmount: 0,
            funeralBondPurchase: 0,
        });
        setReverseMortgageDetails({
            loanAmount: 0,
            loanPercentage: 0,
            interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
            loanStructure: 'lump_sum',
            regularPaymentAmount: 0,
            regularPaymentFrequency: 'fortnightly',
            establishmentFees: 0,
            useForRad: false
        });
        setHeasDetails({
            paymentAmount: 0,
            paymentFrequency: 'fortnightly',
            interestRate: 3.95
        });
        // Reset Other Strategies
        setOtherStrategies({
            gifting: false,
            giftingAmount: 0,
            giftingSource: 'bankAccounts',
            funeralBonds: false,
            funeralBondAmount: 0,
            funeralBondSource: 'bankAccounts',
            annuity: false
        });
        setAnnuityRecord(null);
        setShowOtherStrategies(false);
        setShowProjections(false);
        setShowCompareModal(false);
        setShowStrategyOptions(true);
        setStrategyMode('manual');
    };
    
    const reopenStrategy = (strategy: SavedAgedCareStrategy) => {
        // Handle different strategy structures
        if (strategy.source === 'suggestion_engine') {
            // Restore inputs so facility price and cost context match when strategy was saved (avoids $0 and wrong optimal on reopen)
            const roomCost = strategy.outputs?.roomCost;
            if (roomCost != null && !isNaN(parseFloat(String(roomCost)))) {
                setRadInput(parseFloat(String(roomCost)));
            }
            if (strategy.inputs?.scenarioLivingExpenses != null) {
                setScenarioLivingExpenses(strategy.inputs.scenarioLivingExpenses);
            }
            setStrategyModeChosen(true); setStrategyMode('optimised');
            setShowStrategyOptions(true);
            setSelectedStrategy(strategy.strategy ?? null);
            setActiveSavedStrategyId(strategy.id);
            setReopenedEngineStrategyId(strategy.id ?? null);
            return;
        }
        
        // For Manual Control strategies, restore the strategy inputs
        const inputs = strategy.inputs || {};
        setRadInput(inputs.radInput || 0);
        // Re-clamped on restore: a strategy saved months ago may carry an entry date that has since
        // arrived, which collapses to "care is starting now". Strategies saved before entry became
        // day-precise carry `careEntryMonth` instead, and are read as the 1st of that month.
        const restoredCareStart = careStartDateFromSavedInputs(inputs);
        setCareStartDate(restoredCareStart);
        // Re-clamped against the restored entry date, not just against today: a sale date that has
        // arrived, or that now sits after the move, collapses to "sold now".
        setRadFundingDate(clampRadFundingDate(inputs.radFundingDate, restoredCareStart));
        setRadDrawdown(inputs.radDrawdown || 'fees');
        setSecondRoom(inputs.secondRoom ? { roomPrice: Number(inputs.secondRoom.roomPrice) || 0, careStartDate: inputs.secondRoom.careStartDate || '', extraServiceAnnual: Number(inputs.secondRoom.extraServiceAnnual) || 0 } : EMPTY_SECOND_ROOM);
        setLeadPerson(inputs.leadPerson === 'partner' ? 'partner' : 'client');
        setPhaseTwoLivingExpenses(typeof inputs.phaseTwoLivingExpenses === 'number' ? inputs.phaseTwoLivingExpenses : null);
        setOptInToNovember2025Fees(inputs.residentialFeeArrangements === '2025');
        setExtraServiceFeeInput(inputs.extraServiceFeeInput || 0);
        setRadFunding(inputs.radFunding || {});
        setDebtPayments(inputs.debtPayments || {});
        setDebtFunding(inputs.debtFunding || {});
        setDebtRepaymentBasis(inputs.debtRepaymentBasis || 'keep_repayment');
        setCgtOverrides(inputs.cgtOverrides || {});
        setOverallStrategy(inputs.overallStrategy || { rentHome: false, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false });
        setStrategyInputs(inputs.strategyInputs || {
            rentAmount: factFind.home?.likelyRent || 0,
            rentExpenses: factFind.home?.likelyExpenses || 0,
            partnerRent: 0
        });
        setDownsizeDetails(inputs.downsizeDetails || {
            salePrice: factFind.home?.value || 0,
            sellingCosts: (Number(factFind.home?.value) || 0) * 0.025,
            radAllocation: 0,
            newHomePurchasePrice: 0,
            downsizerContribution: 0,
            partnerDownsizerContribution: 0,
            giftingAmount: 0,
            funeralBondPurchase: 0,
            purchaseInvestmentProperty: 0,
            investmentPropertyRent: 0,
            investmentPropertyExpenses: 0,
            purchaseShares: 0,
        });
        setReverseMortgageDetails(inputs.reverseMortgageDetails || {
            loanAmount: 0,
            loanPercentage: 0,
            interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
            loanStructure: 'lump_sum',
            regularPaymentAmount: 0,
            regularPaymentFrequency: 'fortnightly',
            establishmentFees: 0,
            useForRad: false
        });
        setHeasDetails(inputs.heasDetails || {
            paymentAmount: 0,
            paymentFrequency: 'fortnightly',
            interestRate: 3.95
        });
        setScenarioLivingExpenses(scenarioLivingExpensesOr(inputs.scenarioLivingExpenses, factFind.expenses?.livingExpenses));
        
        // Restore Other Strategies
        setOtherStrategies(inputs.otherStrategies || {
            gifting: false,
            giftingAmount: 0,
        giftingSource: 'bankAccounts',
        funeralBonds: false,
        funeralBondAmount: 0,
        funeralBondSource: 'bankAccounts',
            annuity: false
        });
        setAnnuityRecord(inputs.annuityRecord || null);
        if (inputs.otherStrategies?.annuity || inputs.annuityRecord) {
            setShowOtherStrategies(true); // Expand if strategy was saved
        }
        
        // Ensure Manual Control is active
        setStrategyModeChosen(true); setStrategyMode('manual');
        setShowStrategyOptions(true);
        setActiveSavedStrategyId(strategy.id);
    };
    /**
     * LEAVING A RETIREMENT VILLAGE (owner, 7 Oct 2026). The entering person lives in a village and the
     * unit is sold back on leaving (a single resident's always; a couple's where both go, or the
     * profile says so). The village pays the exit entitlement — the entry contribution less the
     * departure fee, reinstatement and the operator's share of any gain — on the state's timeline,
     * MONTHS after the move. So: the interest counts as their home until the refund; the part of the
     * room the village money will pay is charged daily until it arrives; and from the refund the
     * person is a non-homeowner with cash. The projection does all of it (villageExitPlugin).
     */
    const villagePerson: 'client' | 'partner' = moverPerson;
    const villageSells = !isMover && villageSellsOnEntry(asFactFind(factFind), villagePerson, bothEntering);
    const villageLeaveDate = isCompleteIsoDate(careStartDate) ? careStartDate : todayIsoDate();
    const villageExit = useMemo(() => (villageSells ? villageExitEntitlement(asFactFind(factFind), villageLeaveDate) : null), [villageSells, factFind, villageLeaveDate]);
    /**
     * WHEN THE UNIT IS PAID OUT (owner, 8 Oct 2026, for consistency with the village and granny flat
     * modules): "later", on the state's timeline, is the default here — in a care move the home
     * charges the daily payment from day one, so the conservative case is the one to show unless the
     * adviser knows the sale is settled. "At the move" pays the entitlement on the entry day: no
     * wait, no fees after leaving, and the interest is sold from entry.
     */
    const [villagePaidAtMove, setVillagePaidAtMove] = useState(false);
    const villageRefundDate = villageExit ? (villagePaidAtMove ? villageLeaveDate : villageExit.refundDate) : villageLeaveDate;
    const villageAtMoveOptions = useMemo(() => (villagePaidAtMove && villageExit ? { villageInterestSold: true } : {}), [villagePaidAtMove, villageExit]);
    /** The funding card's choice: the village money tops the room up when it arrives (on by default). */
    const [useVillageRefundForRoom, setUseVillageRefundForRoom] = useState(true);
    const villageFacts = useMemo<VillageFacts | null>(
        () => (villageExit ? { name: (villagePerson === 'partner' ? factFind.partner?.firstName : factFind.client?.firstName) || 'the client', ...villageExit, refundDate: villageRefundDate } : null),
        [villageExit, villagePerson, factFind.client?.firstName, factFind.partner?.firstName, villageRefundDate],
    );
    const radPaidBreakdown = useMemo(() => {
        const { netTotal: fromLiquidAssets } = computeRadFundingTotals(
            factFind,
            radFunding,
            cgtOverrides
        );
        // GATED ON THE STRATEGY TOGGLES. These allocations survive in state when a strategy is
        // switched off (so flipping it back on restores the sliders), but money from a strategy
        // that is OFF is not being paid: before this gate, a downsize test left its $700k
        // radAllocation counting as "paid" after the toggle was off - the rail showed DAP $0 on a
        // room nothing was funding, while the financials (which do gate on the toggles) told the
        // other story (owner-caught, 2026-08-24). simulatedFactFind gates the same way.
        const fromDownsize = overallStrategy.downsizeHome
            ? parseFloat(String(downsizeDetails.radAllocation)) || 0
            : 0;
        const fromInvestmentProperty = overallStrategy.sellInvestmentProperty
            ? investmentPropertyDetails.radAllocation || 0
            : 0;
        const fromReverseMortgage = (overallStrategy.reverseMortgage && reverseMortgageDetails.useForRad && 
            reverseMortgageDetails.loanStructure === 'lump_sum') 
            ? parseFloat(String(reverseMortgageDetails.loanAmount)) || 0 
            : 0;
        // A change of home: the refund of the current deposit goes to the new room before anything is sold.
        const fromRefund = isMover ? moverDeposit.balance : 0;
        const fromOther = fromDownsize + fromInvestmentProperty + fromReverseMortgage + fromRefund;
        // Leaving a village: the exit payment tops the room up when it arrives — only what savings and
        // the strategies leave unpaid, and only up to what the village pays. Nothing of it at entry.
        const fromVillage = villageExit && useVillageRefundForRoom
            ? Math.max(0, Math.min(villageExit.entitlement, radInput - fromLiquidAssets - fromOther))
            : 0;
        return {
            fromLiquidAssets,
            fromRefund,
            fromVillage,
            fromStrategies: fromOther + fromVillage,
            total: fromLiquidAssets + fromOther + fromVillage,
        };
    }, [factFind, radFunding, cgtOverrides, downsizeDetails.radAllocation, investmentPropertyDetails.radAllocation, overallStrategy.downsizeHome, overallStrategy.sellInvestmentProperty, overallStrategy.reverseMortgage, reverseMortgageDetails, isMover, moverDeposit.balance, villageExit, useVillageRefundForRoom, radInput]);
    const radPaid = radPaidBreakdown.total;
    /** The deposit paid AT ENTRY: the village's part arrives later (unless paid at the move), and the means are assessed on this. */
    const radPaidAtEntry = radPaid - (villagePaidAtMove ? 0 : radPaidBreakdown.fromVillage);
    /** What the wait for the village money costs: the daily payment on its share of the room until it arrives. */
    const villageInterim = useMemo(
        () => (villageExit ? villageInterimDap(radPaidBreakdown.fromVillage, villageLeaveDate, villageRefundDate, AGED_CARE_SCHEDULE.MPIR) : null),
        [villageExit, radPaidBreakdown.fromVillage, villageLeaveDate, villageRefundDate],
    );
    /** The old unit's fees after leaving, on the state's rule, for the days of the wait (8 Oct 2026). */
    const villageFees = useMemo(
        () => (villageExit ? villageFeesAfterLeaving(factFind.retirementVillage, villageLeaveDate, villageRefundDate) : null),
        [villageExit, factFind.retirementVillage, villageLeaveDate, villageRefundDate],
    );
    /** The projection's plan for the village: leave, be paid, put this much to the room, pay the fees after leaving. */
    const villageExitPlan = useMemo(
        () => (villageExit ? { person: villagePerson, leaveDate: villageLeaveDate, refundDate: villageRefundDate, entitlement: villageExit.entitlement, radAllocation: radPaidBreakdown.fromVillage, interimDailyPayment: villageInterim?.perDay ?? 0, ...(villageFees && villageFees.total > 0 ? { feesAfterLeaving: villageFees } : {}) } : null),
        [villageExit, villagePerson, villageLeaveDate, villageRefundDate, radPaidBreakdown.fromVillage, villageInterim, villageFees],
    );
    /**
     * The second room pays the SAME SHARE up front as the first (owner, 7 Oct 2026: "the clients
     * don't know what they should be paying"): one decision on the funding card covers both rooms,
     * exactly as every Suggestion Engine candidate does. Funded from what the first deposit leaves.
     */
    const secondRadTarget = secondRoomActive && radInput > 0 ? Math.round(secondRoom.roomPrice * Math.min(1, radPaid / radInput)) : 0;
    const secondFunding = useMemo(
        () => (secondRoomActive ? allocateSecondDepositFunding(asFactFind(factFind), secondRadTarget, radFunding) : null),
        [secondRoomActive, factFind, secondRadTarget, radFunding],
    );
    /*
     * When a strategy above (downsize / property sale / borrowing) already covers the whole room
     * price and nothing is drawn from savings, the funding sliders are pure noise: every control in
     * the section would rightly sit at zero. Collapse it to one line saying WHY it is quiet, with an
     * explicit way back in — never remove a section silently (owner direction, 2026-08-24).
     */
    const roomCoveredByStrategies =
        radInput > 0 && radPaidBreakdown.fromLiquidAssets === 0 && radPaidBreakdown.fromStrategies >= radInput - 0.5;

    /**
     * Is anything actually being SOLD to fund the deposit?
     *
     * Gates the sale-date control below, which otherwise appeared the moment a care date was entered —
     * asking when the assets would be sold of clients who had allocated nothing to a lump sum at all,
     * and were simply paying the daily fee. That reads as the tool having decided something will be
     * sold, which is not a small thing to imply to someone deciding how to fund their parent's care.
     *
     * Cash is not sold: money already in a bank account is paid across on the day, so there is no sale
     * date to nominate and nothing the timing could change. A share parcel, an investment property or
     * the family home is a disposal — the proceeds sit in the bank until the deposit is paid (deemed,
     * and counted in the assets test) and the CGT falls on the day of sale. That is what the control is
     * for, and it is only worth showing to the people it can actually help.
     *
     * A reverse mortgage is drawn, not sold, so it is not counted here either.
     */
    const radFundedBySellingAssets = useMemo(
        () =>
            radFundingInvolvesAssetSale(radFunding, {
                home: overallStrategy.downsizeHome ? downsizeDetails.radAllocation : 0,
                investmentProperty: overallStrategy.sellInvestmentProperty ? investmentPropertyDetails.radAllocation : 0,
            }),
        [radFunding, downsizeDetails.radAllocation, investmentPropertyDetails.radAllocation, overallStrategy.downsizeHome, overallStrategy.sellInvestmentProperty],
    );

    // A sale date must not survive the sale it belonged to. Switch the funding to cash and the control
    // above disappears — but the date would keep feeding the engine, deferring a sale that no longer
    // exists, with nothing on screen to show for it. Same rule already applied when the care date is
    // cleared; this is the other way the control can vanish.
    useEffect(() => {
        if (!radFundedBySellingAssets && radFundingDate) setRadFundingDate('');
    }, [radFundedBySellingAssets, radFundingDate]);

    /**
     * The optional "sell before care starts?" control, rendered inside the RAD Funding card beneath
     * its totals — see RadFundingStrategy's `saleTimingSlot`. It is a consequence of the funding
     * plan, so it belongs where that plan is made rather than beside the care date it depends on.
     *
     * "The assets are sold when care starts" is a statement of what the model does, and was not always
     * one: until `effectiveRadFundingDate` existed it described behaviour the app only performed if you
     * ALSO filled in the date below, and the sentence appeared solely in the case where it was false.
     * The sentence was made true rather than softened to fit — see `effectiveRadFundingDate`.
     */
    const saleTimingSlot =
        careStartDate && radFundedBySellingAssets ? (
            <div className="space-y-2">
                {!showRadFundingDate && !radFundingDate ? (
                    <p className="text-sm text-slate-600">
                        The assets are sold when care starts.{' '}
                        <button
                            type="button"
                            onClick={() => setShowRadFundingDate(true)}
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                        >
                            Sell earlier instead
                        </button>
                    </p>
                ) : (
                    <>
                        <label htmlFor="rad-funding-date" className="block text-sm font-medium text-gray-700 mb-1">
                            Sell the assets before care starts?{' '}
                            <WhatsThis text="Optional. Leave blank to sell on the day care starts, which is what most people do. Choosing an earlier date models selling ahead of the move: the proceeds sit in the bank until the deposit is paid, where they are deemed and counted in the assets test, which can reduce the Age Pension in the meantime. Capital gains tax is calculated on the sale date, so holding an asset past twelve months to qualify for the CGT discount shows up here." />
                        </label>
                        <input
                            type="date"
                            id="rad-funding-date"
                            name="radFundingDate"
                            min={toDateValue(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 1))}
                            max={careStartDate}
                            {...radFundingDateField}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-slate-500">
                            {radFundingDate
                                ? 'Assets are sold on this date and the proceeds held in the bank until the deposit is paid on entry.'
                                : "Leave blank and they'll be sold on the day care starts — no need to guess how long a sale takes."}
                        </p>
                    </>
                )}
            </div>
        ) : null;

    const [initialFinancials, setInitialFinancials] = useState<FinancialPosition>({ netWorth: 0, cashFlow: 0 });
    useEffect(() => {
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((r) => { if (!cancelled) setInitialFinancials(r as FinancialPosition); }).catch(quietCalc('the position today'));
        return () => { cancelled = true; };
    }, [factFind]);

    // Single source of truth: annuity is funded from (home) sale proceeds so we don't double-count in projections.
    const isAnnuityFundedFromSaleProceeds = (record: AnnuityRecordState | null | undefined) => {
        if (!record?.purchaseAmount) return false;
        const fs = record.fundingSource;
        if (fs == null) return false;
        if (typeof fs === 'string') return String(fs).toLowerCase().includes('downsize-proceeds');
        if (typeof fs === 'object') {
            const id = (fs.id != null && String(fs.id)) || '';
            const assetId = (fs.assetId != null && String(fs.assetId)) || '';
            const label = (fs.label != null && String(fs.label).toLowerCase()) || '';
            if (id === 'bankAccounts-downsize-proceeds' || assetId === 'downsize-proceeds') return true;
            if (label.includes('unallocated sale') || label.includes('sale proceeds')) return true;
        }
        return false;
    };

   /**
    * The debt payoff as the engine takes it, or null when nothing is allocated.
    *
    * Null rather than an empty bag so every consumer can test it with a single truthiness check, and so
    * a client with no debt sends no `debtPayoff` at all rather than an empty one the engine would have
    * to recognise as a no-op.
    */
   const debtPayoffPlan = useMemo(() => {
       const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
       if (Object.keys(payments).length === 0) return null;
       const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
       return { payments, funding, repaymentBasis: debtRepaymentBasis };
   }, [debtPayments, debtFunding, debtRepaymentBasis]);

   /**
    * What each card has claimed of each asset, for the OTHER card's ceiling.
    *
    * This is the whole shared-pool mechanism: neither slider can be moved past what its sibling has
    * left, so the same dollar cannot fund a deposit and retire a debt. Doing it here, in the one place
    * that holds both bags, is what stops the two cards needing to know about each other.
    */
   const reservedForRad = useMemo(
       () => Object.fromEntries(Object.entries(radFunding).map(([key, amount]) => [key, Number(amount) || 0])),
       [radFunding],
   );
   const reservedForDebt = useMemo(
       () => Object.fromEntries(Object.entries(debtFunding).map(([key, amount]) => [key, Number(amount) || 0])),
       [debtFunding],
   );

   const simulatedResult = useMemo(() => simulateManualAgedCarePlan(factFind, {
       scenarioLivingExpenses, overallStrategy, downsizeDetails, investmentPropertyDetails, reverseMortgageDetails,
       otherStrategies, annuityRecord, radFunding, cgtOverrides, debtPayoffPlan,
       ...(secondFunding ? { secondRadFunding: secondFunding.radFunding } : {}),
       ...(existingStay ? { existingStay: { person: moverPerson, refundToday: moverDeposit.balance, retentionDaysTaken: moverDeposit.retentionDaysTaken, grandfathered: moverGrandfathered } } : {}),
       deferToEngine: radLiquidationDeferredToEngine,
   }), [factFind, radFunding, radFundingDate, careStartDate, cgtOverrides, overallStrategy, strategyInputs, downsizeDetails, scenarioLivingExpenses, reverseMortgageDetails, otherStrategies, annuityRecord, investmentPropertyDetails, debtPayoffPlan, secondFunding, existingStay, moverPerson, moverDeposit, moverGrandfathered]);

    const simulatedFactFind = simulatedResult.data;
    /** What this annuity has already drawn from each liquid asset — offered back by the funding picker. */
    const annuityDrawnFrom = simulatedResult.annuityDrawnFrom;

    /**
     * The position the Financial Impact panel describes, which is NOT the engine's opening position.
     *
     * The panel shows a normalised year — "what a full year looks like once this plan is in place", in
     * its own words — so once the plan is in place the debt is gone, whenever the payoff is dated.
     * `simulatedFactFind` deliberately leaves a dated payoff to the engine (doing both would sell the
     * funding assets twice and clear the debt twice), which is right for a PROJECTION and wrong for
     * this panel: it left an adviser clearing a $50,000 loan and watching every figure sit still.
     *
     * So the panel gets its own copy with the payoff applied. Only ever the payoff, and only when the
     * simulation skipped it — this is not a second place where strategies are assembled.
     */
    /**
     * The no-plan baseline every figure on this page is measured against — "enters care, pays the
     * full DAP, makes no strategic decisions" (owner direction, 2026-08-26).
     *
     * Aged care is a requirement, not a choice, so comparing strategies with life at home credits
     * every recommendation with the care event itself. See agedCareBaseline.ts for the two bases and
     * for the two ways this is easy to get silently wrong.
     */
    const [liveAgedCareBaseline, setAgedCareBaseline] = useState<AgedCareBaseline>(EMPTY_AGED_CARE_BASELINE);
    useEffect(() => {
        let cancelled = false;
        buildAgedCareBaseline({
            factFind,
            radInput,
            legacyYears: localLegacyYears,
            scenarioLivingExpenses,
            careStartDate,
            runwayYears: RUNWAY_HORIZON_YEARS,
            ...(secondRoomActive ? { secondRoom: { person: secondPerson, roomPrice: secondRoom.roomPrice, careStartDate: secondRoom.careStartDate || undefined, entersLater: secondEntersLater, ...(livingOnceBoth != null ? { livingExpensesOnceBoth: livingOnceBoth } : {}) } } : {}),
            ...(existingStay ? { existingStay: { person: existingStay.person, roomPrice: existingStay.roomPrice, refundToday: existingStay.refundToday, dailyPayment: existingStay.dailyPayment } } : {}),
            // Leaving a village with no plan: the exit payment is banked when it arrives.
            ...(villageExit ? { villageExit: { person: villagePerson, leaveDate: villageLeaveDate, refundDate: villageRefundDate, entitlement: villageExit.entitlement, ...(villagePaidAtMove ? { paidAtMove: true } : {}), ...(villageFees && villageFees.total > 0 ? { feesAfterLeaving: villageFees } : {}) } } : {}),
        }).then((baseline) => { if (!cancelled) setAgedCareBaseline(baseline); });
        return () => { cancelled = true; };
    }, [factFind, radInput, localLegacyYears, scenarioLivingExpenses, careStartDate, secondRoomActive, secondPerson, secondRoom.roomPrice, secondRoom.careStartDate, secondEntersLater, livingOnceBoth, existingStay, villageExit, villagePerson, villageLeaveDate, villageFees, villageRefundDate, villagePaidAtMove]);

    /*
     * ONE BENCHMARK WHILE THE ENGINE'S CARDS ARE ON SCREEN (owner-caught, 9 Oct 2026). The engine's
     * "Do Nothing" row and the walk-in baseline above are the same run by definition — same room,
     * same dates, same village payout, same horizon — and in every reproduction they agree to the
     * dollar. On the owner's screen they did not: a card read "$82,080 behind" a walk-in estate its
     * own run never produced, and the rail quoted that figure beside the cards. Whatever state the
     * browser was in, the cards must be measured against the no-plan row of THEIR run, and the rail
     * must show that same row. So, in engine mode, while the engine's row describes the inputs on
     * screen (room, entry date, village payout, horizon), it IS the baseline: its financials, its
     * first-year pension and cashflow, its projection and its estate. The live baseline carries on
     * underneath (the runway, the second stage, today's position) and takes over the moment any
     * input moves away from what the engine ran. A disagreement is logged so it can be found.
     */
    const [engineDoNothingRow, setEngineDoNothingRow] = useState<Record<string, unknown> | null>(null);
    const agedCareBaseline = useMemo<AgedCareBaseline>(() => {
        const row = engineDoNothingRow as {
            radRoomPrice?: unknown;
            strategyOptions?: { careStartDate?: unknown };
            villageExit?: { refundDate?: string; paidAtMove?: boolean } | null;
            snapshot?: { pension?: unknown; netCashflow?: unknown; legacyValue?: unknown };
            projections?: unknown;
            strategyFinancials?: unknown;
            strategySpecificAgedCareCosts?: unknown;
        } | null;
        if (strategyMode !== 'optimised' || !row || !liveAgedCareBaseline.financials) return liveAgedCareBaseline;
        // The second room and a change of home have their own stages; the live baseline carries those.
        if (secondRoomActive || existingStay) return liveAgedCareBaseline;
        const sameRoom = Math.round(Number(row.radRoomPrice) || 0) === Math.round(Number(radInput) || 0);
        const rowDate = typeof row.strategyOptions?.careStartDate === 'string' ? row.strategyOptions.careStartDate : '';
        const sameDate = rowDate === (isCompleteIsoDate(careStartDate) ? careStartDate : '');
        const rowVillage = row.villageExit || null;
        const sameVillage = villageExit
            ? !!rowVillage && rowVillage.refundDate === villageRefundDate && !!rowVillage.paidAtMove === villagePaidAtMove
            : !rowVillage;
        const rows = Array.isArray(row.projections) ? (row.projections as Record<string, unknown>[]) : [];
        const sameHorizon = rows.length === localLegacyYears;
        if (!sameRoom || !sameDate || !sameVillage || !sameHorizon || !row.strategyFinancials || !row.strategySpecificAgedCareCosts) return liveAgedCareBaseline;
        const legacy = Number(row.snapshot?.legacyValue);
        if (Number.isFinite(legacy) && liveAgedCareBaseline.legacyValue != null && Math.abs(legacy - liveAgedCareBaseline.legacyValue) > 1) {
            console.warn('[AgedCareAnalyser] the walk-in baseline and the engine\'s "Do Nothing" row disagree', { live: liveAgedCareBaseline.legacyValue, engine: legacy });
        }
        return {
            ...liveAgedCareBaseline,
            financials: row.strategyFinancials as AgedCareBaseline['financials'],
            pension: Number(row.snapshot?.pension) || 0,
            cashFlowAfterCare: Number(row.snapshot?.netCashflow) || 0,
            legacyValue: Number.isFinite(legacy) ? legacy : liveAgedCareBaseline.legacyValue,
            projections: rows,
            agedCareCosts: row.strategySpecificAgedCareCosts as AgedCareBaseline['agedCareCosts'],
        };
    }, [strategyMode, engineDoNothingRow, liveAgedCareBaseline, secondRoomActive, existingStay, radInput, careStartDate, villageExit, villageRefundDate, villagePaidAtMove, localLegacyYears]);


    const factFindForImpactPanel = useMemo(() => {
        let ff = simulatedFactFind;
        // A change of home: the panel's "after" is after the move — the refund back, the nominated
        // assets sold, the new deposit paid (the projection does it on the move day).
        if (isMover) ff = applyFacilityMoveToFactFind(asFactFind(ff), { person: moverPerson, refundToday: moverDeposit.balance, radTarget: radPaid, radFunding, cgtOverrides }).factFind as typeof ff;
        if (!debtPayoffPlan || !radLiquidationDeferredToEngine) return ff;
        return applyDebtPayoffToFactFind(ff, debtPayoffPlan, cgtOverrides).factFind;
    }, [simulatedFactFind, debtPayoffPlan, radLiquidationDeferredToEngine, cgtOverrides, isMover, moverPerson, moverDeposit.balance, radPaid, radFunding]);

    // Perform calculations based on factFind and local state
    const [simulatedFinancials, setSimulatedFinancials] = useState<FinancialPosition>({ netWorth: 0, cashFlow: 0 });
    useEffect(() => {
        let cancelled = false;
        const modellerResidency = agedCareEntrantResidencyOverrides(asFactFind(factFind));
        // The FIRST stage where the second person enters later: the other still at home.
        calculationApi.calculateFinancials(factFindForImpactPanel, {
            ...overallStrategy,
            ...strategyInputs,
            ...(secondEntersLater ? { ...modellerResidency, [secondKey]: secondRecorded } : modellerResidency),
            ...villageAtMoveOptions,
            downsizeDetails,
            radFunding,
            cgtOverrides,
            radInput,
            reverseMortgageDetails: overallStrategy.reverseMortgage ? reverseMortgageDetails : null,
            heasDetails: overallStrategy.heas ? heasDetails : null
        }).then((baseFinancials) => { if (!cancelled) setSimulatedFinancials(baseFinancials); }).catch(quietCalc('the position under this plan'));
        return () => { cancelled = true; };
        // Investment property sale: CGT is in net proceeds pool (Option A) — no separate cashflow line.
    }, [villageAtMoveOptions, factFindForImpactPanel, overallStrategy, strategyInputs, downsizeDetails, radFunding, cgtOverrides, reverseMortgageDetails, heasDetails, otherStrategies, annuityRecord, factFind, factFind.adviceFor, factFind.goals, investmentPropertyDetails, secondEntersLater, secondKey, secondRecorded]);

    const [agedCareCosts, setAgedCareCosts] = useState<AgedCareCosts>({});
    /** The second room's schedule, where both are entering care; null otherwise. */
    const [secondAgedCareCosts, setSecondAgedCareCosts] = useState<AgedCareCosts | null>(null);
    /** The second stage, where the second person enters later: the household once both are in care, and the first room's schedule then. */
    const [phaseTwo, setPhaseTwo] = useState<{ financials: FinancialPosition; firstCosts: AgedCareCosts } | null>(null);
    useEffect(() => {
        let cancelled = false;
        const modellerResidency = agedCareEntrantResidencyOverrides(asFactFind(factFind));
        const secondRadPaid = secondFunding?.funded ?? 0;
        // Where the second person enters later, `simulatedFinancials` is the FIRST stage (the other
        // still at home: the home exempt, the pension illness-separated) and the first room's
        // schedule is struck on it. The second room, and the first room once both are in care, are
        // struck on the household as it stands then (carePhases.ts).
        const firstOverrides = { ...(secondEntersLater ? { ...modellerResidency, [secondKey]: secondRecorded } : modellerResidency), ...villageAtMoveOptions };
        const planOpts = { ...overallStrategy, ...strategyInputs, downsizeDetails, radFunding, cgtOverrides, radInput, reverseMortgageDetails: overallStrategy.reverseMortgage ? reverseMortgageDetails : null, heasDetails: overallStrategy.heas ? heasDetails : null };
        calculationApi.calculateAgedCareCosts({
            factFind,
            financials: simulatedFinancials,
            radInput,
            radPaid,
            // The means are assessed on the deposit paid at entry: the village's part is still the
            // village interest then (held, and assessed as it is) — not cash paid twice.
            radPaidHousehold: radPaidAtEntry + (secondRoomActive && !secondEntersLater ? secondRadPaid : 0),
            overallStrategy: { ...overallStrategy, ...firstOverrides },
            extraServiceFeeInput,
            formatCurrency: true, // needed for agedCareCosts.calculationBreakdowns (SummaryRow tooltips below)
            ...(secondRoomActive ? { resident: leadPerson } : {}),
        }).then((costs) => {
            if (!cancelled) setAgedCareCosts({ ...costs, scenarioLivingExpenses });
        }).catch(quietCalc('the fees for the first room'));
        if (secondRoomActive) {
            const financialsBoth: Promise<FinancialPosition> = secondEntersLater
                ? calculationApi.calculateFinancials(factFindForImpactPanel, { ...planOpts, ...modellerResidency, ...(livingOnceBoth != null ? { scenarioLivingExpenses: livingOnceBoth } : {}) })
                : Promise.resolve(simulatedFinancials);
            financialsBoth.then((both) => Promise.all([
                calculationApi.calculateAgedCareCosts({
                    factFind,
                    financials: both,
                    radInput: secondRoom.roomPrice,
                    radPaid: secondRadPaid,
                    radPaidHousehold: radPaid + secondRadPaid,
                    overallStrategy: { ...overallStrategy, ...modellerResidency },
                    extraServiceFeeInput: secondRoom.extraServiceAnnual,
                    formatCurrency: true,
                    resident: secondPerson,
                }),
                secondEntersLater
                    ? calculationApi.calculateAgedCareCosts({
                          factFind,
                          financials: both,
                          radInput,
                          radPaid,
                          radPaidHousehold: radPaid + secondRadPaid,
                          overallStrategy: { ...overallStrategy, ...modellerResidency },
                          extraServiceFeeInput,
                          formatCurrency: true,
                          resident: leadPerson,
                      })
                    : Promise.resolve(null),
            ]).then(([second, firstOnceBoth]) => {
                if (cancelled) return;
                setSecondAgedCareCosts(second as AgedCareCosts);
                setPhaseTwo(firstOnceBoth ? { financials: both, firstCosts: firstOnceBoth as AgedCareCosts } : null);
            })).catch(quietCalc('the fees for the second room'));
        } else {
            setSecondAgedCareCosts(null);
            setPhaseTwo(null);
        }
        return () => { cancelled = true; };
    }, [villageAtMoveOptions, radPaidAtEntry, simulatedFinancials, factFindForImpactPanel, radInput, extraServiceFeeInput, radPaid, factFind, factFind.goals, overallStrategy, strategyInputs, downsizeDetails, radFunding, cgtOverrides, reverseMortgageDetails, heasDetails, scenarioLivingExpenses, secondRoomActive, secondRoom.roomPrice, secondRoom.extraServiceAnnual, secondFunding, secondEntersLater, leadPerson, secondKey, secondRecorded, livingOnceBoth]);

    /**
     * What the household pays for care — both rooms where both are in care. The engine keeps each
     * room's schedule apart (the projection runs them side by side); every figure a reader sees as
     * "what care costs" adds them here, and nowhere else (householdCareCosts.ts).
     */
    const householdAgedCareCosts = useMemo(
        () => withHouseholdTotals(agedCareCosts, secondRoomActive && !secondEntersLater ? secondAgedCareCosts : null),
        [agedCareCosts, secondRoomActive, secondEntersLater, secondAgedCareCosts],
    );
    /** The plan's second stage, where the second person enters later: both rooms from then. */
    const phaseTwoHousehold = useMemo(
        () => (secondEntersLater && phaseTwo ? withHouseholdTotals(phaseTwo.firstCosts, secondAgedCareCosts) : null),
        [secondEntersLater, phaseTwo, secondAgedCareCosts],
    );
    /** The no-plan second room IN THE FIRST YEAR, for the baseline's own totals: none where it enters later. */
    const baselineSecondCosts = secondRoomActive && !secondEntersLater ? ((agedCareBaseline.secondAgedCareCosts as AgedCareCosts | null | undefined) ?? secondAgedCareCosts) : null;

    /** Staying put: the current home's schedule, with the daily payment the resident actually pays (a mover's baseline, and the run's opening schedule). */
    const [stayPutCosts, setStayPutCosts] = useState<AgedCareCosts | null>(null);
    useEffect(() => {
        if (!existingStay) { setStayPutCosts(null); return; }
        let cancelled = false;
        const modellerResidency = agedCareEntrantResidencyOverrides(asFactFind(factFind));
        calculationApi.calculateFinancials(factFind, { ...modellerResidency, scenarioLivingExpenses })
            .then((fin) => calculationApi.calculateAgedCareCosts({
                factFind,
                financials: fin,
                radInput: existingStay.roomPrice,
                radPaid: existingStay.refundToday,
                overallStrategy: { ...modellerResidency },
                extraServiceFeeInput: 0,
                formatCurrency: true,
                resident: existingStay.person,
            }))
            .then((costs) => { if (!cancelled) setStayPutCosts(withDailyPaymentOverride({ ...(costs as AgedCareCosts), scenarioLivingExpenses }, existingStay.dailyPayment)); })
            .catch(quietCalc('the stay-put fees'));
        return () => { cancelled = true; };
    }, [factFind, existingStay, scenarioLivingExpenses]);
    /** The schedule the projection OPENS on: the current home for a mover (the move swaps it), the plan's room otherwise. */
    const runSchedule = isMover && stayPutCosts ? stayPutCosts : agedCareCosts;


    /** What the projection is told about the second resident — null unless both are entering. */
    const secondResidentPlan = useMemo(
        () => (secondRoomActive && secondAgedCareCosts && secondFunding
            ? { person: secondPerson, ...(secondRoom.careStartDate ? { careStartDate: secondRoom.careStartDate } : {}), radFunding: secondFunding.radFunding, agedCareCosts: secondAgedCareCosts, ...(livingOnceBoth != null ? { livingExpensesOnceBoth: livingOnceBoth } : {}) }
            : null),
        [secondRoomActive, secondAgedCareCosts, secondFunding, secondRoom.careStartDate, secondPerson, livingOnceBoth],
    );

    // Update parent strategyOptions when local state changes
    useEffect(() => {
        const updatedStrategyOptions = {
            ...overallStrategy,
            ...strategyInputs,
            ...agedCareEntrantResidencyOverrides(asFactFind(factFind)),
            downsizeDetails,
            scenarioLivingExpenses,
            radFunding,
            cgtOverrides,
            radInput,
            extraServiceFeeInput,
            // Surfaced so consumers of the parent strategyOptions (e.g. the SOA) see the same entry date
            // the projections ran with. No anchor override goes with it: the projection starts today.
            ...(careStartDate ? { careStartDate } : {}),
            // The deposit arrangement travels the same way: the Suggestion Engine reads it off these
            // options so its candidates draw on the deposit under the same agreement the manual plan does.
            radDrawdown,
            // The fee arrangements travel the same way, so the Suggestion Engine prices its candidates
            // on the rules the manual plan is on (and can show what opting in would be worth).
            ...(residentialFeeArrangements ? { residentialFeeArrangements } : {}),
            // The second room, likewise: the Suggestion Engine prices it in every candidate.
            ...(secondRoomActive ? { secondRoom: { person: secondPerson, roomPrice: secondRoom.roomPrice, ...(secondRoom.careStartDate ? { careStartDate: secondRoom.careStartDate } : {}), extraServiceAnnual: secondRoom.extraServiceAnnual } } : {}),
            ...(secondResidentPlan ? { secondResident: secondResidentPlan } : {}),
        };
        setStrategyOptions(updatedStrategyOptions);
    }, [overallStrategy, strategyInputs, downsizeDetails, scenarioLivingExpenses, radFunding, cgtOverrides, radInput, extraServiceFeeInput, careStartDate, radDrawdown, residentialFeeArrangements, factFind, setStrategyOptions, secondRoomActive, secondRoom, secondResidentPlan]);

    // Investment property sale: estimated CGT and net proceeds for insight panel (matches InvestmentPropertyStrategyAllocator / Section 11)
    const investmentPropertySaleSummary = useMemo(() => {
        if (!overallStrategy.sellInvestmentProperty || !investmentPropertyDetails) return null;
        const summary = getInvestmentPropertyTaxSummary(asFactFind(factFind), investmentPropertyDetails);
        if (!summary) return null;
        const { estimatedCGT, netProceeds, salePrice, sellingCosts, isPreCGT } = summary;
        return { estimatedCGT, netProceeds, salePrice, sellingCosts, isPreCGT };
    }, [overallStrategy.sellInvestmentProperty, investmentPropertyDetails, factFind]);

    const projectionStrategyOptions = useMemo(() => {
        const modellerResidency = agedCareEntrantResidencyOverrides(asFactFind(factFind));
        return {
            ...overallStrategy,
            ...strategyInputs,
            ...modellerResidency,
            downsizeDetails,
            scenarioLivingExpenses,
            radFunding,
            cgtOverrides,
            radInput,
            reverseMortgageDetails: overallStrategy.reverseMortgage ? reverseMortgageDetails : null,
            heasDetails: overallStrategy.heas ? heasDetails : null,
            otherStrategies: otherStrategies,
            annuityRecord: otherStrategies.annuity ? annuityRecord : null,
            inflationAssumptionPct: DEFAULT_INFLATION_ASSUMPTION_PCT,
            investmentPropertySaleSummary: investmentPropertySaleSummary || null,
            // A future entry date defers the CARE COSTS, not the projection: the run still starts from
            // today, so the years before entry are modelled (the client at home, on their own living
            // expenses, paying nothing for care) and the legacy horizon is the same length whatever
            // entry date is chosen. Blank = care is already under way.
            ...(careStartDate ? { careStartDate } : {}),
            radDrawdown,
            ...(residentialFeeArrangements ? { residentialFeeArrangements } : {}),
            // Hands the liquidation to the engine, which sells on this day and holds the proceeds in
            // the bank until the deposit is paid on entry. Defaults to the entry date rather than being
            // absent, so a blank "sell earlier" field means what the screen says it means. It is only
            // sent when `simulatedFactFind` has correspondingly NOT applied the sale itself — both read
            // `effectiveRadFundingDate` precisely so they cannot disagree.
            ...(effectiveRadFundingDate ? { radFundingDate: effectiveRadFundingDate } : {}),
            // The Debt Strategy card's plan. Sent ONLY with a date — the same contract the RAD sale
            // follows: a date means the engine owns the event, no date means the fact-find arrived with
            // it already applied. `simulatedFactFind` reads the same `effectiveRadFundingDate`, so the
            // two can never both do it (which would clear the debt twice) or both skip it.
            ...(debtPayoffPlan && effectiveRadFundingDate
                ? { debtPayoff: { ...debtPayoffPlan, date: effectiveRadFundingDate } }
                : {}),
            // The second resident: their own entry, deposit and schedule, run beside the first.
            ...(secondResidentPlan ? { secondResident: secondResidentPlan } : {}),
            // A change of home: no entry date (care is under way); the move is dated instead.
            ...(isMover ? { careStartDate: undefined, radFundingDate: undefined, facilityMove: { person: moverPerson, moveDate: careStartDate || todayIsoDate(), agedCareCosts, radTarget: radPaid, radFunding, grandfathered: moverGrandfathered } } : {}),
            // Leaving a village: the exit payment on its date, and what of it goes to the room.
            ...(villageExitPlan ? { villageExit: villageExitPlan } : {}),
        };
    }, [
        isMover, moverPerson, moverGrandfathered, radPaid, radFunding, agedCareCosts, careStartDate, villageExitPlan,
        secondResidentPlan,
        debtPayoffPlan,
        overallStrategy,
        strategyInputs,
        downsizeDetails,
        scenarioLivingExpenses,
        radFunding,
        cgtOverrides,
        radInput,
        reverseMortgageDetails,
        heasDetails,
        otherStrategies,
        annuityRecord,
        factFind,
        investmentPropertySaleSummary,
        careStartDate,
        radFundingDate,
        radDrawdown,
        residentialFeeArrangements,
    ]);

    const [projections, setProjections] = useState<ProjectionYear[]>([]);
    useEffect(() => {
        let cancelled = false;
        calculationApi.runProjections(simulatedFactFind, projectionStrategyOptions, runSchedule, localLegacyYears, {
            debug: false,
        }).then((proj) => { if (!cancelled) setProjections(proj); }).catch(quietCalc('the projection'));
        return () => { cancelled = true; };
    }, [simulatedFactFind, agedCareCosts, localLegacyYears, projectionStrategyOptions]);

    /*
     * A SECOND, LONGER run, for "how long the money lasts" only.
     *
     * "When does the money run out" is an unbounded question and must not be chained to the Project
     * Legacy Over control, which the reader set for a different purpose — the estate. Asked over the
     * default five years the funding helper can only ever answer "lasts your full 5-year plan", and
     * it says that for a household whose savings actually run low in year 13. That is not a missing
     * number, it is a falsely comforting one, which is worse.
     *
     * The engine already knows the answer: every projection row carries `unfundedShortfall` ("the
     * first year this turns positive is the year the client's money runs out"), and summariseFunding
     * turns the drawdown rows into the year the bank runs low and the year borrowing starts. Until
     * now nothing on any screen read either.
     *
     * The cost is one extra engine call, so it is gated on there being a room price to model at all.
     */
    const [runwayProjections, setRunwayProjections] = useState<ProjectionYear[]>([]);
    useEffect(() => {
        if (!(Number(radInput) > 0)) { setRunwayProjections([]); return undefined; }
        let cancelled = false;
        calculationApi.runProjections(simulatedFactFind, projectionStrategyOptions, runSchedule, RUNWAY_HORIZON_YEARS, {
            debug: false,
        }).then((proj) => { if (!cancelled) setRunwayProjections(proj); }).catch(quietCalc('the projection'));
        return () => { cancelled = true; };
    }, [simulatedFactFind, agedCareCosts, projectionStrategyOptions, radInput]);

    /* The runway itself is struck further down, once the panel's source plan is known — see
       `panelRunwayRows`. Only the long-horizon projection it may draw on is fetched here. */


    /**
     * The projections modal can be switched to CALENDAR-year rows (1 January → 31 December) instead
     * of financial-year ones. That is a different engine run — the periods are cut differently and
     * the engine's plugin phases execute in a rotated order — so it is fetched separately and only
     * on demand, leaving the financial-year `projections` above (which the SOA, the impact panel and
     * the suggestion engine all read) untouched.
     *
     * The mode is the MODAL's sticky preference, so we seed from the same store it reads (rather than
     * always starting at financial year) and the modal opens showing rows already cut the user's way.
     */
    const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
    const [calendarYearProjections, setCalendarYearProjections] = useState<ProjectionYear[] | null>(null);
    useEffect(() => {
        if (modalPeriodMode !== 'calendar_year') { setCalendarYearProjections(null); return; }
        // Only while the modal is open: with the mode now remembered across sessions, a stored
        // 'calendar_year' would otherwise fire this second full engine run on every page load,
        // whether or not the projections modal is ever opened.
        if (!showProjections) return;
        let cancelled = false;
        // Cleared first so an input change (a different strategy, say) re-shows the hourglass rather
        // than leaving the previous run's calendar rows on screen as if they were current.
        setCalendarYearProjections(null);
        calculationApi.runProjections(
            simulatedFactFind,
            { ...projectionStrategyOptions, projectionPeriodMode: 'calendar_year' },
            runSchedule,
            localLegacyYears,
            { debug: false },
        ).then((proj) => { if (!cancelled) setCalendarYearProjections(proj); }).catch(quietCalc('the projection'));
        return () => { cancelled = true; };
    }, [showProjections, modalPeriodMode, simulatedFactFind, agedCareCosts, localLegacyYears, projectionStrategyOptions]);

    // Memoised so these heavy pure calcs don't re-run on every render — notably when selecting a
    // suggestion-engine strategy (which changes selectedStrategy/savedStrategies but NOT these
    // inputs), so the Insight Panel updates without re-running calculateFinancials/buildAnnuityImpact.
    // Purely a performance optimisation — identical inputs produce identical output.
    const [insightPanelNetWorth, setInsightPanelNetWorth] = useState<number>(0);
    useEffect(() => {
        let cancelled = false;
        calculationApi.calculateFinancials({
            ...factFind,
            expenses: { ...factFind.expenses, livingExpenses: scenarioLivingExpenses }
        }, { ...strategyOptions, ...agedCareEntrantResidencyOverrides(asFactFind(factFind)) }).then((r) => {
            if (!cancelled) setInsightPanelNetWorth(r?.netWorth);
        }).catch(quietCalc('the comparison position'));
        return () => { cancelled = true; };
    }, [factFind, scenarioLivingExpenses, strategyOptions]);

    const insightPanelAnnuityRecord = useMemo(() => {
        if (!otherStrategies.annuity || !annuityRecord || !annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) {
            return null;
        }
        try {
            const mappedAnnuityRecord = { ...annuityRecord };
            if (!mappedAnnuityRecord.age && factFind?.client?.dob) {
                mappedAnnuityRecord.age = calculateAge(factFind.client?.dob);
            }
            if (mappedAnnuityRecord.age && mappedAnnuityRecord.age > 0) {
                return buildAnnuityImpact(asFactFind(factFind), mappedAnnuityRecord);
            }
        } catch (error) {
            console.error('Error building annuity for Insight Panel:', error);
        }
        return null;
    }, [otherStrategies.annuity, annuityRecord, factFind]);

    // Stable object identity for the Suggestion Engine panel's agedCareCosts prop. Without this the
    // inline {...} object got a new reference on EVERY render, so the SE panel's analysis useEffect
    // (deps include agedCareCosts) re-ran the full strategy analysis on unrelated re-renders —
    // notably when selecting a strategy — causing a multi-second lag. Now it only changes when the
    // costs actually change, so analysis still re-runs when it should, but not on select.
    const suggestionEngineAgedCareCosts = useMemo(
        () => ({ ...agedCareCosts, radAmount: radInput, scenarioLivingExpenses }),
        [agedCareCosts, radInput, scenarioLivingExpenses]
    );
    /**
     * What the Suggestion Engine reads from the case (and nothing more): the entry date, whether fees
     * may be drawn from the deposit, the fee arrangements, and the second room where both of a couple
     * are entering care. Until 7 Oct 2026 only the entry date reached it — the second room, the
     * drawdown choice and the fee arrangements were put on the page-level bag that this panel never
     * received, so every candidate was priced on one room (owner-caught).
     */
    const suggestionEngineStrategyOptions = useMemo<Record<string, unknown>>(
        () => ({
            ...(careStartDate ? { careStartDate } : {}),
            radDrawdown,
            ...(residentialFeeArrangements ? { residentialFeeArrangements } : {}),
            ...(secondRoomActive
                ? { secondRoom: { person: secondPerson, roomPrice: secondRoom.roomPrice, ...(secondRoom.careStartDate ? { careStartDate: secondRoom.careStartDate } : {}), extraServiceAnnual: secondRoom.extraServiceAnnual, entersLater: secondEntersLater, ...(livingOnceBoth != null ? { livingExpensesOnceBoth: livingOnceBoth } : {}) } }
                : {}),
            // A change of home: every candidate but "stay put" moves on this date and pays the new
            // room from the refund first; the run opens in the current home on its schedule.
            ...(isMover && existingStay && stayPutCosts
                ? { facilityMove: { person: moverPerson, moveDate: careStartDate || todayIsoDate(), newRoomPrice: radInput, extraServiceAnnual: extraServiceFeeInput, refundToday: moverDeposit.balance, currentRoomPrice: existingStay.roomPrice, currentDapAnnual: existingStay.dailyPayment * 365, grandfathered: moverGrandfathered, currentSchedule: stayPutCosts } }
                : {}),
            // Leaving a village: every candidate puts the exit payment to the room first when it arrives.
            ...(villageExit ? { villageExit: { person: villagePerson, leaveDate: villageLeaveDate, refundDate: villageRefundDate, entitlement: villageExit.entitlement, ...(villagePaidAtMove ? { paidAtMove: true } : {}), ...(villageFees && villageFees.total > 0 ? { feesAfterLeaving: villageFees } : {}) } } : {}),
        }),
        [careStartDate, radDrawdown, residentialFeeArrangements, secondRoomActive, secondPerson, secondRoom.roomPrice, secondRoom.careStartDate, secondRoom.extraServiceAnnual, secondEntersLater, livingOnceBoth, isMover, existingStay, stayPutCosts, moverPerson, radInput, extraServiceFeeInput, moverDeposit.balance, moverGrandfathered, villageExit, villagePerson, villageLeaveDate, villageFees, villageRefundDate, villagePaidAtMove],
    );

    /**
     * The plain-words recap, built once here and rendered inside "Understanding your plan".
     *
     * It is the SAME component with the SAME props it had when it sat inline on the page — only its
     * position changed. Kept as a node rather than mounted twice: it runs its own baseline lookup,
     * and two mounts would mean two sets of engine calls for one answer.
     */
    const planRecapNode = (
        <StrategyPlanRecap
            factFind={factFind}
            overallStrategy={overallStrategy}
            investmentPropertyDetails={investmentPropertyDetails}
            downsizeDetails={downsizeDetails}
            reverseMortgageDetails={reverseMortgageDetails}
            radFunding={radFunding}
            cgtOverrides={cgtOverrides}
            radInput={radInput}
            radPaid={radPaid}
            agedCareCosts={householdAgedCareCosts}
            simulatedFinancials={simulatedFinancials}
            strategyInputs={strategyInputs}
            // No heading: "Understanding your plan" titles the modal and "Your plan summary" heads
            // the panel beside it, so the agreed default would appear three times on one screen.
            eyebrow={null}
            // ONE baseline for the summary and the panel — see StrategyPlanRecap.
            sharedBaseline={agedCareBaseline.financials ? { pension: agedCareBaseline.pension, cashFlowAfterCare: agedCareBaseline.cashFlowAfterCare } : null}
            planRunway={runwayFactsOf(runwayProjections)}
            baselineLegacy={agedCareBaseline.legacyValue}
            planLegacy={projections.length > 0 ? (projections[projections.length - 1]?.netWorth ?? null) : null}
            legacyYears={localLegacyYears}
            heasDetails={overallStrategy.heas ? heasDetails : null}
            debtPayoffPlan={debtPayoffPlan}
            otherStrategies={otherStrategies}
            annuityRecord={annuityRecord}
            extraServiceFeeInput={extraServiceFeeInput}
            secondRoom={secondRoomActive ? { name: secondName, leadName: firstName, roomPrice: secondRoom.roomPrice, radPaid: secondFunding?.funded ?? 0, dailyTotal: secondAgedCareCosts?.totalDailyCost ?? null, funding: describeFundingKeys(factFind, secondFunding?.radFunding), paidFromLabel: secondEntersLater ? phaseNames.fromLabel : undefined } : null}
            phaseTwo={phaseTwoHousehold ? { names: phaseNames, firstDaily: agedCareCosts.totalDailyCost || 0, phaseTwoDaily: phaseTwoHousehold.totalDailyCost || 0 } : null}
            facilityMove={isMover && currentHomeFacts ? movePiece({
                name: currentHomeFacts.name,
                facilityName: currentHomeFacts.facilityName,
                moveLabel: careStartDate ? phaseFromLabel(careStartDate) : 'the coming weeks',
                newRoomPrice: radInput,
                deposit: Math.min(radInput, radPaid),
                fromRefund: Math.min(moverDeposit.balance, Math.min(radInput, radPaid)),
                fromSale: Math.max(0, Math.min(radInput, radPaid) - Math.min(moverDeposit.balance, Math.min(radInput, radPaid))),
                soldPhrase: fundingPhrase(describeFundingKeys(factFind, radFunding), formatCurrency),
                dapPerDay: Number(agedCareCosts.dailyAccommodationPayment) || 0,
                grandfathered: moverGrandfathered,
                retentionDaysTaken: moverDeposit.retentionDaysTaken,
            }) : null}
            villageExit={villageFacts ? villagePiece({
                name: villageFacts.name,
                leaveLabel: careStartDate ? phaseFromLabel(careStartDate) : 'the coming weeks',
                entitlement: villageFacts.entitlement,
                refundMonths: villageFacts.refundMonths,
                refundDate: villageFacts.refundDate,
                roomPrice: radInput,
                deposit: Math.min(radInput, radPaid),
                fromVillage: radPaidBreakdown.fromVillage,
                fromSavings: Math.max(0, Math.min(radInput, radPaid) - radPaidBreakdown.fromVillage),
                soldPhrase: fundingPhrase(describeFundingKeys(factFind, radFunding), formatCurrency),
                toBank: Math.max(0, villageFacts.entitlement - radPaidBreakdown.fromVillage),
                interim: villageInterim,
                feesAfterLeaving: villageFees,
                paidAtMove: villagePaidAtMove,
                dapPerDay: Number(agedCareCosts.dailyAccommodationPayment) || 0,
            }) : null}
            annuityFromSaleProceeds={
                otherStrategies.annuity && isAnnuityFundedFromSaleProceeds(annuityRecord)
                    ? parseFloat(String(annuityRecord?.purchaseAmount)) || 0
                    : 0
            }
        />
    );


    /**
     * The panel's "In brief" line.
     *
     * Built from figures ALREADY ON SCREEN in the panel's own tiles — the baseline and the plan,
     * subtracted exactly as the tiles subtract them — so the sentence cannot disagree with the
     * numbers directly above it. Nothing here re-reads the fact-find, and the cashflow subtraction
     * is the same `cashFlow − totalDailyCost×365` that FinancialImpactPanel applies.
     */
    /*
     * IN SUGGESTION ENGINE MODE THE RAIL IS THE WALK-IN POSITION, ALWAYS (owner, 2026-09-22).
     *
     * Each card already carries everything the rail used to repeat for a selected plan — its figures
     * with the walk-in figure and the change under each tile, Explain this, Deep Dive with the
     * projections, and the advice document. Two places for one plan was the duplication the review
     * kept finding. So the rail now holds the one thing the cards do not: doing nothing, for
     * comparison, unblurred and labelled, and it never changes while the reader selects and
     * reselects. (The earlier design blurred it while browsing and swapped it to the selected plan on
     * Select; the blur read as a fault, and the swap made the rail and the card say the same thing
     * twice. If the walk-in rail proves too much on screen, the blur is the fallback — owner.)
     *
     * Manual Control is unchanged: there is no card, so the rail is the plan, with the baseline in
     * grey beside each figure.
     */
    const railIsWalkIn = strategyMode === 'optimised' || !strategyModeChosen;

    /*
     * The rail no longer reads the selected engine strategy at all (the block that unwrapped the
     * saved-strategy envelope and priced the selected row lived here until 2026-09-22). In engine
     * mode the rail is the walk-in position; the card the client selected is where that plan is
     * priced, explained and taken to the advice document. A panel that named one plan and priced
     * another was the worst failure this screen had; a panel that names no plan cannot have it.
     */

    // The rail's one source per mode: the walk-in position in engine mode (the baseline this page
    // measures every card against; the current position only if that could not be computed), the
    // modelled plan in Manual Control.
    const panelFirstCosts = railIsWalkIn
        ? ((agedCareBaseline.agedCareCosts as typeof agedCareCosts | null) ?? agedCareCosts)
        : agedCareCosts;
    /** The second room's schedule in the same mode, where both are in care; null otherwise. */
    const panelSecondCosts: AgedCareCosts | null = secondRoomActive && !secondEntersLater ? (railIsWalkIn ? baselineSecondCosts : secondAgedCareCosts) : null;
    /** The second stage on the rail, where the second person enters later: each room's schedule from then. */
    const panelPhaseTwo: { firstCosts: AgedCareCosts; secondCosts: AgedCareCosts | null } | null = !secondEntersLater
        ? null
        : railIsWalkIn
            ? (agedCareBaseline.phase2 ? { firstCosts: agedCareBaseline.phase2.agedCareCosts as AgedCareCosts, secondCosts: agedCareBaseline.phase2.secondAgedCareCosts as AgedCareCosts | null } : null)
            : (phaseTwo ? { firstCosts: phaseTwo.firstCosts, secondCosts: secondAgedCareCosts } : null);
    const panelPhaseTwoDaily = panelPhaseTwo ? householdDailyCost(panelPhaseTwo.firstCosts, panelPhaseTwo.secondCosts) : 0;
    // The household's totals (both rooms) over the first room's fields, so every tile, sentence and
    // breakdown on the rail describes what the two of them pay.
    const panelCosts = withHouseholdTotals(panelFirstCosts, panelSecondCosts);
    /** One room's fee rows, as the cost disclosure lists them — the same rows for either room. */
    const feeRows = (c: AgedCareCosts) => (
        <>
            <SummaryRow label="Basic Daily Fee" value={c.basicDailyFee} breakdown={c.calculationBreakdowns?.bdf} />
            <SummaryRow label="Accommodation (DAP)" value={c.dailyAccommodationPayment} breakdown={c.calculationBreakdowns?.dap} />
            {c.feeArrangements === '2014' ? (
                <SummaryRow label="Means-tested care fee (no worse off)" value={c.dailyMTCF} breakdown={c.calculationBreakdowns?.mtcf} />
            ) : (
                <>
                    <SummaryRow label="Care Contribution (NCCC)" value={c.dailyNCCC} breakdown={c.calculationBreakdowns?.nccc} />
                    <SummaryRow label="Hotelling Supplement Contribution (HSC)" value={c.dailyHSC} breakdown={c.calculationBreakdowns?.hsc} />
                </>
            )}
            <SummaryRow label="Optional Extra Services" value={c.dailyExtraServiceFee} />
        </>
    );
    const panelFinancials = railIsWalkIn ? (agedCareBaseline.financials ?? simulatedFinancials) : simulatedFinancials;
    const panelProjections = railIsWalkIn && agedCareBaseline.projections.length
        ? (agedCareBaseline.projections as typeof projections)
        : projections;

    /**
     * What the plan summary is describing, in the client's words — or null when it is describing
     * nothing yet, in which case the panel says so and shows the walk-in position.
     *
     * Built from the strategy flags that are actually ON rather than from a stored label, so it
     * cannot drift from what is being modelled. A panel headed with a name that no longer matches
     * the numbers underneath it would be worse than one with no name at all.
     */
    const planSummaryStrategyName = useMemo<string | null>(() => {
        /*
         * THE NAME AND THE FIGURES COME FROM ONE SOURCE (owner-reported, 2026-08-31; settled
         * 2026-09-22). Twice this rail named a selected engine strategy while pricing something else.
         * It now never names one: in engine mode it is the walk-in position and the card carries the
         * plan, so the name and every figure switch on the one flag above.
         */
        // Engine mode: the rail is the walk-in position and names no plan — the card names it.
        if (railIsWalkIn) return null;
        const parts: string[] = [];
        if (overallStrategy.downsizeHome) parts.push('Sell the family home');
        if (overallStrategy.rentHome) parts.push('Rent the family home');
        if (overallStrategy.reverseMortgage) parts.push('Take a reverse mortgage');
        if (overallStrategy.heas) parts.push('Use the Home Equity Access Scheme');
        if (radPaid > 0.5) {
            /*
             * NAME THE AMOUNT (owner, 2026-08-31). "Part-pay the room" describes the shape of the
             * decision but not the decision: a $50,000 RAD and a $450,000 RAD are the same phrase and
             * wildly different plans. The heading sits directly above figures driven by that number,
             * so leaving it out made the panel harder to read than it needed to be.
             */
            parts.push(
                radPaid >= (Number(radInput) || 0) - 0.5
                    ? 'Pay the room in full'
                    : `Part-pay the room (${formatCurrency(radPaid)})`,
            );
        }
        /*
         * THE OTHER STRATEGIES COUNT AS A PLAN TOO (seen on screen, 2026-08-28).
         *
         * This read only the property and room levers, so a client who entered a $200,000 gift and a
         * $30,000 funeral bond was shown a panel headed "No plan yet — the walk-in position" and
         * told "this IS the baseline" — directly above tiles reporting a $12,190 Age Pension rise
         * and a $197,982 fall in the estate, both caused by the plan the heading said did not exist.
         *
         * A panel cannot report the effects of a plan and deny there is one. These three move the
         * means test as surely as selling the home does; they were simply added to the module later
         * than the naming was written.
         */
        const giftingAmount = Number(otherStrategies.giftingAmount) || 0;
        if (otherStrategies.gifting && giftingAmount > 0) parts.push('Gift to loved ones');
        const funeralBondAmount = Number(otherStrategies.funeralBondAmount) || 0;
        if (otherStrategies.funeralBonds && funeralBondAmount > 0) parts.push('Buy a funeral bond');
        if (otherStrategies.annuity && (Number(annuityRecord?.purchaseAmount) || 0) > 0) parts.push('Buy a lifetime annuity');
        if (debtPayoffPlan) parts.push('Clear debt');

        if (!parts.length) return null;
        const [lead, ...rest] = parts;
        return rest.length
            ? `${lead}, ${rest.map((p) => p.charAt(0).toLowerCase() + p.slice(1)).join(', ')}`
            : lead;
    }, [strategyMode, selectedStrategy, overallStrategy, radPaid, radInput, otherStrategies, annuityRecord, debtPayoffPlan]);
    /*
     * The runway runs on the SELECTED strategy's own projections when there is one, which are the
     * plan-period rows the card's funding note also reads — so the two cannot disagree about when
     * the money runs out. The horizon is stated either way, so a five-year answer is never mistaken
     * for a twenty-five-year one.
     */
    // In engine mode the rail is the walk-in position, so it reads the same 25-year no-plan rows
    // step 2 reads — the two runways are then one runway, and can never quote different years.
    const panelRunwayRows = railIsWalkIn && agedCareBaseline.runwayProjections.length
        ? (agedCareBaseline.runwayProjections as typeof runwayProjections)
        : runwayProjections;
    const panelRunwayYears = RUNWAY_HORIZON_YEARS;

    const runway = useMemo(
        () => planRunwayCopy(panelRunwayRows as never, panelRunwayYears),
        [panelRunwayRows, panelRunwayYears],
    );
    /**
     * The rail's runway note, in step 2's words: which account runs low when, what takes over, and
     * when the savings are gone — read off the rows the rail's runway is struck on. Borrowing against
     * the home is the last clause, not the headline (owner, 22 Sep 2026: "at the client's age that's
     * highly unlikely to happen"). The generic flag is the fallback only when no pool can be named.
     */
    const railRunwayFlag = useMemo(
        () => moneySequenceSentence((panelRunwayRows as unknown as RunwayRow[]) || [], railIsWalkIn ? factFind : simulatedFactFind, runway),
        [panelRunwayRows, railIsWalkIn, factFind, simulatedFactFind, runway],
    );
    /** Step 2's runway: the no-plan projection over the runway horizon, whatever is switched on in step 3. */
    const walkInRunway = useMemo(
        () => (agedCareBaseline.runwayProjections.length ? planRunwayCopy(agedCareBaseline.runwayProjections as never, RUNWAY_HORIZON_YEARS) : null),
        [agedCareBaseline.runwayProjections],
    );

    const planInBrief = useMemo<React.ReactNode | null>(() => {
        const basePension = agedCareBaseline.pension;
        const baseCashFlow = agedCareBaseline.cashFlowAfterCare;
        // No baseline, no sentence. A "difference" quoted against a benchmark we could not compute
        // is the exact failure this module spent a week removing — say nothing instead.
        if (!planSummaryStrategyName || basePension == null || baseCashFlow == null) return null;
        const planLegacy = panelProjections.length > 0 ? panelProjections[panelProjections.length - 1]?.netWorth ?? null : null;
        const dailyDap = Number(panelCosts?.dailyAccommodationPayment) || 0;
        // The plan's own annual cashflow AFTER care fees — the level, which the In Brief now states
        // alongside the movement so "better by X" cannot be mistaken for "comfortable".
        const cashflowAfter = (panelFinancials?.cashFlow ?? 0) - (Number(panelCosts?.totalDailyCost) || 0) * 365;
        return (
            <RecapClauses
                clauses={buildPlanRecapClauses({
                    roomFullyPaid: dailyDap <= 0.005,
                    dapAnnual: dailyDap * 365,
                    careAnnual: (Number(panelCosts?.totalDailyCost) || 0) * 365,
                    pensionDelta: (panelFinancials?.socialSecurity ?? 0) - basePension,
                    cashflowDelta: cashflowAfter - baseCashFlow,
                    cashflowAfter,
                    legacyDelta:
                        planLegacy != null && agedCareBaseline.legacyValue != null
                            ? planLegacy - agedCareBaseline.legacyValue
                            : null,
                    legacyYears: Number(localLegacyYears) || 0,
                })}
            />
        );
    }, [planSummaryStrategyName, agedCareBaseline, panelProjections, panelCosts, panelFinancials, localLegacyYears]);

    /*
     * WIDTH AND BREAKPOINT (owner-reported 2026-08-26: "when the screen resolution is set to 100%
     * some fields dont work well").
     *
     * The old pair was `max-w-7xl` (1280px) with the two-pane split on `xl:` — which is ALSO 1280px.
     * So the second column appeared at exactly the width the container stopped growing, and it
     * appeared far too early: at a 1280px viewport the sidebar (256px) and page padding (64px) take
     * 320px before the grid sees anything, leaving 960px. Split two-thirds, that is a 619px working
     * column, which is what was squeezing the inputs.
     *
     * The fix is NOT to delay the split — that was the first attempt, and owner screenshots killed
     * it: a 1920 screen at 100% zoom is about a 1330px viewport, so a 2xl (1536px) split meant the
     * plan summary simply never appeared at the resolution the product is actually used at. It only
     * returned at 75% zoom. Withholding the panel from everyone at 100% is a worse bug than the one
     * being fixed.
     *
     * So the split stays at `xl:` and the fix goes where the problem actually was — the grids INSIDE
     * each pane, which now wait for `2xl:` before subdividing. The panes and their contents move on
     * different breakpoints on purpose:
     *
     *   xl  (1280px+) two panes, 60/40 rather than 67/33 — the right pane carries the plan summary.
     *   2xl (1536px+) the panes are finally wide enough for their grids to go multi-column.
     *
     * Between the two, every field simply runs the full width of its pane, which always reads well.
     * The container also runs to 1520px so a 1920 screen is used rather than letterboxed at 1280.
     */
    return (
        <div className="max-w-[1520px] mx-auto">
            {showPlanExplanation && (
                <PlanExplanationModal
                    strategyName={planSummaryStrategyName}
                    onClose={() => setShowPlanExplanation(false)}
                >
                    {planRecapNode}
                </PlanExplanationModal>
            )}
            {showProjections && (
                <ProjectionsModal
                    onClose={() => { setShowProjections(false); setProjectionsSource('plan'); }}
                    // Until the calendar-year run lands the financial-year rows stay on screen, under
                    // the modal's hourglass, rather than the charts and table emptying out.
                    projections={
                        projectionsSource === 'walkIn'
                            ? (agedCareBaseline.projections as typeof projections)
                            : modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : projections
                    }
                    onPeriodModeChange={setModalPeriodMode}
                    isReloading={projectionsSource === 'plan' && modalPeriodMode === 'calendar_year' && !calendarYearProjections}
                    agedCareCosts={projectionsSource === 'walkIn' ? ((agedCareBaseline.agedCareCosts as typeof agedCareCosts | null) ?? agedCareCosts) : agedCareCosts}
                    strategy={{ radAmount: projectionsSource === 'walkIn' ? 0 : radPaid }}
                    factFind={projectionsSource === 'walkIn' ? factFind : simulatedFactFind}
                    initialFinancials={initialFinancials}
                    currentFinancials={projectionsSource === 'walkIn' ? (agedCareBaseline.financials ?? simulatedFinancials) : simulatedFinancials}
                />
            )}
            {showFacilitySearch && (
                <FacilitySearchModal
                    facilities={mockFacilities}
                    onSelect={(facility) => {
                        if (facilityTarget === 'second') {
                            setSelectedSecondFacility(facility);
                            setSecondRoom((r) => ({ ...r, roomPrice: facility.radPrice }));
                        } else {
                            setSelectedFacility(facility);
                            setRadInput(facility.radPrice);
                        }
                        setShowFacilitySearch(false);
                    }}
                    onClose={() => setShowFacilitySearch(false)}
                />
            )}
            <h1 className="text-4xl font-bold text-gray-900 mb-2"><GradientText>Aged Care Funding Analyser</GradientText></h1>
            <p className="text-lg text-gray-600 max-w-4xl">
                {isMover ? <>Three short steps: what the new home costs, what the move means for {enteringFirstName}, and the ways to pay for it.</> : <>Three short steps: what care costs, what it means for {enteringFirstName}, and the ways to close the gap.</>}
            </p>
            <AgedCareSteps step={step} onStep={goToStep} clientName={enteringFirstName} moving={isMover} />

            {/* Five columns rather than three, so the split is 60/40 instead of 67/33: the plan
                summary now carries the costs, the four impact tiles and the narrative, and 33% of
                1280px could never hold that without cramping. */}
            <div className={step === 3 ? 'mt-8 grid grid-cols-1 xl:grid-cols-5 gap-8 items-start' : 'mt-8'}>
                {/* Left Side: Main Inputs and Strategy */}
                <div className={step === 3 ? 'xl:col-span-3 space-y-6' : 'space-y-6'}>
                    {step === 1 && (<>
                    {/* 1. Facility & Service Costs */}
                    <Card>
                        {currentHomeFacts && (
                            <div className="mb-5 rounded-xl border border-slate-200 border-l-4 border-l-slate-500 bg-slate-50/70 p-4" data-testid="current-home">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Where {currentHomeFacts.name} is now</p>
                                <p className="mt-1 text-sm leading-relaxed text-slate-700">{currentHomeSummary(currentHomeFacts)}</p>
                                <p className="mt-1 text-xs text-slate-500">These details come from the Client Profile (Living Arrangements). Change them there if they are not right.</p>
                            </div>
                        )}
                        {villageFacts && (
                            <div className="mb-5 rounded-xl border border-slate-200 border-l-4 border-l-slate-500 bg-slate-50/70 p-4" data-testid="village-now">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Where {villageFacts.name} is now</p>
                                <p className="mt-1 text-sm leading-relaxed text-slate-700">{villageSummary(villageFacts)}</p>
                                <p className="mt-1 text-xs text-slate-500">The village's terms come from the Client Profile (Living Arrangements). Change them there if they are not right.</p>
                            </div>
                        )}
                        <h3 className="text-xl font-bold text-gray-800 mb-4">{isMover ? 'The new home' : 'Facility & Service Costs'}</h3>
                        {/*
                          * THREE columns became two, and the switch moved from `md:` to `xl:`.
                          *
                          * Tailwind's breakpoints measure the VIEWPORT, not the column the grid sits
                          * in. `md:grid-cols-3` therefore fired at a 768px window while this grid was
                          * inside a 619px working column — three ~185px columns, one of which holds a
                          * text input AND a per-day/per-year toggle. That is the reported "fields
                          * don't work well at 100%".
                          *
                          * The pane splits at `xl:`, so this grid must wait for `2xl:` — at 1280–1535
                          * the working pane is only ~576–650px, and splitting that in two puts the
                          * room-price input and its Facility button in ~290px. One full-width column
                          * there, two from 1536px where the pane is 890px. Every field stays above
                          * 430px in the split state and far wider below it.
                          */}
                        {bothEntering ? (
                            <>
                                <p className="mb-4 text-sm leading-relaxed text-slate-600" data-testid="both-rooms-intro">
                                    Both of you are moving into care, so each of you has a room of your own, with its own price and fees.
                                    Centrelink assesses you as a couple living apart, each at the single pension rate. How much of each room is
                                    paid up front is worked out in step 3 — the same share for both rooms.
                                </p>
                                <div className="space-y-6">
                                    {/* The blocks never move or change hands: the profile's client first, the
                                        partner second, whichever of them moves in first. Each block shows the
                                        room its person's inputs are held in (owner, 7 Oct 2026). */}
                                    {(['client', 'partner'] as const).map((person) => {
                                        const slot: 'first' | 'second' = person === leadPerson ? 'first' : 'second';
                                        const name = (person === 'client' ? factFind.client?.firstName : factFind.partner?.firstName) || (person === 'client' ? 'the client' : 'their partner');
                                        const subtitle = !secondEntersLater
                                            ? 'Both move in together — give one of you a later start date if they move in later.'
                                            : slot === 'first' ? 'Moves in first.' : `Moves in from ${phaseNames.fromLabel}.`;
                                        return (
                                            <div key={person} className="rounded-xl border border-slate-200 border-l-4 border-l-blue-600 bg-white p-4" data-testid={`room-${person}`}>
                                                <h4 className="text-base font-bold text-gray-800">{name}&apos;s room</h4>
                                                <p className="mb-4 text-xs text-slate-500">{subtitle}</p>
                                                {slot === 'first' ? firstRoomInputs : secondRoomInputs}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : firstRoomInputs}
                        {/* The household's questions — asked once, whoever is entering. */}
                        {(radInput > 0 || coveredByNoWorseOff) && (
                        <div className="mt-6 grid grid-cols-1 2xl:grid-cols-2 gap-6">
                            {/*
                              * Asked only once there is a deposit to draw on. It decides where the plan
                              * turns when the savings run low — the deposit before the home — so it is a
                              * fact worth having before either engine runs, and both read it from here.
                              */}
                            {radInput > 0 && (
                                <div className="space-y-2">
                                    <label htmlFor="rad-drawdown" className="block text-sm font-medium text-gray-700 mb-1">
                                        {bothEntering ? 'Can the homes take their fees out of your deposits?' : 'Can the home take its fees out of your deposit?'}{' '}
                                        <WhatsThis text="When you pay a refundable deposit (RAD), the aged-care home holds that money and refunds it when you leave. Instead of paying every fee out of your bank account, you can ask the home to take some fees out of the deposit. The home must allow this for the daily accommodation payment, and most homes will agree to it for the other fees too if you ask in writing. Each dollar taken out slightly reduces your refund and slightly increases the daily accommodation payment, because the home is now holding less of your money. This only matters once your savings run low — until then, fees are paid from cashflow as normal." />
                                    </label>
                                    <select
                                        id="rad-drawdown"
                                        name="radDrawdown"
                                        value={radDrawdown}
                                        onChange={(e) => setRadDrawdown(e.target.value as RadDrawdownMode)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                    >
                                        {RAD_DRAWDOWN_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-slate-500">
                                        {radDrawdown === 'none'
                                            ? 'Once savings run low the plan borrows against the home instead of drawing on the deposit.'
                                            : radDrawdown === 'dap'
                                                ? 'Only the daily accommodation payment is drawn from the deposit; other fees come from savings, then the home.'
                                                : 'Once savings run low, care fees are drawn from the deposit before the plan borrows against the home.'}
                                    </p>
                                </div>
                            )}
                            {/*
                              * Asked only of a resident the profile puts under the no-worse-off principle.
                              * Their default is the 1 July 2014 arrangements; opting in cannot be undone in
                              * practice, so it is modelled only when chosen here, and both engines read it.
                              */}
                            {coveredByNoWorseOff && (
                                <div className="space-y-2" data-testid="fee-arrangements-choice">
                                    <label htmlFor="fee-arrangements" className="block text-sm font-medium text-gray-700 mb-1">
                                        Which fee rules apply in care?{' '}
                                        <WhatsThis text="Because a Home Care Package was in place (or approved) on 12 September 2024, the Department's 'no worse off' principle puts this person on the 1 July 2014 fee arrangements when they enter a home: a means-tested care fee instead of the hotelling and non-clinical care contributions, with an annual cap and a lifetime cap. They may opt in to the 1 November 2025 rules at any time, but cannot go back. Accommodation is on the 2025 rules either way." />
                                    </label>
                                    <select
                                        id="fee-arrangements"
                                        name="residentialFeeArrangements"
                                        value={optInToNovember2025Fees ? '2025' : '2014'}
                                        onChange={(e) => setOptInToNovember2025Fees(e.target.value === '2025')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                    >
                                        <option value="2014">Stay on the 1 July 2014 rules — means-tested care fee (no worse off)</option>
                                        <option value="2025">Opt in to the 1 November 2025 rules — hotelling and non-clinical care contributions</option>
                                    </select>
                                    <p className="text-xs text-slate-500">
                                        {optInToNovember2025Fees
                                            ? 'Modelled as having opted in to the 1 November 2025 fee arrangements. This election cannot be reversed once made.'
                                            : 'The means-tested care fee applies, capped each year and over a lifetime. The strategies below also show what opting in would be worth.'}
                                    </p>
                                </div>
                            )}
                        </div>
                        )}
                        {/* The walk-in position, stated where the room price was just entered — the
                            anchor every strategy below is compared against. Inline and unclosable,
                            unlike the Insight Panel that used to carry this and could be dismissed. */}
                        {/* The walk-in note lives on step 2 (WalkInHero), under the four numbers it explains. */}
                    </Card>
                    {/* 2. Living Expenses */}
                    <Card>
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm your living needs</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            You told us that your current living needs were {formatCurrency(factFind.expenses?.livingExpenses)} pa. Will this amount change with {enteringFirstName} moving into aged care? This amount will be added to the estimated aged care cost to determine your cashflow position.
                        </p>
                        <CurrencyInput label={secondEntersLater ? `Adjusted Annual Living Expenses ($) — until ${phaseNames.fromLabel}` : 'Adjusted Annual Living Expenses ($)'} name="scenarioLivingExpenses" value={scenarioLivingExpenses} onChange={(v) => setScenarioLivingExpenses(v ?? 0)} />
                        {/* Two entry dates, two figures (owner, 7 Oct 2026): the second applies from the second entry. */}
                        {secondEntersLater && (
                            <div className="mt-4 rounded-xl border border-slate-200 border-l-4 border-l-blue-600 bg-white p-4" data-testid="living-phase-two">
                                <p className="mb-3 text-sm leading-relaxed text-slate-600">{livingPhaseTwoHelp(phaseNames)}</p>
                                <CurrencyInput
                                    label={`Annual living expenses from ${phaseNames.fromLabel}, with both in care ($)`}
                                    name="phaseTwoLivingExpenses"
                                    value={phaseTwoLivingExpenses ?? scenarioLivingExpenses}
                                    onChange={(v) => setPhaseTwoLivingExpenses(v == null ? null : v)}
                                />
                                {phaseTwoLivingExpenses != null && Math.round(phaseTwoLivingExpenses) !== Math.round(Number(scenarioLivingExpenses) || 0) && (
                                    <button type="button" onClick={() => setPhaseTwoLivingExpenses(null)} className="mt-2 text-xs font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900">
                                        Same as before {phaseNames.fromLabel}
                                    </button>
                                )}
                            </div>
                        )}
                    </Card>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 hover:text-blue-900">
                            <BookOpen size={15} /> New to aged care fees? Read the guide
                        </button>
                        <button
                            type="button"
                            onClick={() => goToStep(2)}
                            disabled={!(radInput > 0)}
                            className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600 disabled:opacity-50"
                        >
                            See what this means for {enteringFirstName} <ArrowRight size={16} />
                        </button>
                    </div>
                    </>)}

                    {step === 2 && (
                        <WalkInHero
                            factFind={factFind}
                            radInput={radInput}
                            secondRoom={secondRoomActive ? { person: secondPerson, roomPrice: secondRoom.roomPrice, careStartDate: secondRoom.careStartDate || undefined, extraServiceAnnual: secondRoom.extraServiceAnnual, entersLater: secondEntersLater, ...(livingOnceBoth != null ? { livingExpensesOnceBoth: livingOnceBoth } : {}) } : null}
                            existingStay={existingStay}
                            villageExit={villageFacts ? { entitlement: villageFacts.entitlement, refundDate: villageFacts.refundDate } : null}
                            extraServiceFeeInput={extraServiceFeeInput}
                            scenarioLivingExpenses={Number(scenarioLivingExpenses) || 0}
                            agedCareBaseline={agedCareBaseline}
                            runway={walkInRunway}
                            legacyYears={Number(localLegacyYears) || 0}
                            onLegacyYearsChange={setLocalLegacyYears}
                            onBack={() => goToStep(1)}
                            onNext={() => goToStep(3)}
                            onReadGuide={onBack}
                            onAssumptions={() => setActivePage('Assumptions')}
                            onShowProjections={() => { setProjectionsSource('walkIn'); setShowProjections(true); }}
                        />
                    )}

                    {step === 3 && (<>
                    {/* 3. Strategy Mode Selector (Redesigned) */}
                    <Card>
                        <p className="mb-4 text-[14px] leading-relaxed text-slate-600">
                            This step is about moving the numbers from step 2. Let the Suggestion Engine test the
                            combinations and rank them by what matters to you, or set each strategy yourself and see what it does.
                            Either way the plan summary on the right shows what you are working against.
                        </p>
                        {/* The shared ModeChooser artifact (components/ui/ModeChooser.tsx) — this module's
                        original markup became the shared component, so every module's chooser is this one. */}
                        <ModeChooser
                            value={strategyModeChosen ? strategyMode : ''}
                            onSelect={(id) => {
                                // A mode chosen by hand is a fresh run, never a reopen.
                                setReopenedEngineStrategyId(null);
                                if (id === 'manual') {
                                    setStrategyModeChosen(true); setStrategyMode('manual');
                                    setShowStrategyOptions(true);
                                    if (strategyMode === 'optimised') {
                                        setActiveSavedStrategyId(null);
                                    }
                                    return;
                                }
                                // Check if couple validation is needed
                                const isCouple = factFind.adviceFor === 'couple';
                                const hasHome = (Number(factFind.home?.value) || 0) > 0;
                                const hasStructures = (factFind.structures || []).length > 0;
                                const needsCoupleValidation =
                                    isCouple && hasHome && coupleValidation.exploreAlternatives === undefined;
                                const needsSeSetup =
                                    hasStructures && !coupleValidation.suggestionEngineSetupComplete;
                                if (needsCoupleValidation || needsSeSetup) {
                                    setShowValidationModal(true);
                                } else {
                                    setStrategyModeChosen(true); setStrategyMode('optimised');
                                    setShowStrategyOptions(false);
                                }
                            }}
                            options={[
                                {
                                    id: 'manual',
                                    title: 'Manual Control',
                                    blurb:
                                        'Set everything yourself — you control which assets are sold and what is paid up front. We show the results and the impacts.',
                                    chip: 'Advisers & confident users',
                                },
                                {
                                    id: 'optimised',
                                    title: 'Suggestion Engine',
                                    blurb:
                                        'Answer a few questions and it tests hundreds of combinations, then shows the best — ranked by your goals.',
                                    chip: 'Most people start here',
                                },
                            ]}
                        />
                    </Card>

                    {/* 4. Progressive Disclosure: Only show strategy options if Manual Control is selected */}
                    {strategyMode === 'manual' && showStrategyOptions && (
                        <>
                        <Card>
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Property Strategy Options</h3>
                            {(anyoneOwnsHome(asFactFind(factFind)) || (factFind.assets?.investmentProperty && factFind.assets.investmentProperty.length > 0)) ? (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {anyoneOwnsHome(asFactFind(factFind)) && (
                                            <>
                                                <FormCheckbox
                                                    label="Rent the family home"
                                                    name="rentHome"
                                                    checked={overallStrategy.rentHome}
                                                    onChange={(n, v) => setOverallStrategy(s => ({ ...s, [n]: v, downsizeHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false }))}
                                                />
                                                <FormCheckbox
                                                    label="Sell/Downsize the family home"
                                                    name="downsizeHome"
                                                    checked={overallStrategy.downsizeHome}
                                                    onChange={(n, v) => setOverallStrategy(s => ({ ...s, [n]: v, rentHome: false, accessHomeEquity: false, reverseMortgage: false, heas: false }))}
                                                />
                                            </>
                                        )}
                                        {factFind.assets?.investmentProperty && factFind.assets.investmentProperty.length > 0 && (
                                            <FormCheckbox
                                                label="Sell Investment Property"
                                                name="sellInvestmentProperty"
                                                checked={overallStrategy.sellInvestmentProperty}
                                                onChange={(n, v) => setOverallStrategy(s => ({ ...s, [n]: v }))}
                                            />
                                        )}
                                    </div>
                                    {anyoneOwnsHome(asFactFind(factFind)) && (
                                        <>
                                            <div className="border-t border-gray-200 pt-4 mt-4">
                                                <FormCheckbox
                                                    label="Access Home Equity"
                                                    name="accessHomeEquity"
                                                    checked={overallStrategy.accessHomeEquity}
                                                    onChange={(n, v) => setOverallStrategy(s => ({ ...s, [n]: v, rentHome: false, downsizeHome: false }))}
                                                />
                                                {overallStrategy.accessHomeEquity && (
                                                    <div className="mt-4 ml-6 space-y-3">
                                                        <FormCheckbox
                                                            label="Reverse Mortgage"
                                                            name="reverseMortgage"
                                                            checked={overallStrategy.reverseMortgage}
                                                            onChange={(n, v) => setOverallStrategy(s => ({ ...s, [n]: v, heas: false }))}
                                                        />
                                                        <FormCheckbox
                                                            label="Home Equity Access Scheme (HEAS)"
                                                            name="heas"
                                                            checked={overallStrategy.heas}
                                                            onChange={(n, v) => setOverallStrategy(s => ({ ...s, [n]: v, reverseMortgage: false }))}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            {(overallStrategy.reverseMortgage || overallStrategy.heas) && (
                                                <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                                                    <p className="text-sm text-gray-800 font-semibold mb-2 flex items-center gap-2">
                                                        <AlertTriangle className="w-4 h-4 text-blue-600" />
                                                        Important Disclaimer
                                                    </p>
                                                    <p className="text-xs text-gray-700">
                                                        This is strategic advice only - we do not endorse or recommend any reverse mortgage provider & you will need to do your own research on these providers and their product features. This calculator is for educational and illustrative purposes only and does not constitute financial product advice. You should seek independent financial advice before taking out a reverse mortgage or accessing HEAS.
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="p-4 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded text-slate-700 text-sm">
                                    Property strategies are only available if you own your home or have an investment property.
                                </div>
                            )}
                            {overallStrategy.rentHome && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
                                    <h4 className="font-semibold">Rental Strategy Assumptions</h4>
                                    <CurrencyInput label="Weekly Rent from Home ($)" name="rentAmount" value={strategyInputs.rentAmount} onChange={(v) => setStrategyInputs(s => ({...s, rentAmount: v}))} />
                                    <CurrencyInput label="Annual Expenses for Home ($)" name="rentExpenses" value={strategyInputs.rentExpenses} onChange={(v) => setStrategyInputs(s => ({...s, rentExpenses: v}))} />
                                    {factFind.adviceFor === 'couple' && <CurrencyInput label="Partner's Weekly Rent ($)" name="partnerRent" value={strategyInputs.partnerRent} onChange={(v) => setStrategyInputs(s => ({...s, partnerRent: v}))} />}
                                    <div className="mt-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg text-sm">
                                        <p><strong className="text-blue-700">For Aged Care Fees:</strong> The home's value is included in the means test, but capped at {formatCurrency(AGED_CARE_HOME_EXEMPTION_CAP)}. The net rental income is also included.</p>
                                        <p className="mt-2"><strong className="text-blue-700">For Age Pension:</strong> The home is exempt from the assets test for 2 years, but the net rental income is included in the income test.</p>
                                    </div>
                                </div>
                            )}
                            {overallStrategy.downsizeHome && (
                                <>
                                    <DownsizeStrategyAllocator
                                        factFind={factFind}
                                        details={downsizeDetails}
                                        setDetails={setDownsizeDetails}
                                        radRoomCost={radInput}
                                    />
                                    {(() => {
                                        const flag = homeSaleHorizonFlag({ factFind, sellsHome: true, newHomePrice: downsizeDetails?.newHomePurchasePrice });
                                        return flag ? (
                                            <p className="mt-2 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                                                <span className="font-semibold">Worth knowing.</span> {flag.warning}
                                            </p>
                                        ) : null;
                                    })()}
                                </>
                            )}
                            {overallStrategy.sellInvestmentProperty && (
                                <InvestmentPropertyStrategyAllocator
                                    factFind={factFind}
                                    details={investmentPropertyDetails}
                                    setDetails={setInvestmentPropertyDetails}
                                    radRoomCost={radInput}
                                />
                            )}
                            {/* What borrowing actually costs, said where it is entered. Both of these
                                compound with nothing repaid along the way, so the number that decides
                                the family's outcome is not what is borrowed but what is owed at the
                                end — which was buried in a floating panel nobody had reason to open. */}
                            {overallStrategy.reverseMortgage && (
                                <>
                                    <ReverseMortgageStrategyAllocator
                                        factFind={factFind}
                                        details={reverseMortgageDetails}
                                        setDetails={setReverseMortgageDetails}
                                        legacyYears={localLegacyYears}
                                    />
                                    <ReverseMortgageFieldNote
                                        loanAmount={Number(reverseMortgageDetails?.loanAmount) || 0}
                                        interestRate={Number(reverseMortgageDetails?.interestRate) || 0}
                                        years={Number(localLegacyYears) || 5}
                                        useForRad={!!reverseMortgageDetails?.useForRad}
                                    />
                                </>
                            )}
                            {overallStrategy.heas && (
                                <>
                                    <HEASStrategyAllocator
                                        factFind={factFind}
                                        details={heasDetails}
                                        setDetails={setHeasDetails}
                                        legacyYears={localLegacyYears}
                                    />
                                    <HeasFieldNote
                                        paymentAmount={Number(heasDetails?.paymentAmount) || 0}
                                        paymentFrequency={String(heasDetails?.paymentFrequency || 'fortnightly')}
                                        interestRate={Number(heasDetails?.interestRate) || 0}
                                        years={Number(localLegacyYears) || 5}
                                    />
                                </>
                            )}
                        </Card>
                        {/* Above RAD funding, because money spent retiring a debt is money that cannot
                            pay a deposit — the page reads in the order the money moves, and the engine
                            runs the two in that same order. Renders nothing when the client has no debt
                            (the card decides that itself, so the rule lives with the card). */}
                        <DebtPayoffStrategy
                            factFind={factFind}
                            debtPayments={debtPayments}
                            setDebtPayments={setDebtPayments}
                            debtFunding={debtFunding}
                            setDebtFunding={setDebtFunding}
                            repaymentBasis={debtRepaymentBasis}
                            setRepaymentBasis={setDebtRepaymentBasis}
                            reservedByAsset={reservedForRad}
                        />
                        {roomCoveredByStrategies && !showFundingAnyway ? (
                            <Card>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-800">{bothEntering ? `Paying for ${firstName}'s room` : isMover ? 'Paying for the new room' : 'Paying for the room'}</h3>
                                        <p className="mt-1 text-sm leading-relaxed text-slate-600">
                                            {isMover && currentHomeFacts
                                                ? <>Fully covered — the {formatCurrency(Math.round(currentHomeFacts.refundToday))} refund of the current deposit{radPaidBreakdown.fromStrategies - radPaidBreakdown.fromRefund > 0.5 ? ' and the strategy above' : ''} already {radPaidBreakdown.fromStrategies - radPaidBreakdown.fromRefund > 0.5 ? 'pay' : 'pays'} the whole {formatCurrency(radInput)} room price, so nothing needs to come from savings.</>
                                                : villageFacts && villageInterim && radPaidBreakdown.fromVillage > 0.5 && radPaidBreakdown.fromStrategies - radPaidBreakdown.fromVillage < 0.5
                                                    ? <span data-testid="village-covered">{villageCoveredLine(villageFacts, radPaidBreakdown.fromVillage, villageInterim, radInput, villagePaidAtMove)}</span>
                                                : <>Fully covered — the strategy above already pays the whole{' '}
                                            {formatCurrency(radInput)} room price, so nothing needs to come from savings.</>}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowFundingAnyway(true)}
                                        className="shrink-0 text-sm font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                                    >
                                        Pay part from savings instead
                                    </button>
                                </div>
                            </Card>
                        ) : (
                        <Card>
                            {isMover && currentHomeFacts && (
                                <p className="mb-4 rounded-lg border border-slate-200 border-l-4 border-l-blue-600 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700" data-testid="refund-first">
                                    <strong>The current deposit comes back first.</strong> {refundFirstLine(currentHomeFacts)}
                                </p>
                            )}
                            {villageFacts && villageInterim && (
                                <div className="mb-4 rounded-lg border border-slate-200 border-l-4 border-l-blue-600 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700" data-testid="village-refund">
                                    <p className="mb-2"><strong>When will the unit be paid out?</strong></p>
                                    <div className="mb-2 flex flex-wrap gap-2" role="radiogroup" aria-label="When the unit is paid out" data-testid="village-paid-when">
                                        {([['later', `Later — up to ${villageExit?.refundMonths ?? 12} months after leaving`], ['move', 'At the move']] as const).map(([id, label]) => (
                                            <button key={id} type="button" role="radio" aria-checked={villagePaidAtMove === (id === 'move')} onClick={() => setVillagePaidAtMove(id === 'move')} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${villagePaidAtMove === (id === 'move') ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>{label}</button>
                                        ))}
                                    </div>
                                    <p>{villagePaidAtMove ? <strong>The village money is paid at the move.</strong> : <strong>The village money comes later.</strong>} {villageRefundLine(villageFacts, radPaidBreakdown.fromVillage, villageInterim, useVillageRefundForRoom, villagePaidAtMove)}{!villagePaidAtMove && villageFees && villageFees.total > 0.5 ? ` ${villageFeesAfterLeavingLine(villageFees)}` : ''}{villagePaidAtMove ? '' : ` ${villageWaitSensitivityLine({ refundMonths: villageFacts.refundMonths, interim: villageInterim, feesAfterLeaving: villageFees })}`}</p>
                                    <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                                        <input type="checkbox" checked={useVillageRefundForRoom} onChange={(e) => setUseVillageRefundForRoom(e.target.checked)} />
                                        Use the village's exit payment to pay the rest of the room when it arrives
                                    </label>
                                </div>
                            )}
                            <RadFundingStrategy
                                saleTimingSlot={saleTimingSlot}
                                factFind={factFind}
                                radPaid={radPaid}
                                radInput={radInput}
                                radFunding={radFunding}
                                setRadFunding={setRadFunding}
                                cgtOverrides={cgtOverrides}
                                setCgtOverrides={setCgtOverrides}
                                strategyMode={strategyMode}
                                reservedByAsset={reservedForDebt}
                            />
                        {secondRoomActive && secondFunding && (
                            <p className="mt-4 rounded-lg border border-slate-200 border-l-4 border-l-blue-600 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700" data-testid="second-room-share">
                                <strong>{secondName}&apos;s room follows the same share.</strong> {radInput > 0 ? Math.round(Math.min(1, radPaid / radInput) * 100) : 0}% of it is paid up front — {formatCurrency(secondFunding.funded)} — taken from what is left after {firstName}&apos;s deposit (the bank first, then term deposits, pension accounts and shares), so the same money cannot pay for both rooms.{' '}
                                {secondFunding.shortfall > 0.5
                                    ? `Only ${formatCurrency(secondFunding.funded)} can be raised; the rest of ${secondName}'s room is paid daily.`
                                    : `The rest of ${secondName}'s room is paid daily.`}
                            </p>
                        )}
                        </Card>
                        )}

                        {/* The plain-words recap used to sit here, a long way below the figures it
                            explains and a long way from the panel that now carries them. It moved
                            into "Understanding your plan" — see planRecapNode and the modal at the
                            foot of this component. Nothing about it changed but where it is read. */}

                        {/* Other Strategies Section */}
                        <Card>
                            <button
                                onClick={() => setShowOtherStrategies(!showOtherStrategies)}
                                className="w-full flex items-center justify-between text-left"
                            >
                                <h3 className="text-xl font-bold text-gray-800">Other Strategies</h3>
                                <ChevronDown 
                                    className={`w-5 h-5 text-gray-400 transition-transform ${showOtherStrategies ? 'rotate-180' : ''}`} 
                                />
                            </button>
                            
                            {showOtherStrategies && (
                                <div className="mt-4 space-y-4">
                                    <p className="text-sm text-gray-600 mb-4">
                                        Optional strategies to reduce ongoing costs and boost Age Pension. These work alongside your RAD funding strategy above.
                                    </p>
                                    
                                    {/* Gifting and Funeral Bonds - Side by Side */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Gifting Strategy */}
                                        <FormCheckbox
                                            label="Gifting to Family"
                                            name="gifting"
                                            checked={otherStrategies.gifting}
                                            onChange={(n, v) => setOtherStrategies(s => ({ ...s, [n]: v }))}
                                        />
                                        
                                        {/* Funeral Bonds Strategy - Only show if they don't already have funeral bonds */}
                                        {(() => {
                                            const hasFuneralBonds = factFind.assets?.funeralBonds && factFind.assets.funeralBonds.length > 0 && 
                                                factFind.assets.funeralBonds.some(fb => parseFloat(String(fb.value || 0)) > 0);
                                            if (hasFuneralBonds) return null;
                                            
                                            return (
                                                <FormCheckbox
                                                    label="Funeral Bonds"
                                                    name="funeralBonds"
                                                    checked={otherStrategies.funeralBonds}
                                                    onChange={(n, v) => setOtherStrategies(s => ({ ...s, [n]: v }))}
                                                />
                                            );
                                        })()}
                                    </div>
                                    
                                    {/* Gifting Details - Opens below when checked */}
                                    {otherStrategies.gifting && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
                                            <h4 className="font-semibold">Gifting Strategy Details</h4>
                                            {/* Previous Gifting Warning */}
                                            {(() => {
                                                const giftsHistory = factFind.giftsHistory || {};
                                                if (!giftsHistory.hasGifted) return null;
                                                let totalGifted = 0;
                                                Object.keys(giftsHistory).forEach((key) => {
                                                    if (key.startsWith('year')) {
                                                        totalGifted += parseFloat(String(giftsHistory[key] || 0)) || 0;
                                                    }
                                                });
                                                const asOf = new Date();
                                                // The draft giftsHistory bag is structurally compatible with FactFindGiftsHistory
                                                // (see types/formDraft.ts); calculateDeprivedAssets defensively parses internally
                                                // regardless, so this cast is safe.
                                                const giftsHistoryForCalc = giftsHistory as unknown as FactFindGiftsHistory;
                                                const historicalDeprived = calculateDeprivedAssets(giftsHistoryForCalc, {}, asOf);
                                                const planAmt = parseFloat(String(otherStrategies.giftingAmount)) || 0;
                                                const provisionalDeprived =
                                                    planAmt > 0
                                                        ? calculateDeprivedAssets(
                                                              giftsHistoryForCalc,
                                                              { otherStrategies: { gifting: true, giftingAmount: planAmt } },
                                                              asOf,
                                                          )
                                                        : historicalDeprived;
                                                const incrementalDeprived = Math.max(0, provisionalDeprived - historicalDeprived);

                                                return (
                                                        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 shadow-sm">
                                                            <div className="flex items-start gap-3">
                                                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                                                    <Info className="w-4 h-4 text-blue-600" />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <p className="text-sm font-semibold text-gray-900 mb-2">
                                                                        Previous Gifting Detected
                                                                    </p>
                                                                    <div className="space-y-1.5 text-sm text-gray-700">
                                                                        <p>
                                                                            You entered <strong className="text-gray-900">{formatCurrency(totalGifted)}</strong> in FY buckets (Client Profile).
                                                                        </p>
                                                                        <p>
                                                                            Free areas used in this model: <strong>{formatCurrency(GIFTING_LIMIT_ANNUAL)}</strong> per financial year and <strong>{formatCurrency(GIFTING_LIMIT_5_YEAR)}</strong> over any rolling five financial years (combined limits for singles and couples).
                                                                        </p>
                                                                        {historicalDeprived > 0 && (
                                                                            <p className="text-sm text-gray-800 font-medium mt-2 pt-2 border-t border-blue-200">
                                                                                About <strong>{formatCurrency(historicalDeprived)}</strong> is included as deprived assets in the pension estimate (asset test / deeming).
                                                                            </p>
                                                                        )}
                                                                        {incrementalDeprived > 0 && planAmt > 0 && (
                                                                            <p className="text-sm text-gray-800 font-medium mt-2 pt-2 border-t border-blue-200">
                                                                                If you proceed with this gift, the model adds about <strong>{formatCurrency(incrementalDeprived)}</strong> more deprived assets for the assessment snapshot.
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                            })()}
                                            
                                            <div className="space-y-3">
                                                {(() => {
                                                    // Build funding source options with balances - include all liquid asset types
                                                    // NOTE: For "Other Strategies", we need to show funds BEFORE RAD allocations
                                                    // because Other Strategies are applied BEFORE RAD in simulatedFactFind
                                                    const buildFundingSourceOptions = () => {
                                                        const options = [];
                                                        // Get all liquid asset types from ASSET_TYPES
                                                        const liquidAssetTypes = ASSET_TYPES.filter(assetType => assetType.isLiquid).map(assetType => assetType.id);

                                                        liquidAssetTypes.forEach(type => {
                                                            // Use original factFind (before RAD) for available funds calculation
                                                            // because Other Strategies are applied before RAD
                                                            if (factFind.assets && factFind.assets[type]) {
                                                                factFind.assets[type].forEach((asset, _idx) => {
                                                                    const assetId = `${type}-${asset.id}`;
                                                                    // Don't subtract RAD allocations - Other Strategies are applied before RAD
                                                                    const assetValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                                                                    // But we need to subtract any amounts already allocated by Other Strategies in current strategy
                                                                    // Check if this asset was used for gifting or funeral bonds in current strategy
                                                                    let otherStrategyAllocation = 0;
                                                                    if (otherStrategies.gifting && otherStrategies.giftingSource === assetId) {
                                                                        otherStrategyAllocation += parseFloat(String(Number(otherStrategies.giftingAmount) || 0));
                                                                    }
                                                                    if (otherStrategies.funeralBonds && otherStrategies.funeralBondSource === assetId) {
                                                                        otherStrategyAllocation += parseFloat(String(Number(otherStrategies.funeralBondAmount) || 0));
                                                                    }
                                                                    const available = Math.max(0, assetValue - otherStrategyAllocation);
                                                                    if (available > 0) {
                                                                        const ownership = asset.ownership || 'client';
                                                                        const ownershipLabel = ownership === 'joint' ? 'Joint' : (ownership === 'partner' ? (factFind?.partner?.firstName || 'Partner') : (factFind?.client?.firstName || 'Client'));
                                                                        const assetName = asset.description || asset.name || ASSET_TYPES.find(at => at.id === type)?.name || type;
                                                                        options.push({
                                                                            value: assetId, // Use unique assetId instead of type
                                                                            label: `${assetName} (${ownershipLabel}) - ${formatCurrency(available)} available`
                                                                        });
                                                                    }
                                                                });
                                                            }
                                                        });
                                                        
                                                        // If no specific assets, show generic option
                                                        if (options.length === 0) {
                                                            // Calculate total available from all bank accounts (before RAD)
                                                            let totalBank = 0;
                                                            if (factFind.assets?.bankAccounts) {
                                                                totalBank = factFind.assets.bankAccounts.reduce((sum, acc) => {
                                                                    return sum + (parseFloat(String(acc.value || 0)) || 0);
                                                                }, 0);
                                                            }
                                                            options.push({
                                                                value: 'bankAccounts',
                                                                label: `Bank Accounts / Cash - ${formatCurrency(totalBank)} available`
                                                            });
                                                        }
                                                        
                                                        return options;
                                                    };
                                                    
                                                    const fundingOptions = buildFundingSourceOptions();
                                                    
                                                    return (
                                                        <>
                                                            <CurrencyInput
                                                                label="Gifting Amount ($)"
                                                                name="giftingAmount"
                                                                value={otherStrategies.giftingAmount}
                                                                onChange={(v) => {
                                                                    setOtherStrategies(s => ({ ...s, giftingAmount: v ?? 0 }));
                                                                }}
                                                            />
                                                            
                                                            {/*
                                                              * THE FIVE-YEAR RULE, WHERE THE GIFT IS ENTERED.
                                                              *
                                                              * This was explained only in the floating insight panel, which meant a
                                                              * client could type $200,000 into this box and read nothing about the
                                                              * consequence unless they went looking for it. Gifting above the free
                                                              * area is the single most misunderstood thing on this screen: the money
                                                              * is gone immediately and Centrelink goes on counting it for five years.
                                                              */}
                                                            <GiftingFieldNote
                                                                giftingAmount={Number(otherStrategies.giftingAmount) || 0}
                                                                giftsHistory={factFind.giftsHistory}
                                                            />

                                                            <FormSelect
                                                                label="Funding Source"
                                                                name="giftingSource"
                                                                value={otherStrategies.giftingSource}
                                                                onChange={(n, v) => setOtherStrategies(s => ({ ...s, [n]: v }))}
                                                            >
                                                                {fundingOptions.map((opt, idx) => (
                                                                    <option key={`gifting-${opt.value}-${idx}`} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </FormSelect>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Funeral Bonds Details - Opens below when checked */}
                                    {otherStrategies.funeralBonds && (() => {
                                        const hasFuneralBonds = factFind.assets?.funeralBonds && factFind.assets.funeralBonds.length > 0 && 
                                            factFind.assets.funeralBonds.some(fb => parseFloat(String(fb.value || 0)) > 0);
                                        if (hasFuneralBonds) return null;
                                        
                                        return (
                                            <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
                                                <h4 className="font-semibold">Funeral Bonds Strategy Details</h4>
                                                {/*
                                                  * A teal "Exempt Asset" card sat here saying funeral bonds are exempt
                                                  * up to the limit. The note UNDER the amount field now says the same
                                                  * rule and, unlike the card, says what THIS amount does with it — so
                                                  * the card was the general half of a statement whose specific half
                                                  * appears three inches below. Two cards about one rule is the
                                                  * duplication this whole change is removing.
                                                  */}

                                                <div className="space-y-3">
                                                    {(() => {
                                                        // Build funding source options with balances - include all liquid asset types
                                                        // NOTE: For "Other Strategies", we need to show funds BEFORE RAD allocations
                                                        // because Other Strategies are applied BEFORE RAD in simulatedFactFind
                                                        const buildFundingSourceOptions = () => {
                                                            const options = [];
                                                            // Get all liquid asset types from ASSET_TYPES
                                                            const liquidAssetTypes = ASSET_TYPES.filter(assetType => assetType.isLiquid).map(assetType => assetType.id);

                                                            liquidAssetTypes.forEach(type => {
                                                                // Use original factFind (before RAD) for available funds calculation
                                                                // because Other Strategies are applied before RAD
                                                                if (factFind.assets && factFind.assets[type]) {
                                                                    factFind.assets[type].forEach((asset, _idx) => {
                                                                        const assetId = `${type}-${asset.id}`;
                                                                        // Don't subtract RAD allocations - Other Strategies are applied before RAD
                                                                        const assetValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                                                                        // But we need to subtract any amounts already allocated by Other Strategies in current strategy
                                                                        // Check if this asset was used for gifting or funeral bonds in current strategy
                                                                        let otherStrategyAllocation = 0;
                                                                        if (otherStrategies.gifting && otherStrategies.giftingSource === assetId) {
                                                                            otherStrategyAllocation += parseFloat(String(Number(otherStrategies.giftingAmount) || 0));
                                                                        }
                                                                        if (otherStrategies.funeralBonds && otherStrategies.funeralBondSource === assetId) {
                                                                            otherStrategyAllocation += parseFloat(String(Number(otherStrategies.funeralBondAmount) || 0));
                                                                        }
                                                                        const available = Math.max(0, assetValue - otherStrategyAllocation);
                                                                        if (available > 0) {
                                                                            const ownership = asset.ownership || 'client';
                                                                            const ownershipLabel = ownership === 'joint' ? 'Joint' : (ownership === 'partner' ? (factFind?.partner?.firstName || 'Partner') : (factFind?.client?.firstName || 'Client'));
                                                                            const assetName = asset.description || asset.name || ASSET_TYPES.find(at => at.id === type)?.name || type;
                                                                            options.push({
                                                                                value: assetId, // Use unique assetId instead of type
                                                                                label: `${assetName} (${ownershipLabel}) - ${formatCurrency(available)} available`
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                                                            
                                                            // If no specific assets, show generic option
                                                            if (options.length === 0) {
                                                                // Calculate total available from all bank accounts (before RAD)
                                                                let totalBank = 0;
                                                                if (factFind.assets?.bankAccounts) {
                                                                    totalBank = factFind.assets.bankAccounts.reduce((sum, acc) => {
                                                                        return sum + (parseFloat(String(acc.value || 0)) || 0);
                                                                    }, 0);
                                                                }
                                                                options.push({
                                                                    value: 'bankAccounts',
                                                                    label: `Bank Accounts / Cash - ${formatCurrency(totalBank)} available`
                                                                });
                                                            }
                                                            
                                                            return options;
                                                        };
                                                        
                                                        const fundingOptions = buildFundingSourceOptions();
                                                        
                                                        return (
                                                            <>
                                                                <CurrencyInput
                                                                    label="Funeral Bond Amount ($)"
                                                                    name="funeralBondAmount"
                                                                    value={otherStrategies.funeralBondAmount}
                                                                    onChange={(v) => {
                                                                        setOtherStrategies(s => ({ ...s, funeralBondAmount: v ?? 0 }));
                                                                    }}
                                                                />
                                                                {/*
                                                                  * THE LIMIT WAS HARD-CODED AT $15,000 HERE, IN THREE PLACES, and the
                                                                  * published figure is $15,750 — the one the assessment itself uses.
                                                                  * So a client entering $15,500 was told the whole amount was exempt
                                                                  * while being assessed on a different number. The note below reads
                                                                  * the constant, so the screen and the assessment cannot drift again.
                                                                  */}
                                                                <FuneralBondFieldNote
                                                                    amount={Number(otherStrategies.funeralBondAmount) || 0}
                                                                    isCouple={factFind.adviceFor === 'couple'}
                                                                />

                                                                <FormSelect 
                                                                    label="Funding Source" 
                                                                    name="funeralBondSource" 
                                                                    value={otherStrategies.funeralBondSource} 
                                                                    onChange={(n, v) => setOtherStrategies(s => ({ ...s, [n]: v }))}
                                                                >
                                                                    {fundingOptions.map((opt, idx) => (
                                                                        <option key={`funeral-${opt.value}-${idx}`} value={opt.value}>{opt.label}</option>
                                                                    ))}
                                                                </FormSelect>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    
                                    {/* Lifetime Annuity Strategy - Full Width */}
                                    <FormCheckbox
                                        label={
                                            <span className="flex items-center gap-2">
                                                Lifetime Annuity
                                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                            </span>
                                        }
                                        name="annuity"
                                        checked={otherStrategies.annuity}
                                        onChange={(n, v) => {
                                            setOtherStrategies(s => ({ ...s, [n]: v }));
                                            if (v && !annuityRecord) {
                                                // Initialize annuity when checked
                                                setAnnuityRecord(initializeAnnuityRecord());
                                            } else if (!v) {
                                                // Clear annuity when unchecked
                                                setAnnuityRecord(null);
                                            }
                                        }}
                                    />
                                    
                                    {/* Owner, 23 Sep 2026: allowed, and warned — here and in the advice document. */}
                                    {otherStrategies.annuity && annuityRecord && (() => {
                                        const flag = annuityHorizonFlag({ factFind, residualCapital: annuityRecord.residualCapital, owner: annuityRecord.owner });
                                        return flag ? (
                                            <p className="mt-2 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                                                <span className="font-semibold">Worth knowing.</span> {flag.warning}
                                            </p>
                                        ) : null;
                                    })()}
                                    {/* Annuity Details - Opens below when checked */}
                                    {otherStrategies.annuity && (
                                        <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                                            <h4 className="font-semibold">Annuity Strategy Details</h4>

                                            {annuityRecord ? (() => {
                                                    const isCouple = factFind?.adviceFor === 'couple';
                                                    const clientName = factFind?.client?.firstName || 'Client';
                                                    const partnerName = factFind?.partner?.firstName || 'Partner';
                                                    
                                                    const ownershipLabel = (ownership: string | undefined) => {
                                                        if (!ownership) return '';
                                                        if (ownership === 'joint') return 'Joint';
                                                        if (ownership === 'partner') return partnerName;
                                                        return clientName;
                                                    };

                                                    const fundingSources: ExternalAnnuityFundingSource[] = [];

                                                    // Get all liquid asset types from ASSET_TYPES
                                                    const liquidAssetTypes = ASSET_TYPES.filter(assetType => assetType.isLiquid).map(assetType => assetType.id);

                                                    liquidAssetTypes.forEach(assetType => {
                                                        if (simulatedFactFind.assets && simulatedFactFind.assets[assetType]) {
                                                            simulatedFactFind.assets[assetType].forEach((asset: any) => {
                                                                const assetId = `${assetType}-${asset.id}`;
                                                                const radAllocated = radFunding[assetId] || 0;
                                                                const assetValue = parseFloat(String(asset.value || asset.balance || 0)) || 0;
                                                                /*
                                                                 * Offer back what THIS annuity has already taken from this asset.
                                                                 * The question is "what could the annuity be bought with", and the
                                                                 * money it is already using is part of the answer. Read from the
                                                                 * simulation rather than re-derived, so the two cannot disagree
                                                                 * (owner-caught, 25 Sep 2026: entering an amount made the proceeds
                                                                 * row vanish and the bank row collapse).
                                                                 */
                                                                const drawnByThisAnnuity = annuityDrawnFrom[assetId] || 0;
                                                                const available = annuityFundingAvailable(assetValue, drawnByThisAnnuity, radAllocated);
                                                                if (available > 0) {
                                                                    const ownership = asset.ownership || 'client';
                                                                    const assetName = asset.description || asset.name || ASSET_TYPES.find(at => at.id === assetType)?.name || assetType;
                                                                    // Map asset type for annuity configurator (it expects 'bank' not 'bankAccounts')
                                                                    const mappedType = assetType === 'bankAccounts' ? 'bank' : assetType;
                                                                    fundingSources.push({
                                                                        id: assetId,
                                                                        type: mappedType,
                                                                        assetId: asset.id,
                                                                        label: `${assetName} (${ownershipLabel(ownership)}) - ${formatCurrency(available)} available`,
                                                                        ownership: ownership,
                                                                        available: available
                                                                    });
                                                                }
                                                            });
                                                        }
                                                    });
                                                    
                                                    // Default to bank if no sources
                                                    if (fundingSources.length === 0) {
                                                        fundingSources.push({
                                                            id: 'bank-default',
                                                            type: 'bank',
                                                            assetId: null,
                                                            label: 'Bank accounts / cash',
                                                            ownership: isCouple ? 'joint' : 'client',
                                                            available: 0
                                                        });
                                                    }
                                                    
                                                    // Calculate annuity impact for summary display
                                                    const annuityRecordForCalculation = (() => {
                                                        try {
                                                            if (!annuityRecord || !annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) return null;
                                                            return buildAnnuityImpact(asFactFind(factFind), annuityRecord);
                                                        } catch (error) {
                                                            console.error('Error building annuity impact', error);
                                                            return null;
                                                        }
                                                    })();
                                                    
                                                    // Calculate annuity summary for display
                                                    const annuitySummary = annuityRecordForCalculation ? summariseAnnuityImpact(annuityRecordForCalculation) : null;
                                                    
                                                    // Calculate annuity warnings
                                                    const annuityWarnings = (() => {
                                                        if (!annuityRecord || !annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) return [];
                                                        const warnings: string[] = [];
                                                        // Add any specific warnings here if needed
                                                        return warnings;
                                                    })();
                                                    
                                                    return (
                                                        <>
                                                            <SupportAtHomeAnnuityConfigurator
                                                                factFind={simulatedFactFind}
                                                                // AnnuityRecordState (this file) is a superset of AnnuityDetails (extra bookkeeping
                                                                // fields: id, age, fundingSourceType/Id) — safe structural cast both ways.
                                                                annuityDetails={annuityRecord as unknown as AnnuityDetails | null}
                                                                setAnnuityDetails={setAnnuityRecord as unknown as (updater: AnnuityDetails | ((prev: AnnuityDetails) => AnnuityDetails)) => void}
                                                                annuitySummary={annuitySummary}
                                                                annuityWarnings={annuityWarnings}
                                                                fundingSources={fundingSources}
                                                                formatCurrency={formatCurrency}
                                                                clientAge={factFind?.client?.dob ? calculateAge(factFind.client?.dob) : null}
                                                                partnerAge={isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null}
                                                            />
                                                        </>
                                                    );
                                            })() : (
                                                <p className="text-sm text-gray-500">Annuity configuration not initialized.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>
                        
                        {/* Save Strategy Section */}
                        <Card>
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Save Strategy</h3>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <input
                                        type="text"
                                        placeholder="Enter strategy name (e.g., Conservative Approach)"
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        id="strategyNameInput"
                                    />
                                    <button
                                        onClick={() => {
                                            const input = document.getElementById('strategyNameInput') as HTMLInputElement | null;
                                            const strategyName = input?.value.trim() || '';
                                            if (strategyName) {
                                                saveStrategy(strategyName);
                                                if (input) input.value = '';
                                            } else {
                                                notify('Please enter a strategy name');
                                            }
                                        }}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                                    >
                                        Save Strategy
                                    </button>
                                </div>
                                
                                {/* Show saved strategies with reopen functionality */}
                                {savedStrategies.filter(s => s.type === 'aged_care').length > 0 && (
                                    <div className="space-y-3">
                                        <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                                            <p className="text-sm text-blue-700 mb-2">
                                                <strong>{savedStrategies.filter(s => s.type === 'aged_care').length}</strong> aged care strategies saved.
                                            </p>
                                            <div className="space-y-1">
                                                {savedStrategies.filter(s => s.type === 'aged_care').map(strategy => (
                                                    <div
                                                        key={strategy.id}
                                                        className={`flex items-center justify-between text-xs rounded-lg px-2 py-1.5 transition-colors ${
                                                            activeSavedStrategyId === strategy.id
                                                                ? 'bg-white ring-2 ring-blue-500 border border-blue-500 shadow-sm'
                                                                : 'text-gray-600'
                                                        }`}
                                                    >
                                                        <span className="text-gray-600">{strategy.name}</span>
                                                        <button
                                                            onClick={() => reopenStrategy(strategy)}
                                                            className="text-blue-600 hover:text-blue-800 underline font-semibold"
                                                        >
                                                            Reopen
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {savedStrategies.filter(s => s.type === 'aged_care').length > 1 && (
                                            <button
                                                onClick={() => setShowCompareModal(true)}
                                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                                            >
                                                Compare Strategies ({savedStrategies.filter(s => s.type === 'aged_care').length})
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Card>
                        </>
                    )}

                    {/* 5. Show Enhanced Suggestion Engine when Suggestion Engine is selected */}
                    {strategyMode === 'optimised' && (
                        <>
                        <Card>
                            <AgedCareSuggestionEnginePanel
                                factFind={factFind}
                                setFactFind={setFactFind}
                                agedCareCosts={suggestionEngineAgedCareCosts}
                                goals={factFind.goals}
                                // The SAME no-plan baseline this page's own panel measures against,
                                // so "Explain this" on a card reconciles to the same benchmark
                                // rather than computing a second one of its own.
                                agedCareBaseline={agedCareBaseline}
                                onDoNothingRow={setEngineDoNothingRow}
                                reopenedSavedStrategyId={reopenedEngineStrategyId}
                                selectedPlanRunwayRows={railIsWalkIn ? null : (panelRunwayRows as unknown as RunwayRow[])}
                                // AgedCareSuggestionEnginePanel.tsx's own `Strategy` type (owned by a concurrent
                                // typing pass on that file) is close to but not identical to this file's
                                // EvaluatedStrategy (e.g. no `id`/`source` named fields — those live behind its
                                // index signature); cast at this one boundary rather than chasing the two
                                // shapes into exact parity.
                                onStrategySelect={(strategy: unknown) => {
                                    const s = strategy as EvaluatedStrategy;
                                    setActiveSavedStrategyId(s.id ?? null);
                                    startTransition(() => {
                                        setSelectedStrategy(s);
                                    });
                                    if (s.source === 'suggestion_engine') {
                                        const newStrategies = [...savedStrategies, s as unknown as SavedAgedCareStrategy];
                                        setSavedStrategies(newStrategies);
                                    }
                                }}
                                selectedStrategy={selectedStrategy as any}
                                legacyYears={localLegacyYears}
                                // AgedCareSuggestionEnginePanel.tsx (not yet typed — owned by a concurrent typing
                                // pass) has no prop annotation, so TS infers this prop's shape from its own
                                // default value literal instead of accepting the real CoupleValidationState.
                                coupleValidation={coupleValidation as any}
                                onScenarioSaved={(scenario: SavedCaseStudy) => {
                                    const scenarioWithCase = {
                                        ...scenario,
                                        caseId: professionalCaseId || scenario.caseId || null,
                                        soaDeliveryStatus: scenario.soaDeliveryStatus || 'draft',
                                        isCurrentForCase: true,
                                    };
                                    // Add scenario to saved case studies
                                    const newCaseStudies = [
                                        ...savedCaseStudies.map((item) =>
                                            scenarioWithCase.caseId && item.caseId === scenarioWithCase.caseId
                                                ? { ...item, isCurrentForCase: false }
                                                : item
                                        ),
                                        scenarioWithCase
                                    ];
                                    setSavedCaseStudies(newCaseStudies);
                                    // Save to both keys for consistency (AgedCareSuggestionEnginePanel uses careCompanionScenarios)
                                    sessionStorage.setItem('careCompanionCaseStudies', JSON.stringify(newCaseStudies));
                                    sessionStorage.setItem('careCompanionScenarios', JSON.stringify(newCaseStudies));
                                    onSoaGenerated?.(scenarioWithCase);
                                }}
                                onNavigateToDashboard={() => {
                                    setActivePage('Dashboard');
                                }}
                                // The facts of the case every candidate is priced on — the entry
                                // date, the deposit-drawdown choice, the fee arrangements and the
                                // second room. Memoised: the panel re-runs its analysis on a click,
                                // not on every render of this page.
                                strategyOptions={suggestionEngineStrategyOptions}
                            />
                        </Card>
                        </>
                    )}
                    </>)}
                </div>
                {/* Right Side: the plan summary — costs, impact and narrative in ONE panel. Step 3 only:
                    on step 2 the page is the baseline, and a rail restating it was a second column
                    saying the same thing. */}
                {step === 3 && (
                <div className="xl:col-span-2 space-y-3 xl:sticky xl:top-8">
                <div className="space-y-6 transition-all duration-300">
                    {/*
                      * ONE panel, not two cards plus a narrative further down the page. See
                      * PlanSummaryPanel.tsx for why. The content is unchanged — the Cost Overview
                      * block and FinancialImpactPanel are passed in exactly as they were rendered
                      * before, so no figure is derived anywhere new.
                      */}
                    <PlanSummaryPanel
                        strategyName={planSummaryStrategyName}
                        baselineLabel={agedCareBaseline.financials ? 'no plan' : undefined}
                        // Engine mode: say what the rail is and where the plan lives.
                        noStrategyHeading={railIsWalkIn ? 'Doing nothing — the walk-in position' : undefined}
                        noStrategyNote={railIsWalkIn ? (
                            <><span className="font-semibold text-slate-700">For comparison</span> while you choose. Each strategy card carries its own figures —{' '}
                            <span className="font-semibold text-slate-700">Select</span> one, then{' '}
                            <span className="font-semibold text-slate-700">Explain this</span> and{' '}
                            <span className="font-semibold text-slate-700">Deep Dive</span> on the card.</>
                        ) : undefined}
                        inBrief={planInBrief}
                        onReadFullExplanation={planSummaryStrategyName ? () => setShowPlanExplanation(true) : null}
                        onViewProjections={() => { setProjectionsSource(railIsWalkIn ? 'walkIn' : 'plan'); setShowProjections(true); }}
                        liquidity={runway && (
                            <>
                                <p className="text-lg font-bold leading-tight tracking-tight text-slate-900">
                                    {runway.headline}
                                </p>
                                <div className="mt-2">
                                    <FundingRunway
                                        coveredPct={runway.coveredPct}
                                        leftLabel={runway.leftLabel}
                                        rightLabel={runway.rightLabel}
                                        depleted={runway.depleted}
                                        flag={railRunwayFlag ?? runway.flag}
                                    />
                                </div>
                                <HorizonNote runway={runway} className="mt-1.5" />
                            </>
                        )}
                        costOverview={
                            <CostOverviewDisclosure dailyTotal={panelCosts.totalDailyCost || 0} note={panelPhaseTwo ? railPhaseTwoLine(phaseNames, panelPhaseTwoDaily) : undefined}>
                                {/* panelCosts: the walk-in position in engine mode, the modelled plan in
                                    Manual Control — one source per mode, see railIsWalkIn. With both in
                                    care, each room's rows under its own name, and the total above is both. */}
                                {panelPhaseTwo ? (
                                    <>
                                        <p className="pt-1 text-[11px] font-bold uppercase tracking-wide text-gray-300">Until {phaseNames.fromLabel} — {firstName}&apos;s room, {formatCurrency(panelFirstCosts.totalDailyCost || 0)} a day</p>
                                        {feeRows(panelFirstCosts)}
                                        <p className="pt-3 text-[11px] font-bold uppercase tracking-wide text-gray-300">From {phaseNames.fromLabel} — {firstName}&apos;s room, {formatCurrency(panelPhaseTwo.firstCosts.totalDailyCost || 0)} a day</p>
                                        {feeRows(panelPhaseTwo.firstCosts)}
                                        {panelPhaseTwo.secondCosts && (
                                            <>
                                                <p className="pt-3 text-[11px] font-bold uppercase tracking-wide text-gray-300">From {phaseNames.fromLabel} — {secondName}&apos;s room, {formatCurrency(panelPhaseTwo.secondCosts.totalDailyCost || 0)} a day</p>
                                                {feeRows(panelPhaseTwo.secondCosts)}
                                            </>
                                        )}
                                    </>
                                ) : panelSecondCosts ? (
                                    <>
                                        <p className="pt-1 text-[11px] font-bold uppercase tracking-wide text-gray-300">{firstName}&apos;s room — {formatCurrency(panelFirstCosts.totalDailyCost || 0)} a day</p>
                                        {feeRows(panelFirstCosts)}
                                        <p className="pt-3 text-[11px] font-bold uppercase tracking-wide text-gray-300">{secondName}&apos;s room — {formatCurrency(panelSecondCosts.totalDailyCost || 0)} a day</p>
                                        {feeRows(panelSecondCosts)}
                                    </>
                                ) : feeRows(panelFirstCosts)}
                            </CostOverviewDisclosure>
                        }
                        financialImpact={
                        <FinancialImpactPanel
                            embedded
                            baselineOnly={railIsWalkIn}
                            // Measured against ENTERING CARE WITH NO PLAN, not against life at home:
                            // the care is happening either way, so the panel prices the advice rather
                            // than the diagnosis. Falls back to today's position only if the baseline
                            // could not be computed — better a stated comparison than a wrong one.
                            before={agedCareBaseline.financials ?? initialFinancials}
                            // Net of the BASELINE's own care fees. Without this the tile compared a
                            // figure gross of the DAP with one net of it — a $97,649 gap on one real
                            // plan, and it disagreed with the summary directly below.
                            cashFlowBeforeValue={agedCareBaseline.cashFlowAfterCare}
                            baselineLabel={agedCareBaseline.financials ? 'no plan' : undefined}
                            // The SELECTED plan's own figures — the engine strategy's when one is
                            // chosen, otherwise Manual Control's. Never a mix: the tiles, the cost
                            // block and the heading all read from the same source.
                            after={panelFinancials}
                            legacyBaselineValue={agedCareBaseline.legacyValue}
                            agedCareCosts={panelCosts}
                            legacyYears={localLegacyYears}
                            setLegacyYears={setLocalLegacyYears}
                            radPaid={radPaid}
                            onViewProjections={() => { setProjectionsSource(railIsWalkIn ? 'walkIn' : 'plan'); setShowProjections(true); }}
                            projections={panelProjections}
                        />
                        }
                    />

                    {isProfessionalJourney && (
                        <Card>
                            <button
                                onClick={openManualControlDigitalSOA}
                                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all transform hover:scale-[1.01]"
                            >
                                Generate Digital SOA
                            </button>
                            <p className="text-xs text-gray-500 mt-2">
                                For adviser-led journeys, this SOA records the selected manual strategy and alternative saved modelling scenarios.
                            </p>
                        </Card>
                    )}
                </div>
                </div>
                )}
            </div>
            
            {/* Couple Validation Modal */}
                                        <CoupleValidationModal
                                isOpen={showValidationModal}
                                onClose={() => setShowValidationModal(false)}
                                coupleValidation={coupleValidation}
                                setCoupleValidation={setCoupleValidation}
                                factFind={factFind}
                                setFactFind={setFactFind}
                                scenarioLivingExpenses={scenarioLivingExpenses}
                                defaultPartnerRoomPrice={parseFloat(String(radInput)) || 0}
                                onValidationComplete={() => {
                                    setStrategyModeChosen(true); setStrategyMode('optimised'); 
                                    setShowStrategyOptions(false); 
                                }}
                            />
            
            {/* Strategy Comparison Modal */}
            {showCompareModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80">
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-br from-slate-50/90 via-white to-teal-50/40">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                        <GradientText>Strategy Comparison</GradientText>
                                    </h2>
                                    <p className="text-sm text-slate-600 mt-1.5">Side-by-side view of saved aged care strategies (model outputs).</p>
                                    {/* Ranked side by side, these read as alternatives to the SAME question. They
                                        need not be: each was saved against whatever room price, entry date and
                                        projection length were set at the time. Said plainly rather than left for
                                        the reader to infer from figures that look simply better or worse. */}
                                    {comparedBasisDifferences.length > 0 && (
                                        <p role="status" className="mt-3 border-l-4 border-amber-400 pl-3 py-1 text-sm text-slate-700">
                                            <strong className="font-semibold">Not a like-for-like comparison.</strong>{' '}
                                            These strategies were modelled on a different {comparedBasisDifferences.join(', ')}, so the figures below are not directly comparable. Check the basis columns before drawing a conclusion.
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowCompareModal(false)}
                                    className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
                        </div>

                        <div className="overflow-auto flex-1 px-6 py-5">
                            <div className="rounded-xl border border-slate-200 overflow-x-auto shadow-sm">
                                <table className="w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 text-slate-800">
                                            <th className="px-3 py-2.5 text-left font-semibold border-b border-slate-200/90">Strategy name</th>
                                            <th className="px-3 py-2.5 text-right font-semibold border-b border-slate-200/90">Room price</th>
                                            <th className="px-3 py-2.5 text-center font-semibold border-b border-slate-200/90">Care starts</th>
                                            <th className="px-3 py-2.5 text-center font-semibold border-b border-slate-200/90">Projected over</th>
                                            <th className="px-3 py-2.5 text-right font-semibold border-b border-slate-200/90">Daily care cost</th>
                                            <th className="px-3 py-2.5 text-right font-semibold border-b border-slate-200/90">Age pension</th>
                                            <th className="px-3 py-2.5 text-right font-semibold border-b border-slate-200/90">Cash flow</th>
                                            <th className="px-3 py-2.5 text-right font-semibold border-b border-slate-200/90">Legacy estimate</th>
                                            <th className="px-3 py-2.5 text-center font-semibold border-b border-slate-200/90">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/*
                                          * THE BASELINE ROW, always first (owner, 2026-08-31).
                                          *
                                          * A comparison of strategies against only each other has no
                                          * floor: the least-bad option still reads as a winner, and
                                          * nothing on the table says whether ANY of them beats doing
                                          * nothing. It is the same gap the Downsizer engine was caught
                                          * with (2026-08-26), and the fix is the same — show the
                                          * do-nothing position beside them.
                                          *
                                          * Visually distinct and labelled, so it is never mistaken for
                                          * a strategy someone saved.
                                          */}
                                        {agedCareBaseline.pension != null && (
                                            <tr className="border-b border-slate-200 bg-slate-100/70">
                                                <td className="px-3 py-2.5 font-semibold text-slate-800">
                                                    No plan — the walk-in position
                                                    <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                                                        Baseline
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{formatCurrency(Number(radInput) || 0)}</td>
                                                <td className="px-3 py-2.5 text-center text-slate-500">—</td>
                                                <td className="px-3 py-2.5 text-center text-slate-700 whitespace-nowrap">{`${Number(localLegacyYears) || 0} years`}</td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{formatCurrency(householdDailyCost((agedCareBaseline.agedCareCosts as AgedCareCosts | null | undefined) ?? agedCareCosts, baselineSecondCosts))}</td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{formatCurrency(agedCareBaseline.pension)}</td>
                                                <td className={`px-4 py-3 text-right tabular-nums font-medium ${(agedCareBaseline.cashFlowAfterCare ?? 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                                    {formatCurrency(agedCareBaseline.cashFlowAfterCare ?? 0)}
                                                </td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{formatCurrency(agedCareBaseline.legacyValue ?? 0)}</td>
                                                <td className="px-3 py-2.5 text-center text-slate-500">—</td>
                                            </tr>
                                        )}
                                        {comparedAgedCareStrategies.map((strategy, index) => (
                                            <tr
                                                key={strategy.id}
                                                className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                                            >
                                                <td className="px-3 py-2.5 font-medium text-slate-900">{strategy.name}</td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{formatCurrency(describeStrategyBasis(strategy).roomPrice)}</td>
                                                <td className="px-3 py-2.5 text-center text-slate-700 whitespace-nowrap">
                                                    {describeStrategyBasis(strategy).careStartDate
                                                        ? new Date(describeStrategyBasis(strategy).careStartDate).toLocaleDateString('en-AU')
                                                        : 'Now'}
                                                </td>
                                                <td className="px-3 py-2.5 text-center text-slate-700 whitespace-nowrap">
                                                    {describeStrategyBasis(strategy).legacyYears
                                                        ? `${describeStrategyBasis(strategy).legacyYears} years`
                                                        : <span className="text-slate-400 italic">not recorded</span>}
                                                </td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{formatCurrency((strategy.outputs?.agedCareCost || 0) / 365)}</td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{formatCurrency(strategy.outputs?.agePension)}</td>
                                                <td className={`px-4 py-3 text-right tabular-nums font-medium ${getDisplayCashFlowForSavedStrategy(strategy) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                                    {formatCurrency(getDisplayCashFlowForSavedStrategy(strategy))}
                                                </td>
                                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{formatCurrency(strategy.outputs?.legacyEstimate)}</td>
                                                <td className="px-3 py-2.5 text-center text-slate-600">
                                                    {strategy.createdAt ? new Date(strategy.createdAt).toLocaleDateString() : ''}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowCompareModal(false)}
                                className="px-5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 shadow-sm transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgedCareAnalyser;
